/**
 * @fileoverview HTML Document Export Utilities
 *
 * Word: Builds a proper .docx ZIP (via JSZip) that embeds the body HTML
 *       as an altChunk. Word converts the HTML to native formatting on open,
 *       preserving all the CSS styling from the TipTap templates.
 *       Headers/footers are separate OOXML parts, so they repeat on every
 *       page with zero body pollution and no blank-page artifacts.
 * PDF:  Opens a print-ready window; user picks "Save as PDF".
 */

import {
  resolveVariantFragments,
  resolveDocumentPadding,
  resolvePagePadding,
  variantPaddingTiers,
  pickVariantForPage,
  clampTopBlockHeightPct,
  normalizeSideCrop,
  sideCropGeometry,
  sideCropIsActive,
  EMPTY_SIDE_CROP,
  DEFAULT_PADDING,
  type PageRule,
  type DocumentPadding,
  type PaddingSource,
  type PageOverrideSource,
  type PagePaddingInputs,
  type SideCrop,
  type SideFit,
  type VariantFragments,
} from "@/utils/branding/resolve-letterhead";
import {
  buildPageNumberHtml,
  buildWordPageNumberParagraphXml,
  shouldShowPageNumber,
  PAGE_NUMBER_BAND_PX,
  PAGE_NUMBER_BAND_TWIP,
  type PageNumberingConfig,
} from "@/utils/branding/page-numbering";
import {
  applyPageFitToPadding,
  fitZoneHtml,
  footerSlotInsets,
  pageNumberBottomPx,
  zonePageFit,
  PAGE_FIT_HEIGHT_PX,
  PAGE_FIT_WIDTH_PX,
} from "@/utils/branding/pageFit";
import JSZip from "jszip";

export const A4_PAGE_WIDTH_PX = PAGE_FIT_WIDTH_PX;
export const A4_PAGE_HEIGHT_PX = PAGE_FIT_HEIGHT_PX;
const WORD_PAGE_WIDTH_PT = 595.3;
const WORD_HORIZONTAL_MARGIN_PT = 56;
const WORD_PRINTABLE_WIDTH_PT = Number(
  (WORD_PAGE_WIDTH_PT - WORD_HORIZONTAL_MARGIN_PT * 2).toFixed(1),
);
const WORD_PRINTABLE_WIDTH_PX = Math.round((WORD_PRINTABLE_WIDTH_PT * 96) / 72);

const FALLBACK_FONT_FAMILY = "Arial, sans-serif";

/**
 * Google Fonts markup mirroring the `<link>` in `index.html`.
 *
 * The PDF export paths (`downloadAsPdf`, `letterPartsToPdfBlob`) render into a
 * *fresh* document (a print window or an off-screen iframe) that does NOT
 * inherit the main page's font links. Without re-linking the branding fonts
 * here, `document.fonts.ready` resolves with e.g. Open Sans never loaded, so
 * the render silently falls back to Arial — and the html2canvas raster path
 * additionally bakes that wrong, heavier font into the image (MOR-623: wrong
 * font + "too blocky" bold). Inject this into the export `<head>` so
 * `waitForPrintWindowReady` actually waits for the real fonts before
 * measuring/rasterising.
 */
const EXPORT_FONT_LINKS = `<link rel="preconnect" href="https://fonts.googleapis.com" /><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin /><link href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;600&family=Inter:wght@400;500;600;700&family=Lora:wght@400;500;600;700&family=Open+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=Roboto:ital,wght@0,400;0,500;0,700;1,400;1,700&display=swap" rel="stylesheet" />`;

/**
 * Resolve the font-family CSS value used as the document body default.
 *
 * Falls back to Arial when the org has not configured a branding font,
 * matching the historical behaviour of the export pipeline.
 */
function resolveFontFamily(font?: string | null): string {
  return font && font.trim() ? font : FALLBACK_FONT_FAMILY;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export interface LetterParts {
  bodyHtml: string;
  headerHtml?: string;
  footerHtml?: string;
  headerImageUrl?: string;
  footerImageUrl?: string;
  /**
   * Optional full-height side letterhead images (NEW v5).
   * When present, the side band reserves its own intrinsic image width
   * along the left or right edge of every page that matches the rule.
   */
  leftSideImageUrl?: string;
  rightSideImageUrl?: string;
  headerPageRule?: PageRule;
  footerPageRule?: PageRule;
  leftSidePageRule?: PageRule;
  rightSidePageRule?: PageRule;
  /**
   * Side fit — "fullHeight" (default) stretches the side image over the
   * whole page and uniformly narrows the body; "topBlock" anchors the image
   * to the top corner at its natural aspect ratio (first page only) and
   * lets body text wrap around it.
   */
  leftSideFit?: SideFit;
  rightSideFit?: SideFit;
  /** Fraction of the page height a "topBlock" side spans (default 1/3). */
  leftSideTopBlockHeightPct?: number;
  rightSideTopBlockHeightPct?: number;
  /**
   * Source-image crop for a "topBlock" side — isolates the relevant
   * section of the artwork (e.g. the top logo/address block of a
   * full-height strip). Fractions of the source image per edge.
   */
  leftSideCrop?: Partial<SideCrop> | null;
  rightSideCrop?: Partial<SideCrop> | null;
  /**
   * Footer page numbering ("Page 2 of 5"), resolved from the org's branding
   * profile via `resolvePageNumbering`. Rendered as its own line at the
   * bottom of the page — below the footer letterhead when there is one —
   * because the letterhead itself is a static fragment repeated on every
   * matching page and so can't carry a per-page value.
   *
   * Omit (or pass `null`) for no numbering.
   */
  pageNumbering?: PageNumberingConfig | null;
  /** Branding-level padding source (org defaults) */
  brandingPadding?: PaddingSource | null;
  /** Per-template padding overrides */
  templatePadding?: PaddingSource | null;
  /** Per-document padding overrides (highest priority) */
  documentPadding?: PaddingSource | null;
  /**
   * Per-page margin overrides (1-based page numbers) from the precedent
   * template and the job document. Resolved by `resolvePagePadding` ahead of
   * the variant's per-layout margins and the uniform tiers above.
   */
  templatePageOverrides?: PageOverrideSource[] | null;
  documentPageOverrides?: PageOverrideSource[] | null;
  /**
   * Ordered letterhead variants (highest priority first). When set, this
   * is the source of truth for header/footer/sides — the flat
   * headerImageUrl/footerImageUrl/etc. fields above are ignored, as are the
   * per-zone page rules. Use this for multi-variant documents where
   * different variants apply on different pages.
   *
   * Per-variant padding (if any) is consulted between templatePadding and
   * brandingPadding via the variant tier of resolveDocumentPadding().
   */
  variants?: VariantFragments[];
  /**
   * Default font-family applied to the document body.
   *
   * Sourced from the org's branding profile (`editorFontFamily`) so the
   * exported Word/PDF/preview output matches the TipTap editor. When
   * unset, exports fall back to Arial (see `resolveFontFamily`).
   */
  defaultFontFamily?: string | null;
  /**
   * Default CSS `line-height` applied to the document body. Sourced from
   * `brandingProfiles.editorLineHeight`. Per-paragraph overrides serialised
   * by the LineHeight TipTap extension still win at render time.
   */
  defaultLineHeight?: string | null;
  /**
   * Default body text colour (any CSS colour). Sourced from
   * `brandingProfiles.bodyTextColor`. Defaults to `"#000"` when unset.
   */
  bodyTextColor?: string | null;
  /**
   * Default body font-size (CSS value, e.g. `"11pt"`). Sourced from
   * `brandingProfiles.editorFontSize`. Defaults to `"11pt"` when unset.
   */
  defaultFontSize?: string | null;
  /**
   * Default `margin-top` for body paragraphs and headings (CSS value,
   * e.g. `"6pt"`). Sourced from `brandingProfiles.editorParagraphSpacingBefore`.
   * Per-paragraph overrides serialised by the ParagraphSpacing extension
   * still win at render time.
   */
  defaultParagraphSpacingBefore?: string | null;
  /**
   * Default `margin-bottom` for body paragraphs and headings (CSS value,
   * e.g. `"12pt"`). Sourced from `brandingProfiles.editorParagraphSpacingAfter`.
   * Per-paragraph overrides serialised by the ParagraphSpacing extension
   * still win at render time.
   */
  defaultParagraphSpacingAfter?: string | null;
}

const FALLBACK_LINE_HEIGHT = "1.6";
const FALLBACK_BODY_TEXT_COLOR = "#000";
const FALLBACK_FONT_SIZE = "11pt";

/** Resolve the `line-height` CSS value for the export pipeline. */
function resolveLineHeight(value?: string | null): string {
  return value && value.trim() ? value.trim() : FALLBACK_LINE_HEIGHT;
}

/** Resolve the body `font-size` CSS value for the export pipeline. */
function resolveFontSize(value?: string | null): string {
  return value && value.trim() ? value.trim() : FALLBACK_FONT_SIZE;
}

/**
 * Resolve a paragraph spacing CSS value (before/after). Returns an empty
 * string when unset so callers can omit the rule entirely instead of
 * forcing a `0`.
 */
function resolveParagraphSpacing(value?: string | null): string {
  return value && value.trim() ? value.trim() : "";
}

/**
 * Extract the numeric `pt` component from a CSS `font-size` value (e.g.
 * `"11pt"` → `11`, `"12.5pt"` → `12.5`). Falls back to `BODY_FONT_SIZE_PT`
 * for any unitless / non-pt value so unitless line-height multipliers stay
 * reasonable.
 */
function fontSizePt(value?: string | null): number {
  const resolved = resolveFontSize(value);
  const match = resolved.match(/^([0-9]*\.?[0-9]+)pt$/i);
  if (match) {
    const numeric = parseFloat(match[1]);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return BODY_FONT_SIZE_PT;
}

/**
 * Body font-size used throughout the export pipeline (Word + PDF + Preview).
 * Multiplied by unitless line-height multipliers when emitting absolute `pt`
 * values for Word's altChunk import.
 */
const BODY_FONT_SIZE_PT = 11;

/**
 * Convert an arbitrary CSS line-height value to absolute `pt` form when it's
 * unitless (e.g. `"1.6"` → `"17.6pt"`).
 *
 * Paired with `mso-line-height-rule:at-least` (see `normalizeLineHeightForWord`
 * / `wrapBodyHtml`), `pt` values map to OOXML `<w:spacing w:lineRule="atLeast"/>`,
 * which renders loose values literally and clamps tight values to the
 * font's natural single-line height instead of clipping glyph descenders.
 * See `normalizeLineHeightForWord` for the full rationale and the tradeoff.
 *
 * Values that already carry a unit (`%`, `px`, `pt`, `em`, `rem`) are
 * passed through unchanged.
 */
function lineHeightToPt(
  value: string,
  fontSizePtValue: number = BODY_FONT_SIZE_PT,
): string {
  const trimmed = value.trim();
  // Already has a unit — leave untouched.
  if (/[a-z%]$/i.test(trimmed)) return trimmed;
  const numeric = parseFloat(trimmed);
  if (!Number.isFinite(numeric) || numeric <= 0) return trimmed;
  const pt = numeric * fontSizePtValue;
  return `${formatPt(pt)}pt`;
}

/** Format a pt number with up to 2 decimals, trimming trailing zeros. */
function formatPt(value: number): string {
  return value.toFixed(2).replace(/\.?0+$/, "");
}

/** Resolve the body text colour CSS value for the export pipeline. */
function resolveBodyTextColor(value?: string | null): string {
  return value && value.trim() ? value.trim() : FALLBACK_BODY_TEXT_COLOR;
}

interface WordExportDebugInfo {
  filename?: string;
  paddingSources?: {
    branding?: PaddingSource | null;
    template?: PaddingSource | null;
    document?: PaddingSource | null;
  };
  headerAsset: WordExportAssetDebugInfo;
  footerAsset: WordExportAssetDebugInfo;
  bodyWrapperStrategy: "none" | "table-spacer-columns" | "page-margins";
  resolvedPadding: DocumentPadding;
  baseSidePaddingPx: number;
  bodyExtraSidePaddingPx: number;
  headerWidthIn: number;
  footerWidthIn: number;
  headerHeightTwip: number;
  footerHeightTwip: number;
  wordMargins: {
    top: number;
    bottom: number;
    side: number;
    header: number;
    footer: number;
  };
}

interface WordExportAssetDebugInfo {
  requestedUrl?: string;
  fetched: boolean;
  width?: number;
  height?: number;
  error?: string | null;
}

declare global {
  interface Window {
    __lastWordExportLayout?: WordExportDebugInfo;
  }
}

/**
 * Check whether a header/footer zone should render on a given page.
 *
 * @param rule      The page application rule for this zone
 * @param pageIndex 1-based page index (1 = first page)
 * @param totalPages Total number of pages (for bulk exports)
 */
export function shouldShowZone(
  rule: PageRule,
  pageIndex: number,
  _totalPages = 1,
): boolean {
  switch (rule) {
    case "all":
      return true;
    case "firstOnly":
      return pageIndex === 1;
    case "notFirst":
      return pageIndex !== 1;
    case "oddOnly":
      return pageIndex % 2 === 1;
    case "evenOnly":
      return pageIndex % 2 === 0;
    case "none":
      return false;
    default:
      return true;
  }
}

/**
 * Resolve the ordered variants used by a LetterParts. When the caller has
 * provided an explicit `variants` array, that wins; otherwise a single
 * synthetic variant is built from the legacy flat fields so existing
 * callers keep working unchanged.
 */
export function getVariantsForParts(parts: LetterParts): VariantFragments[] {
  if (parts.variants && parts.variants.length > 0) return parts.variants;
  const synthetic = resolveVariantFragments({
    headerImageUrl: parts.headerImageUrl,
    footerImageUrl: parts.footerImageUrl,
    leftSideImageUrl: parts.leftSideImageUrl,
    rightSideImageUrl: parts.rightSideImageUrl,
    headerHtml: parts.headerHtml,
    footerHtml: parts.footerHtml,
    headerPageRule: parts.headerPageRule,
    footerPageRule: parts.footerPageRule,
    leftSidePageRule: parts.leftSidePageRule,
    rightSidePageRule: parts.rightSidePageRule,
    leftSideFit: parts.leftSideFit,
    rightSideFit: parts.rightSideFit,
    leftSideTopBlockHeightPct: parts.leftSideTopBlockHeightPct,
    rightSideTopBlockHeightPct: parts.rightSideTopBlockHeightPct,
    leftSideCrop: parts.leftSideCrop,
    rightSideCrop: parts.rightSideCrop,
  });
  return [synthetic];
}

/**
 * Ordered list of per-variant padding sources (highest priority first),
 * ending with the body column the given page's letterhead measured from its
 * source — see `variantPaddingTiers`. Whole-document callers (measurement,
 * pagination) resolve at page 1; per-page renderers pass their own index so
 * a `notFirst` letterhead's body column applies on its pages.
 *
 * @param pageIndex 1-based page index (default 1)
 */
export function variantPaddingSources(
  variants: VariantFragments[],
  pageIndex = 1,
): Array<PaddingSource | null | undefined> {
  return variantPaddingTiers(variants, pageIndex);
}

/** Everything `resolvePagePadding` needs, lifted off a LetterParts. */
export function pagePaddingInputs(parts: LetterParts): PagePaddingInputs {
  return {
    brandingPadding: parts.brandingPadding,
    templatePadding: parts.templatePadding,
    documentPadding: parts.documentPadding,
    variants: getVariantsForParts(parts),
    templatePageOverrides: parts.templatePageOverrides ?? null,
    documentPageOverrides: parts.documentPageOverrides ?? null,
  };
}

export function hasPageOverrides(parts: LetterParts): boolean {
  return (
    (parts.templatePageOverrides?.length ?? 0) > 0 ||
    (parts.documentPageOverrides?.length ?? 0) > 0
  );
}

/** Does any variant carry per-layout margins? */
export function hasLayoutMargins(parts: LetterParts): boolean {
  return getVariantsForParts(parts).some(
    (v) => (v.layoutMargins?.length ?? 0) > 0,
  );
}

function sideReservedOnPage(
  variants: VariantFragments[],
  zone: "leftSide" | "rightSide",
  pageIndex: number,
  widthPx: number,
): number {
  const v = pickVariantForPage(variants, zone, pageIndex);
  if (!v) return 0;
  const fit = zone === "leftSide" ? v.leftSideFit : v.rightSideFit;
  return fit === "topBlock" ? 0 : Math.max(0, widthPx);
}

export interface BodyGeometry {
  columnWidthPx: number;
  bodyLeftPaddingPx: number;
  bodyRightPaddingPx: number;
}

export function bodyGeometriesEqual(a: BodyGeometry, b: BodyGeometry): boolean {
  return (
    a.columnWidthPx === b.columnWidthPx &&
    a.bodyLeftPaddingPx === b.bodyLeftPaddingPx &&
    a.bodyRightPaddingPx === b.bodyRightPaddingPx
  );
}

/**
 * The body column geometry and resolved margins for one page. The column is
 * A4 minus the full-height side bands that show on that page; the body's
 * own side padding sits inside the column.
 */
export function bodyGeometryForPage(
  parts: LetterParts,
  measured: Pick<PaginationMeasured, "leftWidth" | "rightWidth">,
  pageIndex: number,
): BodyGeometry & { padding: DocumentPadding } {
  const inputs = pagePaddingInputs(parts);
  const padding = resolvePagePadding(inputs, pageIndex);
  const left = sideReservedOnPage(
    inputs.variants,
    "leftSide",
    pageIndex,
    measured.leftWidth ?? 0,
  );
  const right = sideReservedOnPage(
    inputs.variants,
    "rightSide",
    pageIndex,
    measured.rightWidth ?? 0,
  );
  return {
    columnWidthPx: Math.max(1, A4_PAGE_WIDTH_PX - left - right),
    bodyLeftPaddingPx: padding.bodyLeft,
    bodyRightPaddingPx: padding.bodyRight,
    padding,
  };
}

/**
 * Distinct body geometries the document's pages need, in first-seen page
 * order. Page rules only distinguish first / other / even pages, so probing
 * pages 1-3 plus any explicitly overridden page covers every case.
 */
export function distinctBodyGeometries(
  parts: LetterParts,
  measured: Pick<PaginationMeasured, "leftWidth" | "rightWidth">,
): BodyGeometry[] {
  const probe = new Set<number>([1, 2, 3]);
  for (const e of parts.templatePageOverrides ?? []) probe.add(e.page);
  for (const e of parts.documentPageOverrides ?? []) probe.add(e.page);
  const out: BodyGeometry[] = [];
  for (const page of [...probe].sort((a, b) => a - b)) {
    const g = bodyGeometryForPage(parts, measured, page);
    const geometry = {
      columnWidthPx: g.columnWidthPx,
      bodyLeftPaddingPx: g.bodyLeftPaddingPx,
      bodyRightPaddingPx: g.bodyRightPaddingPx,
    };
    if (!out.some((o) => bodyGeometriesEqual(o, geometry))) out.push(geometry);
  }
  return out;
}

/**
 * Build an HTML string for browser preview (day-one letters).
 *
 * @param opts     Letter parts including body, header/footer, and page rules
 * Deliberately renders no page number: this is a single-page approximation
 * used only when a document has no body to paginate, so there is no page
 * count to number against. Every real surface (on-screen preview, PDF, Word)
 * goes through the paginated path.
 *
 * @param pageIndex 1-based page index for page-rule evaluation (default 1)
 */
export function buildLetterHtml(
  opts: LetterParts & { title?: string },
  pageIndex = 1,
): string {
  const variants = getVariantsForParts(opts);
  const p = resolveDocumentPadding(
    opts.brandingPadding,
    opts.templatePadding,
    opts.documentPadding,
    variantPaddingSources(variants, pageIndex),
  );

  const headerVariant = pickVariantForPage(variants, "header", pageIndex);
  const footerVariant = pickVariantForPage(variants, "footer", pageIndex);
  const leftVariant = pickVariantForPage(variants, "leftSide", pageIndex);
  const rightVariant = pickVariantForPage(variants, "rightSide", pageIndex);

  const fragments = {
    headerHtml: headerVariant?.headerHtml,
    footerHtml: footerVariant?.footerHtml,
    leftSideHtml: leftVariant?.leftSideHtml,
    rightSideHtml: rightVariant?.rightSideHtml,
  };
  const showHeader = !!fragments.headerHtml;
  const showFooter = !!fragments.footerHtml;
  // Slot padding honours page fit.
  const pageFit = zonePageFit(headerVariant, footerVariant);
  const slotP = applyPageFitToPadding(p, pageFit);

  // topBlock sides don't join the grid's side columns — they float inside
  // the body so text wraps around them (approximation for this single-page
  // preview: the block sits at the top of the BODY rather than the page,
  // which matches the common no-top-header configuration; the paginated
  // preview/PDF path renders the true page-anchored geometry).
  const leftIsTopBlock =
    !!fragments.leftSideHtml && leftVariant?.leftSideFit === "topBlock";
  const rightIsTopBlock =
    !!fragments.rightSideHtml && rightVariant?.rightSideFit === "topBlock";
  const showLeftSide = !!fragments.leftSideHtml && !leftIsTopBlock;
  const showRightSide = !!fragments.rightSideHtml && !rightIsTopBlock;

  const topBlockFloat = (
    html: string,
    side: "left" | "right",
    pct: number | undefined,
    crop: SideCrop | undefined,
  ): string => {
    const heightPx = Math.round(
      clampTopBlockHeightPct(pct) * A4_PAGE_HEIGHT_PX,
    );
    const maxWidthPx = Math.round(
      A4_PAGE_WIDTH_PX * TOP_BLOCK_MAX_WIDTH_FRACTION,
    );
    const gutterSide = side === "right" ? "left" : "right";
    const marginCss = `margin-${gutterSide}:${TOP_BLOCK_WRAP_GUTTER_X_PX}px;margin-bottom:${TOP_BLOCK_WRAP_GUTTER_Y_PX}px`;
    const c = normalizeSideCrop(crop);
    if (!sideCropIsActive(c)) {
      return html.replace(
        /style="([^"]*)"/i,
        `style="float:${side};height:${heightPx}px;width:auto;max-width:${maxWidthPx}px;${marginCss}"`,
      );
    }
    // Cropped: shrink-to-fit float wrapper clips the image; the image is
    // oversized so the visible region fills the wrapper height, shifted by
    // translate (percentages of the image's OWN size, so no aspect needed).
    const geo = sideCropGeometry(c);
    const img = html.replace(
      /style="([^"]*)"/i,
      `style="display:block;height:${geo.imgHeightPct.toFixed(3)}%;width:auto;transform:translate(-${(c.left * 100).toFixed(3)}%,-${(c.top * 100).toFixed(3)}%)"`,
    );
    return `<div style="float:${side};height:${heightPx}px;max-width:${maxWidthPx}px;overflow:hidden;${marginCss}">${img}</div>`;
  };
  const bodyFloatPrefix =
    pageIndex === 1
      ? (leftIsTopBlock && fragments.leftSideHtml
          ? topBlockFloat(
              fragments.leftSideHtml,
              "left",
              leftVariant?.leftSideTopBlockHeightPct,
              leftVariant?.leftSideCrop,
            )
          : "") +
        (rightIsTopBlock && fragments.rightSideHtml
          ? topBlockFloat(
              fragments.rightSideHtml,
              "right",
              rightVariant?.rightSideTopBlockHeightPct,
              rightVariant?.rightSideCrop,
            )
          : "")
      : "";

  const fontFamily = resolveFontFamily(opts.defaultFontFamily);
  const lineHeight = resolveLineHeight(opts.defaultLineHeight);
  const color = resolveBodyTextColor(opts.bodyTextColor);
  const fontSize = resolveFontSize(opts.defaultFontSize);
  const bodyContentHtml = normalizeTableWidthsForOutput(opts.bodyHtml);

  // Fast path: no sides → preserve the historical flex column layout for
  // backwards-compatible output.
  if (!showLeftSide && !showRightSide) {
    const header = showHeader
      ? `<div style="padding:${slotP.headerTop}px ${slotP.headerRight}px 0 ${slotP.headerLeft}px;overflow:hidden">${fitZoneHtml(fragments.headerHtml ?? "", pageFit.headerTop)}</div>`
      : "";
    const footer = showFooter
      ? `<div style="padding:0 ${slotP.footerRight}px ${slotP.footerBottom}px ${slotP.footerLeft}px;overflow:hidden">${fitZoneHtml(fragments.footerHtml ?? "", pageFit.footerBottom)}</div>`
      : "";
    const body = `<div class="letter-body prose-letter" style="flex:1 1 auto;min-height:0;padding:${p.bodyTop}px ${p.bodyRight}px ${p.bodyTop}px ${p.bodyLeft}px">${bodyFloatPrefix}${bodyContentHtml}</div>`;
    return `<div style="width:${A4_PAGE_WIDTH_PX}px;height:${A4_PAGE_HEIGHT_PX}px;box-sizing:border-box;font-family:${fontFamily};font-size:${fontSize};line-height:${lineHeight};color:${color};display:flex;flex-direction:column">${header}${body}${footer}</div>`;
  }

  // Sides path: 3×3 CSS grid with the sides claiming `auto`-sized columns
  // that collapse to 0 when absent. Header/body/footer all sit in the
  // central column so they automatically inset around the side bands.
  //
  // A PAGE-FIT header/footer can't live in that column: the column is as
  // narrow as the bands leave it, and the bands size themselves (`auto`), so
  // there is no width to offset by. It spans the whole row instead, at page
  // width, and the bands stack above it — the same result as the paginated
  // path, where the band clips the artwork rather than squeezing it.
  const headerIsPageFit = showHeader && pageFit.headerTop !== null;
  const footerIsPageFit = showFooter && pageFit.footerBottom !== null;
  const bandStack =
    headerIsPageFit || footerIsPageFit ? "position:relative;z-index:1;" : "";
  const header = !showHeader
    ? `<div style="grid-area:hdr"></div>`
    : headerIsPageFit
      ? `<div style="grid-row:1;grid-column:1 / -1;padding:${slotP.headerTop}px 0 0 0;overflow:hidden">${fitZoneHtml(fragments.headerHtml ?? "", pageFit.headerTop)}</div>`
      : `<div style="grid-area:hdr;padding:${slotP.headerTop}px ${slotP.headerRight}px 0 ${slotP.headerLeft}px">${fragments.headerHtml}</div>`;
  const footer = !showFooter
    ? `<div style="grid-area:ftr"></div>`
    : footerIsPageFit
      ? `<div style="grid-row:3;grid-column:1 / -1;padding:0 0 ${slotP.footerBottom}px 0;overflow:hidden">${fitZoneHtml(fragments.footerHtml ?? "", pageFit.footerBottom)}</div>`
      : `<div style="grid-area:ftr;padding:0 ${slotP.footerRight}px ${slotP.footerBottom}px ${slotP.footerLeft}px">${fragments.footerHtml}</div>`;
  const body = `<div class="letter-body prose-letter" style="grid-area:body;min-height:0;padding:${p.bodyTop}px ${p.bodyRight}px ${p.bodyTop}px ${p.bodyLeft}px">${bodyFloatPrefix}${bodyContentHtml}</div>`;
  const leftCell =
    showLeftSide && fragments.leftSideHtml
      ? `<div style="grid-area:lft;${bandStack}height:100%;line-height:0;font-size:0">${fragments.leftSideHtml.replace(
          /style="([^"]*)"/i,
          'style="display:block;height:100%;width:auto"',
        )}</div>`
      : `<div style="grid-area:lft;width:0"></div>`;
  const rightCell =
    showRightSide && fragments.rightSideHtml
      ? `<div style="grid-area:rgt;${bandStack}height:100%;line-height:0;font-size:0">${fragments.rightSideHtml.replace(
          /style="([^"]*)"/i,
          'style="display:block;height:100%;width:auto"',
        )}</div>`
      : `<div style="grid-area:rgt;width:0"></div>`;

  const gridStyle =
    `width:${A4_PAGE_WIDTH_PX}px;height:${A4_PAGE_HEIGHT_PX}px;box-sizing:border-box;font-family:${fontFamily};font-size:${fontSize};line-height:${lineHeight};color:${color};` +
    `display:grid;grid-template-columns:auto 1fr auto;grid-template-rows:auto 1fr auto;` +
    `grid-template-areas:'lft hdr rgt' 'lft body rgt' 'lft ftr rgt';`;

  return `<div style="${gridStyle}">${leftCell}${header}${rightCell}${body}${footer}</div>`;
}

// ---------------------------------------------------------------------------
// Image helpers
// ---------------------------------------------------------------------------

/** Detect actual image type from the first few base64 characters. */
function detectImageMime(base64: string): string {
  if (base64.startsWith("iVBOR")) return "image/png";
  if (base64.startsWith("/9j/")) return "image/jpeg";
  if (base64.startsWith("R0lGOD")) return "image/gif";
  if (base64.startsWith("UklGR")) return "image/webp";
  return "image/png";
}

/**
 * Fix MIME types in data: URIs. docx-preview often outputs
 * data:application/octet-stream;base64,... for PNG images.
 * Word won't render images with the wrong MIME type.
 */
function fixDataUriMimeTypes(html: string): string {
  return html.replace(
    /src\s*=\s*"data:([^;]+);base64,([^"]*)"/gi,
    (_match, _declaredMime: string, base64: string) => {
      const correctMime = detectImageMime(base64);
      return `src="data:${correctMime};base64,${base64}"`;
    },
  );
}

/**
 * Fetch an image URL and return it as a data URI string.
 * Returns null if the fetch fails.
 *
 * Results are memoised by URL: exports fetch the same letterhead /
 * signature imagery for every document (and every page render) of a bulk
 * download, so re-fetching + re-base64-encoding per document made large
 * day-one downloads crawl (MOR-971). Failures are not cached so a
 * transient network error can recover on the next attempt.
 */
const imageDataUriCache = new Map<string, Promise<string | null>>();

function fetchImageAsDataUri(url: string): Promise<string | null> {
  const cached = imageDataUriCache.get(url);
  if (cached) return cached;
  const promise = fetchImageAsDataUriUncached(url).then((result) => {
    if (result === null) imageDataUriCache.delete(url);
    return result;
  });
  imageDataUriCache.set(url, promise);
  return promise;
}

async function fetchImageAsDataUriUncached(
  url: string,
): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const binary = bytes.reduce(
      (acc, byte) => acc + String.fromCharCode(byte),
      "",
    );
    const base64 = btoa(binary);
    const mime = blob.type || "image/png";
    return `data:${mime};base64,${base64}`;
  } catch {
    return null;
  }
}

/**
 * Find all external image URLs (http/https) in HTML and replace them
 * with embedded base64 data URIs. This ensures Word can render images
 * without needing network access (signed Convex URLs expire).
 */
async function embedExternalImages(html: string): Promise<string> {
  const urlPattern = /src\s*=\s*"(https?:\/\/[^"]*)"/gi;
  const matches: Array<{ full: string; url: string }> = [];
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(html)) !== null) {
    matches.push({ full: match[0], url: match[1] });
  }

  if (matches.length === 0) return html;

  const uniqueUrls = [...new Set(matches.map((m) => m.url))];
  const urlToDataUri = new Map<string, string>();

  await Promise.all(
    uniqueUrls.map(async (url) => {
      const dataUri = await fetchImageAsDataUri(url);
      if (dataUri) urlToDataUri.set(url, dataUri);
    }),
  );

  const result = Array.from(urlToDataUri).reduce(
    (acc, [url, dataUri]) => acc.split(`src="${url}"`).join(`src="${dataUri}"`),
    html,
  );

  return result;
}

// ---------------------------------------------------------------------------
// Word-specific transforms
// ---------------------------------------------------------------------------

const WORD_INCOMPATIBLE_CSS = [
  /display\s*:\s*flex\s*;?/gi,
  /display\s*:\s*inline-flex\s*;?/gi,
  /display\s*:\s*grid\s*;?/gi,
  /flex-direction\s*:[^;]+;?/gi,
  /flex-wrap\s*:[^;]+;?/gi,
  /justify-content\s*:[^;]+;?/gi,
  /align-items\s*:[^;]+;?/gi,
  /align-self\s*:[^;]+;?/gi,
  /gap\s*:\s*[^;]+;?/gi,
  /position\s*:\s*(?:absolute|fixed|sticky)\s*;?/gi,
  /object-fit\s*:[^;]+;?/gi,
  /visibility\s*:\s*hidden\s*;?/gi,
];

/**
 * Strip CSS properties that Word's HTML engine cannot handle.
 * Operates on inline style="" attributes within HTML fragments.
 */
function sanitizeForWord(html: string): string {
  return html.replace(/style="([^"]*)"/gi, (_match, styleContent: string) => {
    const cleaned = WORD_INCOMPATIBLE_CSS.reduce(
      (acc, pattern) => acc.replace(pattern, ""),
      styleContent,
    )
      .replace(/;\s*;/g, ";")
      .replace(/^\s*;\s*/, "")
      .replace(/\s*;\s*$/, "")
      .trim();
    if (!cleaned) return "";
    return `style="${cleaned}"`;
  });
}

/**
 * Convert CSS layout patterns that Word doesn't understand into
 * HTML attributes that Word does understand.
 *
 * - `margin-left:auto` on tables → `align="right"` attribute
 * - `display:inline-block` divs → converted to table cells for
 *   side-by-side layout (Word uses tables for multi-column layout)
 * - Cleans up invalid `<table>` inside `<p>` nesting
 */
function convertCssToWordAttributes(html: string): string {
  // Tables with margin-left:auto → align="right"
  const withRightAlignedTables = html.replace(
    /<table([^>]*)>/gi,
    (_match, attrs: string) => {
      const styleMatch = /style="([^"]*)"/i.exec(attrs);
      if (!styleMatch) return _match;
      const style = styleMatch[1];

      if (/margin-left\s*:\s*auto/i.test(style)) {
        const cleanedStyle = style
          .replace(/margin-left\s*:\s*auto\s*;?/gi, "")
          .replace(/;\s*;/g, ";")
          .replace(/^\s*;\s*/, "")
          .replace(/\s*;\s*$/, "")
          .trim();
        const styleAttr = cleanedStyle ? ` style="${cleanedStyle}"` : "";
        const otherAttrs = attrs.replace(/style="[^"]*"/i, "").trim();
        return `<table align="right"${otherAttrs ? " " + otherAttrs : ""}${styleAttr}>`;
      }

      return _match;
    },
  );

  // Word can flow following paragraphs beside a bare align="right" table.
  // Wrap those tables in a full-width row so subsequent blocks start below.
  const withWrappedRightAlignedTables = withRightAlignedTables.replace(
    /<table([^>]*)\salign="right"([^>]*)>([\s\S]*?)<\/table>/gi,
    (_match, beforeAlign: string, afterAlign: string, content: string) => {
      if (/<table\b/i.test(content)) {
        return _match;
      }

      const rawInnerAttrs = `${beforeAlign}${afterAlign}`.trim();
      const innerAttrs = /style="/i.test(rawInnerAttrs)
        ? rawInnerAttrs.replace(
            /style="/i,
            'style="border:none;mso-border-alt:none;',
          )
        : `${rawInnerAttrs}${rawInnerAttrs ? " " : ""}style="border:none;mso-border-alt:none"`;

      return `<table width="100%" cellpadding="0" cellspacing="0" style="width:100%;border:none;mso-border-alt:none;border-collapse:collapse"><tr><td align="right" style="border:none;mso-border-alt:none"><table${innerAttrs ? ` ${innerAttrs}` : ""}>${content}</table></td></tr></table>`;
    },
  );

  // Convert inline-block divs to table layout for Word.
  // docx-preview wraps DOCX column layouts as:
  //   <p><span><div style="display:inline-block">...</div><div ...>...</div></span></p>
  // Word can't handle inline-block or <table> inside <p>.
  const withTableLayout = /display:\s*inline-block/i.test(
    withWrappedRightAlignedTables,
  )
    ? convertInlineBlockToTable(withWrappedRightAlignedTables)
    : withWrappedRightAlignedTables;

  // Safety net: remove <p>/<span> wrappers that directly contain <table>
  // (invalid HTML that confuses Word's renderer).
  const result = withTableLayout.replace(
    /<p[^>]*>\s*(?:<span[^>]*>\s*)?(<table[\s\S]*?<\/table>)\s*(?:<\/span>\s*)?<\/p>/gi,
    "$1",
  );

  return result;
}

/**
 * Convert consecutive display:inline-block divs into a Word-compatible
 * table layout. Uses regex with a two-step approach:
 *
 * 1. Unwrap `<p><span>` wrappers around inline-block divs (invalid nesting)
 * 2. Convert groups of 2+ consecutive inline-block divs into `<table><tr><td>` cells
 *
 * NOTE: The inner content regex uses `[\s\S]*?</div>` (lazy) which assumes
 * inline-block divs don't contain nested `<div>` elements. This holds for
 * docx-preview header output (uses `<p>/<span>` inside, not nested `<div>`).
 */
function convertInlineBlockToTable(html: string): string {
  const result = html
    .replace(
      /<p[^>]*>\s*<span[^>]*>\s*(?=<div[^>]*style="[^"]*display:\s*inline-block)/gi,
      "",
    )
    .replace(/(<\/div>)\s*<\/span>\s*<\/p>/gi, "$1");

  const inlineBlockDivStart =
    /<div([^>]*)style="[^"]*display:\s*inline-block[^"]*"[^>]*>/i;
  const anchoredInlineBlockDivStart =
    /^<div([^>]*)style="[^"]*display:\s*inline-block[^"]*"[^>]*>/i;
  const divTagPattern = /<\/?div\b[^>]*>/gi;

  const findMatchingDivEnd = (source: string, startIndex: number): number => {
    divTagPattern.lastIndex = startIndex;
    let depth = 0;
    let match: RegExpExecArray | null;

    while ((match = divTagPattern.exec(source)) !== null) {
      if (match[0].startsWith("</")) {
        depth -= 1;
        if (depth === 0) {
          return divTagPattern.lastIndex;
        }
      } else {
        depth += 1;
      }
    }

    return -1;
  };

  const readInlineBlockDiv = (
    source: string,
    startIndex: number,
  ): {
    fullHtml: string;
    attrs: string;
    content: string;
    endIndex: number;
  } | null => {
    const openingMatch = anchoredInlineBlockDivStart.exec(
      source.slice(startIndex),
    );
    if (!openingMatch || openingMatch.index !== 0) return null;

    const openingTag = openingMatch[0];
    const attrs = openingMatch[1];
    const endIndex = findMatchingDivEnd(source, startIndex);
    if (endIndex === -1) return null;

    const fullHtml = source.slice(startIndex, endIndex);
    const content = fullHtml.slice(
      openingTag.length,
      fullHtml.length - "</div>".length,
    );
    return { fullHtml, attrs, content, endIndex };
  };

  let rebuilt = "";
  let cursor = 0;

  while (cursor < result.length) {
    const remaining = result.slice(cursor);
    const startMatch = inlineBlockDivStart.exec(remaining);
    if (!startMatch) {
      rebuilt += remaining;
      break;
    }

    const firstStart = cursor + startMatch.index;
    rebuilt += result.slice(cursor, firstStart);

    const cells: Array<{ attrs: string; content: string }> = [];
    let groupCursor = firstStart;

    while (groupCursor < result.length) {
      const nextCell = readInlineBlockDiv(result, groupCursor);
      if (!nextCell) break;

      cells.push({ attrs: nextCell.attrs, content: nextCell.content });
      groupCursor = nextCell.endIndex;

      const whitespaceMatch = /^\s*/.exec(result.slice(groupCursor));
      groupCursor += whitespaceMatch?.[0].length ?? 0;
    }

    if (cells.length < 2) {
      const singleCell = readInlineBlockDiv(result, firstStart);
      if (!singleCell) {
        rebuilt += result.slice(firstStart, firstStart + 1);
        cursor = firstStart + 1;
        continue;
      }

      rebuilt += singleCell.fullHtml;
      cursor = singleCell.endIndex;
      continue;
    }

    const tableCells = cells
      .map(({ attrs, content }) => {
        const styleMatch = /style="([^"]*)"/i.exec(attrs);
        const cleanedStyle = styleMatch
          ? styleMatch[1]
              .replace(/display\s*:\s*inline-block\s*;?/gi, "")
              .replace(/position\s*:\s*relative\s*;?/gi, "")
              .replace(/text-indent\s*:\s*[^;]+;?/gi, "")
              .replace(/height\s*:\s*[^;]+;?/gi, "")
              .replace(/;\s*;/g, ";")
              .replace(/^\s*;\s*/, "")
              .replace(/\s*;\s*$/, "")
              .trim()
          : "";
        const tdStyle = cleanedStyle
          ? `${cleanedStyle};border:none;mso-border-alt:none`
          : "border:none;mso-border-alt:none";

        const widthMatch = /width\s*:\s*(\d+(?:\.\d+)?(?:pt|px|%))/i.exec(
          attrs,
        );
        const widthAttr = widthMatch ? ` width="${widthMatch[1]}"` : "";
        const valignMatch = /vertical-align\s*:\s*(\w+)/i.exec(attrs);
        const valignAttr = ` valign="${valignMatch?.[1] ?? "top"}"`;

        return `<td${widthAttr}${valignAttr} style="${tdStyle}">${content}</td>`;
      })
      .join("");

    rebuilt += `<table width="100%" cellpadding="0" cellspacing="0" style="width:100%;border:none;mso-border-alt:none;border-collapse:collapse"><tr>${tableCells}</tr></table>`;
    cursor = groupCursor;
  }

  return rebuilt;
}

/**
 * Convert <hr> to a table-based rule that Word renders reliably.
 * Explicitly zeroes all borders except the intended top rule.
 */
function hrToWordTable(html: string): string {
  return html.replace(/<hr([^>]*)\/?\s*>/gi, (_match, attrs: string) => {
    const colorMatch =
      /border-top:\s*[^;]*?(#[0-9a-fA-F]{3,8}|rgb[^)]*\))/i.exec(attrs);
    const widthMatch = /border-top:\s*(\d+(?:\.\d+)?px)/i.exec(attrs);
    const color = colorMatch ? colorMatch[1] : "#1B3A5C";
    const width = widthMatch ? widthMatch[1] : "2px";
    return `<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:none;mso-border-alt:none"><tr><td style="border-top:${width} solid ${color};border-right:none;border-bottom:none;border-left:none;mso-border-top-alt:${width} solid ${color};font-size:1px;line-height:1px">&nbsp;</td></tr></table>`;
  });
}

/**
 * Convert empty blocks that only carry border-top/border-bottom styles into
 * explicit table rules. docx-preview commonly emits these as
 * `<p style="border-top:..."><span></span></p>` and Word often ignores them
 * unless converted to a table-based rule.
 */
function borderRuleBlocksToWordTables(html: string): string {
  return html.replace(
    /<(p|div)([^>]*)>\s*(?:<span[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*\s*<\/span>\s*)*<\/\1>/gi,
    (match, _tag: string, attrs: string) => {
      const styleMatch = /style="([^"]*)"/i.exec(attrs);
      if (!styleMatch) return match;
      const style = styleMatch[1];

      const topMatch = /border-top:\s*([^;]+)/i.exec(style);
      const bottomMatch = /border-bottom:\s*([^;]+)/i.exec(style);
      const rule = topMatch
        ? {
            borderCss: `border-top:${topMatch[1]}`,
            msoBorderCss: `mso-border-top-alt:${topMatch[1]}`,
          }
        : bottomMatch
          ? {
              borderCss: `border-bottom:${bottomMatch[1]}`,
              msoBorderCss: `mso-border-bottom-alt:${bottomMatch[1]}`,
            }
          : null;
      if (!rule) return match;
      const { borderCss, msoBorderCss } = rule;

      return `<table width="100%" cellpadding="0" cellspacing="0" style="width:100%;border:none;mso-border-alt:none;border-collapse:collapse"><tr><td style="${borderCss};border-right:none;border-left:none;${topMatch ? "border-bottom:none" : "border-top:none"};${msoBorderCss};font-size:1px;line-height:1px">&nbsp;</td></tr></table>`;
    },
  );
}

/**
 * Add explicit border:none to table elements that don't already have
 * inline border styles. Word applies default borders to bare tables.
 *
 * MOR-425: When a table's cells carry inline `border:` declarations (the
 * canonical TipTap shape — `<table style="border-collapse:collapse">` with
 * each `<td>/<th>` styled `border: 0.5pt solid #DCDCDC`), forcing
 * `mso-border-alt:none` on the parent `<table>` would explicitly null the
 * table-level borders in OOXML. In `border-collapse:collapse` mode Word's
 * altChunk import lets that table-level null override the cell-level
 * `border:` declarations, so cell borders silently disappear from the
 * exported .docx — exactly the regression Elle hit on Form 535.
 *
 * Fix: when descendant cells already carry inline borders, leave the
 * `<table>` tag alone (don't add `border:none;mso-border-alt:none`) and
 * mirror each cell's `border:` / `border-{side}:` declaration to its
 * `mso-border(-side)?-alt` twin so Word's altChunk reliably populates
 * `<w:tcBorders>` from the cell, not from a nulled table.
 *
 * Tables with no cell-level borders (e.g. the right-aligned appointment
 * blocks built from `convertCssToWordAttributes`) keep the existing
 * `border:none;mso-border-alt:none` neutralisation so Word doesn't draw
 * its default 0.5pt black border around bare tables.
 */
function neutralizeTableBorders(html: string): string {
  // Matches any CSS border-family property on a cell: shorthand `border:`,
  // per-side shorthand `border-{top|right|bottom|left}:`, the longhand
  // triplet `border-{width|style|color}:`, and the per-side longhand
  // `border-{side}-{width|style|color}:` (MOR-456 — mammoth's DOCX→HTML
  // output for the C&P Form 535 re-upload uses the longhand triplet, which
  // the original MOR-425 regex missed).
  const cellBorderInsideTableRe =
    /<(?:td|th)\b[^>]*style\s*=\s*"[^"]*\bborder(?:-(?:top|right|bottom|left))?(?:-(?:width|style|color))?\s*:/i;

  const withNeutralizedTables = html.replace(
    /<table\b([^>]*)>([\s\S]*?)<\/table>/gi,
    (_match, tableAttrs: string, inner: string) => {
      const cellsHaveBorders = cellBorderInsideTableRe.test(inner);
      const tableTagHasBorder = hasBorderInStyle(tableAttrs);
      const mirroredInner = cellsHaveBorders
        ? mirrorBordersToMsoAlt(inner)
        : forceNoBorderOnBareCells(inner);

      // Cells own the borders — set only the CSS `border:none` to suppress
      // Word's default bare-table border WITHOUT nulling `mso-border-alt`,
      // which would override cell borders in border-collapse mode (MOR-425).
      const newTableTag = tableTagHasBorder
        ? `<table${tableAttrs}>`
        : cellsHaveBorders
          ? /style="/i.test(tableAttrs)
            ? `<table${tableAttrs.replace(/style="/i, 'style="border:none;')}>`
            : `<table${tableAttrs} style="border:none">`
          : /style="/i.test(tableAttrs)
            ? `<table${tableAttrs.replace(/style="/i, 'style="border:none;mso-border-alt:none;')}>`
            : `<table${tableAttrs} style="border:none;mso-border-alt:none">`;

      return `${newTableTag}${mirroredInner}</table>`;
    },
  );

  // Stragglers: <td>/<th> not inside any <table> (extremely rare, but the
  // original implementation handled them — preserve that fallback).
  const result = withNeutralizedTables.replace(
    /<(td|th)([^>]*)>/gi,
    (match, tag: string, attrs: string) => {
      if (hasBorderInStyle(attrs)) return match;
      if (/mso-border-alt/i.test(attrs)) return match;
      if (/style="/i.test(attrs)) {
        return `<${tag}${attrs.replace(/style="/i, 'style="border:none;mso-border-alt:none;')}>`;
      }
      return `<${tag}${attrs} style="border:none;mso-border-alt:none">`;
    },
  );

  return result;
}

function hasBorderInStyle(attrs: string): boolean {
  const styleMatch = /style="([^"]*)"/i.exec(attrs);
  if (!styleMatch) return false;
  // Same vocabulary as `cellBorderInsideTableRe` — shorthand, per-side
  // shorthand, longhand triplet, and per-side longhand all count.
  const re =
    /(?:^|;\s*)border(?:-(?:top|right|bottom|left))?(?:-(?:width|style|color))?\s*:/i;
  return re.test(styleMatch[1]);
}

function forceNoBorderOnBareCells(inner: string): string {
  return inner.replace(
    /<(td|th)([^>]*)>/gi,
    (match, tag: string, attrs: string) => {
      if (hasBorderInStyle(attrs)) return match;
      if (/style="/i.test(attrs)) {
        return `<${tag}${attrs.replace(/style="/i, 'style="border:none;mso-border-alt:none;')}>`;
      }
      return `<${tag}${attrs} style="border:none;mso-border-alt:none">`;
    },
  );
}

/**
 * Read a CSS longhand border triplet (`border-{width|style|color}` or the
 * per-side variant `border-{side}-{width|style|color}`) from an inline style
 * string and return a reconstructed shorthand value (e.g. `"0.5pt solid
 * rgb(220, 220, 220)"`). Returns `null` when none of the three properties is
 * present — the caller should not synthesise a mirror in that case.
 *
 * Missing pieces fall back to CSS defaults (`medium none currentcolor`) so the
 * emitted shorthand stays well-formed for Word's altChunk import.
 *
 * `sideSuffix` is `""` for the table/cell-level triplet and `"-top"` /
 * `"-right"` / `"-bottom"` / `"-left"` for the per-side variants.
 */
function readLonghandBorder(style: string, sideSuffix: string): string | null {
  const widthRe = new RegExp(
    `(?:^|;)\\s*border${sideSuffix}-width\\s*:\\s*([^;]+?)\\s*(?=;|$)`,
    "i",
  );
  const styleRe = new RegExp(
    `(?:^|;)\\s*border${sideSuffix}-style\\s*:\\s*([^;]+?)\\s*(?=;|$)`,
    "i",
  );
  const colorRe = new RegExp(
    `(?:^|;)\\s*border${sideSuffix}-color\\s*:\\s*([^;]+?)\\s*(?=;|$)`,
    "i",
  );
  const w = widthRe.exec(style);
  const s = styleRe.exec(style);
  const c = colorRe.exec(style);
  if (!w && !s && !c) return null;
  const width = w?.[1].trim() ?? "medium";
  const styleKeyword = s?.[1].trim() ?? "none";
  const color = c?.[1].trim() ?? "currentcolor";
  return `${width} ${styleKeyword} ${color}`;
}

/**
 * Mirror inline `border:` / `border-{side}:` declarations to their
 * `mso-border-alt` / `mso-border-{side}-alt` twins on `<table>`, `<tr>`,
 * `<td>`, `<th>` elements.
 *
 * Word's altChunk HTML importer uses `mso-border-alt` to populate the
 * OOXML `<w:tblBorders>` / `<w:tcBorders>` elements. Plain CSS `border:`
 * alone is unreliable for tables in `border-collapse:collapse` mode —
 * Office's own HTML output mirrors every cell border declaration with an
 * `mso-border-alt` twin (e.g. `border:solid black 1pt;mso-border-alt:solid
 * black 1pt`) and altChunk round-trips that idiom cleanly.
 *
 * Existing `mso-border(-side)?-alt` declarations are preserved.
 */
function mirrorBordersToMsoAlt(html: string): string {
  const sides = ["top", "right", "bottom", "left"] as const;
  return html.replace(
    /<(table|tr|td|th)\b([^>]*)>/gi,
    (match, tag: string, attrs: string) => {
      const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(attrs);
      if (!styleMatch) return match;
      const style = styleMatch[1];
      const additions: string[] = [];

      const shorthandMatch = /(?:^|;)\s*border\s*:\s*([^;]+?)\s*(?=;|$)/i.exec(
        style,
      );
      const hasMsoBorderAlt = /(?:^|;)\s*mso-border-alt\s*:/i.test(style);
      if (shorthandMatch && !hasMsoBorderAlt) {
        additions.push(`mso-border-alt:${shorthandMatch[1].trim()}`);
      } else if (!shorthandMatch && !hasMsoBorderAlt) {
        // Longhand triplet `border-width/style/color` (MOR-456). Mammoth's
        // DOCX→HTML output for the C&P Form 535 re-upload uses this shape.
        // CSS defaults fill in any missing piece so Word still gets a
        // well-formed shorthand mirror.
        const longhand = readLonghandBorder(style, "");
        if (longhand) {
          additions.push(`mso-border-alt:${longhand}`);
        }
      }

      for (const side of sides) {
        const msoRe = new RegExp(`(?:^|;)\\s*mso-border-${side}-alt\\s*:`, "i");
        if (msoRe.test(style)) continue;

        const sideRe = new RegExp(
          `(?:^|;)\\s*border-${side}\\s*:\\s*([^;]+?)\\s*(?=;|$)`,
          "i",
        );
        const sideMatch = sideRe.exec(style);
        if (sideMatch) {
          additions.push(`mso-border-${side}-alt:${sideMatch[1].trim()}`);
          continue;
        }

        // Per-side longhand triplet `border-{side}-{width|style|color}`.
        const sideLonghand = readLonghandBorder(style, `-${side}`);
        if (sideLonghand) {
          additions.push(`mso-border-${side}-alt:${sideLonghand}`);
        }
      }

      if (additions.length === 0) return match;

      const trimmed = style.trim();
      const sep = trimmed === "" ? "" : trimmed.endsWith(";") ? "" : ";";
      const newStyle = `${trimmed}${sep}${additions.join(";")}`;
      const newAttrs = attrs.replace(
        /\bstyle\s*=\s*"[^"]*"/i,
        `style="${newStyle}"`,
      );
      return `<${tag}${newAttrs}>`;
    },
  );
}

/**
 * Strip auto-generated `<colgroup><col style="min-width: …px"></colgroup>`
 * artifacts that TipTap's `TableKit` (with `resizable: true`) emits on
 * every serialization (see MOR-220).
 *
 * Word's altChunk treats `<col style="min-width: 25px">` as a fixed 25px
 * column width, which fights `<table style="width: 100%">` and squashes
 * cells in the rendered .docx (visible on the Form 535 second table in
 * MOR-425). Stripping these defaults lets Word size each column from its
 * cell content, matching the PDF render.
 *
 * Preserves any colgroup whose `<col>` carries an explicit `width:` (or
 * `width=` attribute) — those are user-resized columns.
 */
function stripAutoGeneratedColgroups(html: string): string {
  return html.replace(
    /<colgroup\b[^>]*>([\s\S]*?)<\/colgroup>/gi,
    (match, inner: string) => {
      const cols = [...inner.matchAll(/<col\b([^>]*?)\/?\s*>/gi)];
      if (cols.length === 0) return match;
      const allAutoMinWidth = cols.every((m) => {
        const colAttrs = m[1];
        const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(colAttrs);
        const style = styleMatch?.[1] ?? "";
        const hasMinWidth = /(?:^|;)\s*min-width\s*:/i.test(style);
        const hasExplicitWidthStyle = /(?:^|;)\s*width\s*:/i.test(style);
        const hasExplicitWidthAttr = /\swidth\s*=/i.test(colAttrs);
        return hasMinWidth && !hasExplicitWidthStyle && !hasExplicitWidthAttr;
      });
      return allAutoMinWidth ? "" : match;
    },
  );
}

/**
 * Stretch user-resized TipTap tables to the full body width (MOR-791).
 *
 * The template editor renders EVERY table at `width:100%` with
 * `table-layout:fixed` (see `.template-editor-content table` in
 * editor-styles.css), so explicit pixel column widths behave as
 * *proportions* of the full content width while editing. The exports
 * (preview, PDF, and Word) instead honoured the serialised inline widths
 * literally — a table resized to e.g. `width:445px` with 48px/397px
 * columns rendered narrower than the body text around it, wrapping cell
 * content early (Shaw Gidley: the "Formerly Trading as …" line stopped
 * ~170px short of the right edge the sentences below reach).
 *
 * This transform rewrites that resized-table signature — an explicit
 * pixel `width` on the `<table>` style plus a `<colgroup>` whose every
 * `<col>` carries an explicit pixel width — to match the editor:
 *
 *   - table `width` → `100%` (+ `table-layout:fixed`)
 *   - each col width → its percentage share of the summed column widths
 *
 * Right-aligned layout tables (`margin-left:auto` or an `align` attr) are
 * deliberately narrow — they keep their explicit width. Tables whose
 * colgroup mixes resized and auto (`min-width`-only) columns are left
 * untouched, as are tables with no explicit pixel width.
 */
export function stretchUserResizedTablesToFullWidth(html: string): string {
  if (!/<colgroup/i.test(html)) return html;
  return html.replace(
    /(<table\b[^>]*>)(\s*)(<colgroup\b[^>]*>)([\s\S]*?)(<\/colgroup>)/gi,
    (
      match,
      tableTag: string,
      ws: string,
      colgroupOpen: string,
      colsInner: string,
      colgroupClose: string,
    ) => {
      const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(tableTag);
      if (!styleMatch) return match;
      const tableStyle = styleMatch[1];

      const tableWidthRe = /(?:^|;)\s*width\s*:\s*[0-9]*\.?[0-9]+px\s*(?=;|$)/i;
      if (!tableWidthRe.test(tableStyle)) return match;
      // Right-aligned blocks are intentionally narrow — keep their width.
      if (/margin-left\s*:\s*auto/i.test(tableStyle)) return match;
      if (/\balign\s*=/i.test(tableTag)) return match;

      const cols = [...colsInner.matchAll(/<col\b([^>]*?)\/?\s*>/gi)];
      if (cols.length === 0) return match;
      const widths: number[] = [];
      for (const col of cols) {
        const colStyle = /\bstyle\s*=\s*"([^"]*)"/i.exec(col[1])?.[1] ?? "";
        const w =
          /(?:^|;)\s*width\s*:\s*([0-9]*\.?[0-9]+)px\s*(?=;|$)/i.exec(
            colStyle,
          ) ?? /\bwidth\s*=\s*"([0-9]*\.?[0-9]+)(?:px)?"/i.exec(col[1]);
        // Any auto column → not the fully-resized signature; leave as-is.
        if (!w) return match;
        widths.push(parseFloat(w[1]));
      }
      const total = widths.reduce((a, b) => a + b, 0);
      if (!(total > 0)) return match;

      const newTableStyle = `${tableStyle
        .replace(
          /(^|;)\s*width\s*:\s*[0-9]*\.?[0-9]+px\s*(?=;|$)/i,
          "$1width:100%",
        )
        .replace(/(^|;)\s*table-layout\s*:[^;]*(?=;|$)/i, "$1")
        .replace(/;\s*;/g, ";")
        .replace(/^\s*;\s*/, "")
        .replace(/\s*;\s*$/, "")
        .trim()};table-layout:fixed`.replace(/^;/, "");
      const newTableTag = tableTag.replace(
        /\bstyle\s*=\s*"[^"]*"/i,
        `style="${newTableStyle}"`,
      );

      const newCols = cols
        .map((col, i) => {
          const pct = `${((widths[i] / total) * 100).toFixed(4)}%`;
          const strippedAttrs = col[1]
            .replace(/\bwidth\s*=\s*"[^"]*"/i, "")
            .replace(/\s+$/, "");
          const colStyleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(strippedAttrs);
          const cleaned = colStyleMatch
            ? colStyleMatch[1]
                .replace(
                  /(^|;)\s*width\s*:\s*[0-9]*\.?[0-9]+px\s*(?=;|$)/i,
                  `$1width:${pct}`,
                )
                .replace(/;\s*;/g, ";")
                .replace(/^\s*;\s*/, "")
                .trim()
            : "";
          const colAttrs = colStyleMatch
            ? strippedAttrs.replace(
                /\bstyle\s*=\s*"[^"]*"/i,
                `style="${cleaned.includes("width:") ? cleaned : `${cleaned};width:${pct}`.replace(/^;/, "")}"`,
              )
            : `${strippedAttrs} style="width:${pct}"`;
          // Word's HTML importer reads the classic width attribute more
          // reliably than CSS on <col> — emit both.
          return `<col width="${pct}"${colAttrs}>`;
        })
        .join("");

      return `${newTableTag}${ws}${colgroupOpen}${newCols}${colgroupClose}`;
    },
  );
}

/**
 * Give tables the editor's full-width geometry (MOR-829).
 *
 * `.template-editor-content table` (editor-styles.css) renders EVERY table
 * at `width:100%` with `table-layout:fixed`, so an author laying out a
 * two-column table sees two equal half-width columns. That geometry lives
 * only in the editor stylesheet: TipTap serialises a bare `<table>` and its
 * auto-generated min-width `<colgroup>` is stripped on save
 * (`stripAutoGeneratedColgroups`), so the preview, PDF, and Word exports all
 * fell back to the browser/Word shrink-to-fit layout. A PPSR registration
 * table authored as "Date of registration | Registration number" collapsed
 * to the width of its longest cell and the two header labels ran together.
 *
 * Tables that carry their own width are left alone: user-resized tables are
 * already rewritten to percentages by
 * `stretchUserResizedTablesToFullWidth`, and deliberately narrow
 * right-aligned layout blocks (`margin-left:auto`, or an `align` attribute)
 * keep their intrinsic width — same exemptions as that transform.
 *
 * The `width="100%"` attribute is emitted alongside the CSS declaration
 * because Word's altChunk importer reads the classic attribute far more
 * reliably than the style (mirrors what `stretchUserResizedTablesToFullWidth`
 * already does for `<col>`).
 */
export function stretchUnsizedTablesToFullWidth(html: string): string {
  if (!/<table\b/i.test(html)) return html;
  return html.replace(/<table\b([^>]*)>/gi, (match, attrs: string) => {
    if (/\swidth\s*=/i.test(attrs)) return match;
    if (/\salign\s*=/i.test(attrs)) return match;
    const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(attrs);
    const style = styleMatch?.[1] ?? "";
    if (/(?:^|;)\s*width\s*:/i.test(style)) return match;
    if (/margin-left\s*:\s*auto/i.test(style)) return match;

    const additions = "width:100%;table-layout:fixed";
    const newAttrs = styleMatch
      ? attrs.replace(
          /\bstyle\s*=\s*"/i,
          style ? `style="${additions};` : `style="${additions}`,
        )
      : `${attrs} style="${additions}"`;
    return `<table width="100%"${newAttrs}>`;
  });
}

/**
 * Bring every table in a document body up to the geometry the template
 * editor shows the author — resized tables converted to proportional
 * percentages, un-sized tables stretched to the full body width.
 *
 * Applied by all three render surfaces (preview, PDF print window, Word
 * altChunk) on the BODY html only, so branding header/footer layout tables
 * keep their own sizing.
 */
export function normalizeTableWidthsForOutput(html: string): string {
  return stretchUnsizedTablesToFullWidth(
    stretchUserResizedTablesToFullWidth(html),
  );
}

/**
 * Convert unitless `line-height` values inside inline `style="..."` attributes
 * to absolute `pt` form so Word's altChunk import respects per-paragraph
 * spacing set in the editor.
 *
 * Why absolute `pt` + `mso-line-height-rule:at-least`:
 *
 * There are three viable strategies and each has a tradeoff:
 *
 *   - `lineRule="auto"` with a multiplier (e.g. `<w:line w:val="240" w:lineRule="auto"/>`)
 *     means N × *natural* line height. Word strips fractional multipliers
 *     below `1.0` to single, so `0.85` and `1.0` look identical.
 *   - `lineRule="exact"` with a pt value means literally that. Faithful to
 *     CSS semantics for loose values (≥ ~1.2 × font) but **clips glyph
 *     descenders/ascenders** for tight values (< ~1.2 × font) because the
 *     line box is smaller than the natural glyph cell. Symptom: the bottom
 *     of every line in tight address blocks (e.g. line-height `0.85`) is
 *     visibly cut off in the exported Word document.
 *   - `lineRule="atLeast"` with a pt value means `max(specified, natural)`.
 *     For values above natural (loose: `1.5`, `1.6`, `2.0`) it renders
 *     literally — same as `exact`, so the user's loose intent is preserved.
 *     For values below natural (tight: `0.85`, `1.0`, `1.15`) Word falls
 *     back to single-line height, which is **closer than default `1.6` but
 *     not as tight as the editor renders**, and crucially never clips.
 *
 * We use `at-least` because the user's expectation is "closer than default
 * but not cut off". CSS line-height < 1.0 in the editor lets glyphs overlap
 * the line box; Word's `exact` renderer cannot — it truncates instead. The
 * `at-least` fallback is the cleanest way to map "tighter than default"
 * intent into a Word-renderable line height without losing visible glyphs.
 *
 * Tradeoff (accepted): editor values below the font's natural single-line
 * height (~1.2 × font) all collapse to single in Word. Loose values are
 * faithful. To get tightly packed blocks in Word, set explicit paragraph
 * margins (TipTap ParagraphSpacing) — import also honours mirrored
 * `mso-margin-*-alt` via `normalizeParagraphMarginsForWord`.
 *
 * Any pre-existing `mso-line-height-rule` declaration is stripped first so
 * callers can't override us. Unit-bearing values (`px`, `pt`, `em`, `%`) are
 * left untouched on the assumption they were authored deliberately.
 *
 * @example
 * <p style="line-height: 1.0">x</p>  →  <p style="line-height:11pt;mso-line-height-rule:at-least">x</p>
 * <p style="line-height: 0.85">x</p> →  <p style="line-height:9.35pt;mso-line-height-rule:at-least">x</p>
 * <p style="line-height: 1.6">x</p>  →  <p style="line-height:17.6pt;mso-line-height-rule:at-least">x</p>
 */
function normalizeLineHeightForWord(
  html: string,
  bodyFontSizePt: number = BODY_FONT_SIZE_PT,
): string {
  return html.replace(/style="([^"]*)"/gi, (_match, style: string) => {
    const lhMatch =
      /(^|;)\s*line-height\s*:\s*([0-9]*\.?[0-9]+)\s*(?=;|$)/i.exec(style);
    if (!lhMatch) return `style="${style}"`;
    const numeric = parseFloat(lhMatch[2]);
    if (!Number.isFinite(numeric) || numeric <= 0) return `style="${style}"`;
    const pt = `${formatPt(numeric * bodyFontSizePt)}pt`;
    const next = style
      .replace(
        /(^|;)\s*line-height\s*:\s*[0-9]*\.?[0-9]+\s*(;|$)/i,
        `$1line-height:${pt}$2`,
      )
      // Strip any pre-existing rule so we always end up with `:at-least` exactly once.
      .replace(/(^|;)\s*mso-line-height-rule\s*:\s*[a-z-]+\s*(;|$)/i, "$1$2")
      .replace(/;\s*;/g, ";")
      .replace(/^\s*;\s*/, "")
      .replace(/\s*;\s*$/, "")
      .trim();
    const trailing = next.length === 0 ? "" : ";";
    return `style="${next}${trailing}mso-line-height-rule:at-least"`;
  });
}

/**
 * Duplicate `margin-top` / `margin-bottom` into `mso-margin-top-alt` /
 * `mso-margin-bottom-alt` on block tags Word maps to paragraphs.
 *
 * Word's altChunk importer often substitutes "Auto" paragraph spacing for
 * HTML not produced by Office, which ignores or overrides plain CSS margins.
 * Office persists paragraph before/after in these `mso-margin-*-alt` properties
 * (see Word "Paragraph Spacing" HTML notes), so mirroring makes editor
 * spacing survive the import.
 */
function normalizeParagraphMarginsForWord(html: string): string {
  return html.replace(
    /<(p|h[1-6])\b([^>]*)>/gi,
    (full, tag: string, attrs: string) => {
      const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(attrs);
      if (!styleMatch) return full;
      const style = styleMatch[1];
      const mt = /(?:^|;)\s*margin-top\s*:\s*([^;]+?)\s*(?=;|$)/i.exec(style);
      const mb = /(?:^|;)\s*margin-bottom\s*:\s*([^;]+?)\s*(?=;|$)/i.exec(
        style,
      );
      const additions: string[] = [];
      if (mt && !/(?:^|;)\s*mso-margin-top-alt\s*:/i.test(style)) {
        additions.push(`mso-margin-top-alt:${mt[1].trim()}`);
      }
      if (mb && !/(?:^|;)\s*mso-margin-bottom-alt\s*:/i.test(style)) {
        additions.push(`mso-margin-bottom-alt:${mb[1].trim()}`);
      }
      if (additions.length === 0) return full;
      const trimmed = style.trim();
      const sep = trimmed === "" ? "" : trimmed.endsWith(";") ? "" : ";";
      const newStyle = `${trimmed}${sep}${additions.join(";")}`;
      const newAttrs = attrs.replace(
        /\bstyle\s*=\s*"[^"]*"/i,
        `style="${newStyle}"`,
      );
      return `<${tag}${newAttrs}>`;
    },
  );
}

/**
 * Inject `&nbsp;` into empty `<p>` / `<div>` blocks so renderers don't
 * collapse them to zero height. Without this, manual blank lines the
 * user inserts via Enter (which TipTap serialises as `<p></p>`) disappear:
 *
 *   - Browser HTML rendering: empty `<p></p>` has no line-box (height 0)
 *     and adjacent paragraphs' `margin-bottom` collapses, so 5 empty
 *     paragraphs visually become a single ~0.75em gap.
 *   - Word `altChunk` import: empty paragraphs are dropped entirely.
 *
 * Used by Word export (via `prepareForWord`) and by the day-one Preview
 * and PDF render paths (so the Edit-tab gap is preserved everywhere).
 *
 * Treats blocks containing only whitespace, `<br>` tags, or empty inline
 * spans as empty. Skips blocks already converted to border-rule tables
 * (those are handled by `borderRuleBlocksToWordTables` earlier in the
 * Word pipeline; for non-Word renderers a styled empty `<p>` with a
 * `border-top`/`border-bottom` is intentionally a thin horizontal rule).
 */
export function preserveEmptyParagraphs(html: string): string {
  return html.replace(
    /<(p|div)\b([^>]*)>(\s*(?:<br\s*\/?>|<span[^>]*>\s*<\/span>|&nbsp;|\s)*)<\/\1>/gi,
    (match, tag: string, attrs: string, inner: string) => {
      // Manual page-break markers must stay EMPTY (MOR-911). Inflating one
      // with `&nbsp;` gives it a line-box (a stray blank line at the top of
      // the next PDF page) and — worse — stops `pageBreakDivsToWordBreaks`'
      // empty-div match, so the Word export silently drops the page break.
      // Round-tripped documents (templates extracted from previously
      // exported .docx files) arrive with the filler already inside the
      // marker, so strip it back out rather than just skipping.
      if (/\bdata-page-break\b/i.test(attrs)) {
        return `<${tag}${attrs}></${tag}>`;
      }
      // Already non-empty (contains an `&nbsp;` already) — leave it alone.
      if (/&nbsp;/i.test(inner)) return match;
      // Border-only blocks are handled separately (see comment above).
      if (/border-(?:top|bottom)\s*:/i.test(attrs)) return match;
      return `<${tag}${attrs}>&nbsp;</${tag}>`;
    },
  );
}

// ---------------------------------------------------------------------------
// Word export
// ---------------------------------------------------------------------------

function buildWordHtml(
  bodyHtml: string,
  filename: string,
  headerHtml?: string,
  footerHtml?: string,
  defaultFontFamily?: string | null,
  defaultLineHeight?: string | null,
  bodyTextColor?: string | null,
  defaultFontSize?: string | null,
): string {
  const fontFamily = resolveFontFamily(defaultFontFamily);
  const lineHeight = resolveLineHeight(defaultLineHeight);
  const color = resolveBodyTextColor(bodyTextColor);
  const fontSize = resolveFontSize(defaultFontSize);
  const normalizedHeaderHtml = headerHtml
    ? constrainWordLetterheadImages(headerHtml)
    : undefined;
  const normalizedFooterHtml = footerHtml
    ? constrainWordLetterheadImages(footerHtml)
    : undefined;

  const wordHeaderBlock = normalizedHeaderHtml
    ? `<div style="mso-element:header" id="h1">${normalizedHeaderHtml}</div>`
    : "";
  const wordFooterBlock = normalizedFooterHtml
    ? `<div style="mso-element:footer" id="f1">${normalizedFooterHtml}</div>`
    : "";
  const headerFooterDefinitions =
    wordHeaderBlock || wordFooterBlock
      ? `<table id="hrdftrtbl" border="0" cellspacing="0" cellpadding="0"><tr><td>${wordHeaderBlock}${wordFooterBlock}</td></tr></table>`
      : "";

  const headerRef = normalizedHeaderHtml ? "mso-header:h1;" : "";
  const footerRef = normalizedFooterHtml ? "mso-footer:f1;" : "";

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8">
  <title>${filename}</title>
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml>
  <![endif]-->
  <style>
    @page WordSection1 {
      size: 595.3pt 841.9pt;
      margin: 90pt 56pt 72pt 56pt;
      mso-header-margin: 14pt;
      mso-footer-margin: 14pt;
      ${headerRef}
      ${footerRef}
    }
    div.WordSection1 { page: WordSection1; }
    body {
      margin: 0; padding: 0;
      font-family: ${fontFamily};
      font-size: ${fontSize}; line-height: ${lineHeight}; color: ${color};
    }
    table { border-collapse: collapse; }
    img { border: 0; }
    /* Header/footer definitions rendered at full size in their zones */
    div#h1, div#f1 { width: ${WORD_PRINTABLE_WIDTH_PT}pt; max-width: ${WORD_PRINTABLE_WIDTH_PT}pt; margin: 0; padding: 0; }
    div#h1 img, div#f1 img { display: block; width: ${WORD_PRINTABLE_WIDTH_PT}pt; max-width: ${WORD_PRINTABLE_WIDTH_PT}pt; height: auto; }
    /* Collapse the in-body definition table so it takes zero vertical space */
    table#hrdftrtbl { margin: 0in 0in 0in 900in; border: none; }
    table#hrdftrtbl td { border: none; padding: 0; }
    table#hrdftrtbl img { width: 1px !important; height: 1px !important; max-height: 1px !important; }
  </style>
</head>
<body>
  <div class="WordSection1" style="">${bodyHtml}${headerFooterDefinitions}</div>
</body>
</html>`;
}

/**
 * Word often ignores percentage-based CSS sizing for header/footer images and
 * falls back to the PNG's native dimensions. Constrain the image element
 * itself so letterhead snapshots render at the printable width.
 */
function constrainWordLetterheadImages(html: string): string {
  return html.replace(/<img\b([^>]*)>/gi, (_match, attrs: string) => {
    const attrsWithoutWidthOrHeight = attrs
      .replace(/\swidth="[^"]*"/gi, "")
      .replace(/\sheight="[^"]*"/gi, "");

    const styleMatch = /style="([^"]*)"/i.exec(attrsWithoutWidthOrHeight);
    const cleanedStyle = (styleMatch?.[1] ?? "")
      .replace(/display\s*:\s*[^;]+;?/gi, "")
      .replace(/width\s*:\s*[^;]+;?/gi, "")
      .replace(/max-width\s*:\s*[^;]+;?/gi, "")
      .replace(/height\s*:\s*[^;]+;?/gi, "")
      .replace(/;\s*;/g, ";")
      .replace(/^\s*;\s*/, "")
      .replace(/\s*;\s*$/, "")
      .trim();

    const attrsWithoutStyle = attrsWithoutWidthOrHeight
      .replace(/\sstyle="[^"]*"/i, "")
      .trim();

    const normalizedStyle = [
      "display:block",
      `width:${WORD_PRINTABLE_WIDTH_PT}pt`,
      `max-width:${WORD_PRINTABLE_WIDTH_PT}pt`,
      "height:auto",
      cleanedStyle,
    ]
      .filter(Boolean)
      .join(";");

    return `<img ${attrsWithoutStyle} width="${WORD_PRINTABLE_WIDTH_PX}" style="${normalizedStyle}" />`;
  });
}

/**
 * Replace user-inserted page-break markers (`<div data-page-break>`) with a
 * page break Word's altChunk importer actually honours.
 *
 * Word maps CSS `page-break-before:always` on a BLOCK element onto that
 * paragraph's native `w:pageBreakBefore`, so the break is moved onto the
 * block that follows the marker. Nothing is added to the document — the
 * next paragraph simply starts a page.
 *
 * What does NOT work (MOR-911): emitting a bare
 *
 *   <br clear="all" style="page-break-before:always;mso-special-character:line-break;">
 *
 * between two block elements. Microsoft's documented idiom always carries
 * that `<br>` INSIDE a paragraph; standing alone between `</p>` and `<p>`
 * the importer discards it, so the .docx contained the break markup yet
 * Word rendered no page break at all. That wrapped form is kept below as
 * the fallback for a marker with no following block (end of document).
 */
function pageBreakDivsToWordBreaks(html: string): string {
  // Marker whose content is only invisible filler. TipTap serialises it
  // empty, but round-tripped documents carry `&nbsp;`-filled markers
  // (templates extracted from a previously exported .docx store that
  // inflated form), so the filler has to match too — otherwise the marker
  // survives into the altChunk as an inert div and the break disappears.
  const MARKER = String.raw`<div\b[^>]*\bdata-page-break\b[^>]*>(?:\s|&nbsp;|<br\s*/?>|<span\b[^>]*>(?:\s|&nbsp;)*<\/span>)*<\/div>`;

  // Block elements Word treats as paragraph-level, i.e. ones that can carry
  // `w:pageBreakBefore`.
  const NEXT_BLOCK = String.raw`(\s*)<(p|h[1-6]|div|blockquote|ul|ol|table)\b([^>]*)>`;

  const withPageBreaksBeforeBlocks = html.replace(
    new RegExp(MARKER + NEXT_BLOCK, "gi"),
    (_match: string, gap: string, tag: string, attrs: string) =>
      gap + appendStyle(tag, attrs, ["page-break-before:always"]),
  );

  // Any marker left over has no following block — fall back to Microsoft's
  // wrapped line-break idiom. The paragraph is sized down to 1pt so it adds
  // as little leading as possible at the top of the new page.
  const result = withPageBreaksBeforeBlocks.replace(
    new RegExp(MARKER, "gi"),
    '<p style="margin:0;font-size:1pt;line-height:1pt">' +
      '<br clear="all" style="page-break-before:always;mso-special-character:line-break;" />' +
      "</p>",
  );

  return result;
}

/**
 * Stamp the body default `font-size` onto every `<td>` / `<th>` that
 * doesn't already declare one inline (MOR-424).
 *
 * Word's altChunk converter does NOT propagate the `body { font-size }`
 * CSS rule into table cells — they fall back to Word's own ~11pt default
 * regardless of what the body says. The PDF flow doesn't have this problem
 * because the outermost wrapper carries the font-size inline, and CSS
 * inheritance flows it into every descendant cell. To match that behaviour
 * for Word, we add the body default to bare cells before altChunk runs.
 *
 * Cells whose inline style already contains a `font-size:` declaration are
 * left untouched — author-set sizes always win.
 */
function stampBodyFontSizeOnBareCells(
  html: string,
  defaultFontSize: string | null | undefined,
): string {
  const fontSize = (defaultFontSize ?? "").trim();
  if (!fontSize) return html;
  return html.replace(
    /<(td|th)\b([^>]*)>/gi,
    (match, tag: string, attrs: string) => {
      const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(attrs);
      if (styleMatch && /(?:^|;)\s*font-size\s*:/i.test(styleMatch[1])) {
        return match;
      }
      if (styleMatch) {
        const newAttrs = attrs.replace(
          /\bstyle\s*=\s*"/i,
          `style="font-size:${fontSize};`,
        );
        return `<${tag}${newAttrs}>`;
      }
      return `<${tag}${attrs} style="font-size:${fontSize}">`;
    },
  );
}

/**
 * Convert a CSS spacing value to absolute `pt` form for Word.
 *
 * Word's altChunk importer is only reliable with absolute units on paragraph
 * margins; `em` values (used by the preview's `0.75em` paragraph default)
 * are resolved against the body font size. Unitless numbers are treated as
 * `pt`. Values that already carry another unit (`pt`, `px`, `%`) pass
 * through unchanged.
 */
function cssSpacingToPt(value: string, fontSizePtValue: number): string {
  const trimmed = value.trim();
  const emMatch = /^([0-9]*\.?[0-9]+)em$/i.exec(trimmed);
  if (emMatch) {
    return `${formatPt(parseFloat(emMatch[1]) * fontSizePtValue)}pt`;
  }
  if (/^([0-9]*\.?[0-9]+)$/.test(trimmed)) {
    return `${formatPt(parseFloat(trimmed))}pt`;
  }
  return trimmed;
}

/** Does an inline style declare the given margin side (longhand or shorthand)? */
function styleDeclaresMargin(style: string, side: "top" | "bottom"): boolean {
  return new RegExp(`(?:^|;)\\s*margin(?:-${side})?\\s*:`, "i").test(style);
}

/**
 * Is the `<p>` whose content starts at `contentStart` the last block inside
 * its `<li>`? (`<p>` can't nest, so the next `</p>` closes it.)
 *
 * Mirrors the `li > p:last-child` bottom-margin reset in `buildLetterBodyCss`:
 * the item's last block takes its spacing from the `<li>`, but a block
 * followed by a sibling — a nested bullet list under a numbered line, or a
 * second paragraph — keeps its own bottom margin.
 */
function isLastParagraphInListItem(
  html: string,
  contentStart: number,
): boolean {
  const close = /<\/p\s*>/i.exec(html.slice(contentStart));
  if (!close) return true;
  const rest = html.slice(contentStart + close.index + close[0].length);
  return rest.trim() === "" || /^\s*<\/li\b/i.test(rest);
}

/** Append declarations to a tag's style attribute (creating one if absent). */
function appendStyle(tag: string, attrs: string, additions: string[]): string {
  if (additions.length === 0) return `<${tag}${attrs}>`;
  const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(attrs);
  if (!styleMatch) {
    return `<${tag}${attrs} style="${additions.join(";")}">`;
  }
  const existing = styleMatch[1].trim();
  const sep = existing === "" || existing.endsWith(";") ? "" : ";";
  const newAttrs = attrs.replace(
    /\bstyle\s*=\s*"[^"]*"/i,
    `style="${existing}${sep}${additions.join(";")}"`,
  );
  return `<${tag}${newAttrs}>`;
}

/**
 * Stamp the preview's paragraph-spacing model inline so Word's altChunk
 * renders it (MOR-778).
 *
 * The browser preview / PDF pipeline styles paragraphs via
 * `buildLetterBodyCss`:
 *
 *   .letter-body p                  { margin: 0 0 0.75em 0; }
 *   .letter-body td p, th p         { margin: 0; }
 *   .letter-body li > p             { margin-top: 0; line-height: <body>; }
 *   .letter-body li > p:last-child  { margin-bottom: 0; }
 *   .letter-body h1..h4             { margin: 1em 0 0.5em 0; }
 *   p:not([style*="margin-top"])    { margin-top: <org default>; }    (etc.)
 *
 * None of that reaches the Word altChunk: `wrapBodyHtml` emits no `p` rule
 * at all, and the org-default rules use `:not([style*=…])` attribute
 * selectors that Word's HTML importer (a CSS2-era engine) ignores entirely.
 * Word therefore falls back to its "Normal (Web)" paragraph style — ~10pt
 * spacing before AND after every paragraph, including paragraphs inside
 * table cells — which blows out letters vertically versus the preview
 * (Shaw Gidley's report: "formatting changes and spacing is affected",
 * suspected "table or header").
 *
 * This transform walks the fragment tracking `<td>/<th>` and `<li>` nesting
 * and stamps explicit `margin-top` / `margin-bottom` (in `pt`) on every
 * `<p>` / `<h1>`–`<h6>` that doesn't already declare that side inline:
 *
 *   - paragraphs in table cells / list items → 0 (both sides)
 *   - other paragraphs → org spacing defaults, falling back to 0 / 0.75em
 *   - headings → org spacing defaults, falling back to 1em / 0.5em
 *
 * Cell/list paragraphs additionally get the body default `line-height`
 * (Word does not inherit body line-height into table cells, mirroring the
 * font-size behaviour fixed in MOR-424); the later
 * `normalizeLineHeightForWord` pass converts it to `pt` + `at-least`.
 * `normalizeParagraphMarginsForWord` then mirrors the stamped margins into
 * their `mso-margin-*-alt` twins, so this must run BEFORE both.
 */
function stampWordParagraphSpacing(
  html: string,
  opts?: {
    defaultFontSize?: string | null;
    defaultLineHeight?: string | null;
    paragraphSpacingBefore?: string | null;
    paragraphSpacingAfter?: string | null;
  },
): string {
  const fontSizePtValue = fontSizePt(opts?.defaultFontSize);
  const before = resolveParagraphSpacing(opts?.paragraphSpacingBefore);
  const after = resolveParagraphSpacing(opts?.paragraphSpacingAfter);
  const lineHeight = resolveLineHeight(opts?.defaultLineHeight);

  const pTop = cssSpacingToPt(before || "0", fontSizePtValue);
  const pBottom = cssSpacingToPt(after || "0.75em", fontSizePtValue);
  const hTop = cssSpacingToPt(before || "1em", fontSizePtValue);
  const hBottom = cssSpacingToPt(after || "0.5em", fontSizePtValue);

  const tagPattern = /<\/?(td|th|li|p|h[1-6])\b[^>]*>/gi;
  let cellDepth = 0;
  let liDepth = 0;
  let result = "";
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = tagPattern.exec(html)) !== null) {
    const tagHtml = match[0];
    const tagName = match[1].toLowerCase();
    const isClosing = tagHtml.startsWith("</");

    result += html.slice(cursor, match.index);
    cursor = match.index + tagHtml.length;

    if (tagName === "td" || tagName === "th") {
      cellDepth = Math.max(0, cellDepth + (isClosing ? -1 : 1));
      result += tagHtml;
      continue;
    }
    if (tagName === "li") {
      liDepth = Math.max(0, liDepth + (isClosing ? -1 : 1));
      result += tagHtml;
      continue;
    }
    if (isClosing) {
      result += tagHtml;
      continue;
    }

    const attrs = tagHtml.slice(1 + tagName.length, -1);
    const style = /\bstyle\s*=\s*"([^"]*)"/i.exec(attrs)?.[1] ?? "";
    const additions: string[] = [];
    const inTightContext = cellDepth > 0 || liDepth > 0;

    if (tagName === "p") {
      // A paragraph followed by a sibling inside the same <li> keeps its
      // bottom spacing, matching the `li > p:last-child` reset in the
      // preview/PDF CSS. Paragraphs in table cells stay tight on both sides
      // (`.letter-body td p { margin: 0 }`).
      const keepsBottomSpacing =
        liDepth > 0 &&
        cellDepth === 0 &&
        !isLastParagraphInListItem(html, cursor);
      if (!styleDeclaresMargin(style, "top")) {
        additions.push(`margin-top:${inTightContext ? "0pt" : pTop}`);
      }
      if (!styleDeclaresMargin(style, "bottom")) {
        additions.push(
          `margin-bottom:${inTightContext && !keepsBottomSpacing ? "0pt" : pBottom}`,
        );
      }
      if (inTightContext && !/(?:^|;)\s*line-height\s*:/i.test(style)) {
        additions.push(`line-height:${lineHeight}`);
      }
    } else {
      // Headings keep their heading spacing everywhere (the preview's
      // `td p` zero-margin rule targets paragraphs only).
      if (!styleDeclaresMargin(style, "top")) {
        additions.push(`margin-top:${hTop}`);
      }
      if (!styleDeclaresMargin(style, "bottom")) {
        additions.push(`margin-bottom:${hBottom}`);
      }
    }

    result += appendStyle(tagName, attrs, additions);
  }
  result += html.slice(cursor);
  return result;
}

/**
 * Rewrite fixed-width manual list-marker boxes (MOR-977, see
 * `manual-list-markers.ts`) into `marker + tab` for Word's HTML importer.
 * The `mso-tab-count` span is Word's own tab idiom — combined with the
 * paragraph's preserved hanging indent it aligns the clause text at the
 * hanging position exactly like the browser rendering of the box.
 */
export function listMarkerSpansToWordTabs(html: string): string {
  if (!html.includes('data-type="list-marker"')) return html;
  return html.replace(
    /<span\b([^>]*)\bdata-type="list-marker"([^>]*)>([\s\S]*?)<\/span>/gi,
    (_match, before: string, after: string, marker: string) => {
      // A box wider than one default tab stop (36pt) stands in for several
      // tabs (a "LABEL:⇥⇥value" column from a Word upload), so emit as many
      // tabs as the width implies. Hanging-indent list markers are ≤36pt and
      // keep the single tab that lands on the hanging position.
      const style = /style="([^"]*)"/i.exec(`${before}${after}`)?.[1] ?? "";
      const minWidth = /min-width\s*:\s*(-?\d*\.?\d+)(pt|px)?/i.exec(style);
      const tabs = minWidth
        ? (() => {
            const value = parseFloat(minWidth[1]);
            const pt =
              (minWidth[2] ?? "pt").toLowerCase() === "px"
                ? value * 0.75
                : value;
            return Math.max(1, Math.ceil(pt / 36 - 0.05));
          })()
        : 1;
      return `${marker}<span style='mso-tab-count:${tabs}'>&#9;</span>`;
    },
  );
}

/**
 * Full preparation pipeline for Word HTML fragments.
 * Fixes image MIMEs, converts <hr> to tables, strips Word-incompatible
 * CSS, and neutralizes default table borders.
 *
 * `defaultFontSize` (when provided) is stamped onto bare `<td>` / `<th>`
 * cells so Word's altChunk renders cell text at the body default — Word's
 * importer does NOT inherit `body { font-size }` into table cells, which
 * leaves them rendering at Word's own ~11pt default and visibly larger
 * than the rest of the body (MOR-424).
 */
function prepareForWord(
  html: string,
  defaultFontSize?: string | null,
  spacingOpts?: {
    defaultLineHeight?: string | null;
    paragraphSpacingBefore?: string | null;
    paragraphSpacingAfter?: string | null;
  },
): string {
  const withoutWrapperStyles = stripBrowserOnlyWrapperStyles(html);
  // Manual list-marker boxes (MOR-977) → marker + real tab. Word ignores
  // `display:inline-block`/`min-width` on spans, so the box would collapse
  // onto the clause text; the paragraph keeps its margin-left/text-indent
  // hanging indent, and Word puts an implicit tab stop at the hanging
  // position, so a tab reproduces the alignment natively.
  const withWordTabs = listMarkerSpansToWordTabs(withoutWrapperStyles);
  // Match the editor's table sizing BEFORE convertCssToWordAttributes turns
  // `margin-left:auto` styles into align="right" attributes (the stretch
  // must still see the original style to skip right-aligned tables).
  const withNormalizedTableWidths = normalizeTableWidthsForOutput(withWordTabs);
  // Rewrite manual page breaks BEFORE sanitiser strips unknown attributes.
  const withPageBreaks = pageBreakDivsToWordBreaks(withNormalizedTableWidths);
  const withHrTables = hrToWordTable(withPageBreaks);
  const withBorderRuleTables = borderRuleBlocksToWordTables(withHrTables);
  const withFixedDataUriMimeTypes = fixDataUriMimeTypes(withBorderRuleTables);
  const withWordCssAttributes = convertCssToWordAttributes(
    withFixedDataUriMimeTypes,
  );
  // Stamp the preview's paragraph-spacing model inline (MOR-778) BEFORE the
  // line-height and margin normalisers below, so stamped unitless
  // line-heights are converted to pt and stamped margins get their
  // mso-margin-*-alt mirrors.
  const withParagraphSpacing = stampWordParagraphSpacing(
    withWordCssAttributes,
    {
      defaultFontSize,
      defaultLineHeight: spacingOpts?.defaultLineHeight,
      paragraphSpacingBefore: spacingOpts?.paragraphSpacingBefore,
      paragraphSpacingAfter: spacingOpts?.paragraphSpacingAfter,
    },
  );
  // Word's altChunk drops unitless line-height multipliers and collapses
  // empty paragraphs — fix both before sanitiseForWord touches inline styles.
  // Unitless multipliers convert against the resolved body font size so the
  // per-paragraph values agree with the body default emitted by
  // `wrapBodyHtml` (which already scales by the branding font size).
  const withNormalizedLineHeight = normalizeLineHeightForWord(
    withParagraphSpacing,
    fontSizePt(defaultFontSize),
  );
  const withNormalizedParagraphMargins = normalizeParagraphMarginsForWord(
    withNormalizedLineHeight,
  );
  const withPreservedEmptyParagraphs = preserveEmptyParagraphs(
    withNormalizedParagraphMargins,
  );
  // TipTap's TableKit auto-generates min-width-only colgroups every save;
  // Word coerces those into 25px fixed columns that break the table
  // layout (MOR-425). Strip before sanitiser/neutraliser run so they
  // don't re-emit them.
  const withoutAutoGeneratedColgroups = stripAutoGeneratedColgroups(
    withPreservedEmptyParagraphs,
  );
  const sanitizedForWord = sanitizeForWord(withoutAutoGeneratedColgroups);
  const withNeutralizedTableBorders = neutralizeTableBorders(sanitizedForWord);
  const result = stampBodyFontSizeOnBareCells(
    withNeutralizedTableBorders,
    defaultFontSize,
  );
  return result;
}

/**
 * Strip padding, margin:auto, and max-width from the outermost wrapper div.
 * These are for browser preview layout and conflict with Word's @page margins,
 * wasting vertical space that can push content to a second page.
 */
function stripBrowserOnlyWrapperStyles(html: string): string {
  const outerDivMatch = /^(<div\b[^>]*style=")([^"]*)(")/i.exec(
    html.trimStart(),
  );
  if (!outerDivMatch) return html;

  const prefix = html.slice(0, html.indexOf(outerDivMatch[0]));
  const cleaned = outerDivMatch[2]
    .replace(/padding\s*:[^;]+;?/gi, "")
    .replace(/max-width\s*:[^;]+;?/gi, "")
    .replace(/margin\s*:\s*0\s+auto\s*;?/gi, "")
    .replace(/;\s*;/g, ";")
    .replace(/^\s*;\s*/, "")
    .replace(/\s*;\s*$/, "")
    .trim();

  return `${prefix}${outerDivMatch[1]}${cleaned}${outerDivMatch[3]}${html.slice(html.indexOf(outerDivMatch[0]) + outerDivMatch[0].length)}`;
}

export const __wordExportTestUtils = {
  buildWordHtml,
  prepareForWord,
  pageBreakDivsToWordBreaks,
  stampWordParagraphSpacing,
  normalizeBodyImageDimensions,
  normalizeLineHeightForWord,
  normalizeParagraphMarginsForWord,
  preserveEmptyParagraphs,
  lineHeightToPt,
  neutralizeTableBorders,
  mirrorBordersToMsoAlt,
  stripAutoGeneratedColgroups,
  stampBodyFontSizeOnBareCells,
};

/** Test-only re-exports for the PDF print pipeline. */
export const __pdfExportTestUtils = {
  buildPrintCss,
  formatOrderedMarker,
  buildPrintMeasurementHtml,
  measurePrintLayout,
  verifyPaginationAfterFonts,
};

// ---------------------------------------------------------------------------
// .docx generation via altChunk (JSZip)
//
// The body HTML is embedded as an altChunk inside a minimal OOXML package.
// Word converts the HTML to native content on open, preserving all CSS
// formatting. Headers/footers are proper OOXML XML parts, so they repeat
// on every page without blank-page artifacts.
// ---------------------------------------------------------------------------

/** A4 in TWIP. */
const A4_W = 11906;
const A4_H = 16838;

/** Convert px padding to TWIP for Word page margins (1px = 15 TWIP). */
function pxToTwip(px: number): number {
  return Math.round(px * 15);
}

/** CSS px (96 DPI) → EMU (914400/inch): px × 9525. */
function pxToEmu(px: number): number {
  return Math.round(px * 9525);
}

/**
 * Derive Word page margins from DocumentPadding.
 *
 * Word's altChunk import honours w:pgMar for page-level margins but
 * strips CSS padding from the embedded HTML body. All spacing must
 * come from the OOXML sectPr values.
 *
 * `w:pgMar` is uniform per section. When a header/footer is restricted
 * to specific pages (any rule other than "all"), the section margin
 * cannot include the zone height — otherwise the no-zone pages get a
 * giant whitespace where the header/footer would have been.
 *
 * For non-"all" rules we set the margin to the small no-zone baseline
 * and rely on Word's natural behaviour: when the header (anchored at
 * `w:header`) extends below `w:top`, Word auto-pushes body content down
 * to make room. The configured `bodyTop` gap between header and body is
 * baked into the header's image XML via `w:spacing w:after` (and
 * mirrored for the footer with `w:before`). The EMPTY header/footer
 * parts referenced by the no-zone pages are in turn sized to the PDF
 * paginator's ~1-inch fallback so those pages never start body text at
 * the raw page edge when the baseline is tiny (MOR-911) — see
 * `buildEmptyPartXml`.
 */
function wordMarginsFromPadding(
  padding: DocumentPadding,
  hasHeader: boolean,
  hasFooter: boolean,
  headerHeightTwip = 0,
  footerHeightTwip = 0,
  headerPageRule: PageRule = "all",
  footerPageRule: PageRule = "all",
  leftSideWidthTwip = 0,
  rightSideWidthTwip = 0,
): {
  top: number;
  bottom: number;
  left: number;
  right: number;
  header: number;
  footer: number;
  /**
   * Symmetric baseline side margin (the smaller of the configured zones).
   * Exposed for callers / tests that previously read `.side`.
   */
  side: number;
} {
  // Each edge takes the SMALLEST of the three zones' margins on that edge —
  // the section margin is the common inset, and any zone that wants more is
  // padded inside its own part (header/footer indents, body spacer table).
  const leftBase = pxToTwip(
    Math.min(padding.headerLeft, padding.bodyLeft, padding.footerLeft),
  );
  const rightBase = pxToTwip(
    Math.min(padding.headerRight, padding.bodyRight, padding.footerRight),
  );
  const side = Math.min(leftBase, rightBase);

  const headerOnEveryPage = hasHeader && headerPageRule === "all";
  const footerOnEveryPage = hasFooter && footerPageRule === "all";

  // When side images are present, the body must inset far enough that text
  // never overlaps the side art. The side image's full width (in twips) is
  // baked into the section's left/right margins. Sides are anchored to the
  // page so they paint on every page that has them, independent of body
  // flow — Word's pgMar just keeps the body story clear of the image.
  const left = Math.max(leftBase, leftSideWidthTwip);
  const right = Math.max(rightBase, rightSideWidthTwip);

  return {
    side,
    left,
    right,
    top: headerOnEveryPage
      ? pxToTwip(padding.headerTop) +
        headerHeightTwip +
        pxToTwip(padding.bodyTop)
      : side,
    bottom: footerOnEveryPage
      ? pxToTwip(padding.footerBottom) +
        footerHeightTwip +
        pxToTwip(padding.bodyBottom)
      : side + pxToTwip(padding.bodyBottom),
    header: pxToTwip(padding.headerTop),
    footer: pxToTwip(padding.footerBottom),
  };
}

/** EMU per inch (914400). */
const EMU_PER_INCH = 914400;

/**
 * Fetch a remote image and return its raw bytes + natural dimensions.
 *
 * Memoised by URL — Word exports re-fetch the same letterhead header/footer
 * for every document in a bulk run (MOR-971). Failed fetches are not cached
 * so transient errors can recover; the buffer is only ever read by
 * consumers, so sharing one ArrayBuffer is safe.
 */
const imageBufferCache = new Map<
  string,
  Promise<{
    image: { buf: ArrayBuffer; w: number; h: number } | null;
    debug: WordExportAssetDebugInfo;
  }>
>();

function fetchImageBuffer(url: string): Promise<{
  image: { buf: ArrayBuffer; w: number; h: number } | null;
  debug: WordExportAssetDebugInfo;
}> {
  const cached = imageBufferCache.get(url);
  if (cached) return cached;
  const promise = fetchImageBufferUncached(url).then((result) => {
    if (result.image === null) imageBufferCache.delete(url);
    return result;
  });
  imageBufferCache.set(url, promise);
  return promise;
}

async function fetchImageBufferUncached(url: string): Promise<{
  image: { buf: ArrayBuffer; w: number; h: number } | null;
  debug: WordExportAssetDebugInfo;
}> {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      return {
        image: null,
        debug: {
          requestedUrl: url,
          fetched: false,
          error: `HTTP ${res.status}`,
        },
      };
    }
    const blob = await res.blob();
    const buf = await blob.arrayBuffer();
    const dims = await new Promise<{ w: number; h: number }>((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ w: img.naturalWidth, h: img.naturalHeight });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        resolve({ w: 800, h: 100 });
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(blob);
    });
    return {
      image: { buf, ...dims },
      debug: {
        requestedUrl: url,
        fetched: true,
        width: dims.w,
        height: dims.h,
        error: null,
      },
    };
  } catch (error) {
    return {
      image: null,
      debug: {
        requestedUrl: url,
        fetched: false,
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }
}

/** Convert CSS px units to Word points. */
function pxToPt(px: number): number {
  return (px * 72) / 96;
}

/**
 * Resolve the printable width available to a zone after applying its own
 * left/right padding. Header and footer snapshots are rendered as centered
 * images, so their visible side padding comes from reducing image width.
 */
function printableWidthInFromSidePadding(
  /** Zone side margin (px) — a single symmetric value or a left/right pair. */
  sidePaddingPx: number | { left: number; right: number },
  /** Combined width (px) of full-height side bands the zone sits beside. */
  sideBandsWidthPx = 0,
): number {
  const sidesPx =
    typeof sidePaddingPx === "number"
      ? sidePaddingPx * 2
      : sidePaddingPx.left + sidePaddingPx.right;
  const printableWidthPt = Math.max(
    WORD_PAGE_WIDTH_PT - pxToPt(sideBandsWidthPx) - pxToPt(sidesPx),
    0,
  );
  return printableWidthPt / 72;
}

/** Convert an image aspect ratio and target width to Word twips. */
function imageHeightTwipFromWidthIn(
  widthIn: number,
  naturalWidth: number,
  naturalHeight: number,
): number {
  if (naturalWidth <= 0 || naturalHeight <= 0 || widthIn <= 0) {
    return 0;
  }

  const widthPt = widthIn * 72;
  const heightPt = widthPt * (naturalHeight / naturalWidth);
  return Math.round(heightPt * 20);
}

/**
 * Wrap imported body content in a table cell inset when the body needs wider
 * side padding than the header/footer zones. Word's section margins apply to
 * the whole section, so body-only side padding must be modelled inside the
 * imported HTML instead of `w:pgMar`.
 */
function wrapBodyContentForDocx(
  bodyHtml: string,
  extraBodyLeftPx: number,
  extraBodyRightPx: number = extraBodyLeftPx,
): string {
  const left = Math.max(0, extraBodyLeftPx);
  const right = Math.max(0, extraBodyRightPx);
  if (left <= 0 && right <= 0) {
    return bodyHtml;
  }

  const spacer = (px: number) =>
    px > 0
      ? `<td width="${px}" style="width:${px}px;border:none;font-size:0;line-height:0;">&nbsp;</td>`
      : "";
  return `<table role="presentation" width="100%" style="width:100%;border-collapse:collapse;border:none;"><tr>${spacer(left)}<td style="border:none;vertical-align:top;">${bodyHtml}</td>${spacer(right)}</tr></table>`;
}

/**
 * Horizontal source crop for a PAGE-FIT header/footer image that sits beside
 * full-height side bands: the columns under each band are cropped away so
 * the remaining artwork fills the slot at page scale instead of being
 * squeezed into it. Fractions of the image width.
 */
interface ZoneBandCrop {
  left: number;
  right: number;
}

/** `<a:srcRect>` for a band crop (thousandths of a percent per edge). */
function zoneBandCropXml(crop: ZoneBandCrop | null | undefined): string {
  if (!crop || (crop.left <= 0 && crop.right <= 0)) return "";
  const pct1000 = (v: number) => Math.round(Math.max(0, v) * 100000);
  return `<a:srcRect l="${pct1000(crop.left)}" r="${pct1000(crop.right)}"/>`;
}

/**
 * Build a minimal OOXML header or footer XML part containing a single
 * inline image scaled to the printable width.
 *
 * `gapBeforeTwip` / `gapAfterTwip` add `<w:spacing>` to the image
 * paragraph so the configured body gap is preserved on zone-bearing
 * pages even when the section-level `w:pgMar` is set to the small
 * no-zone baseline (see `wordMarginsFromPadding`). For headers we use
 * `w:after` so the gap renders below the image (between header and
 * body); for footers we use `w:before` so the gap renders above the
 * image (between body and footer).
 *
 * `trailingParagraphsXml` is appended after the image paragraph — used for
 * the footer page-number line, which must sit below the letterhead.
 */
function buildImagePartXml(
  tag: "w:hdr" | "w:ftr",
  imgRelId: string,
  widthEmu: number,
  heightEmu: number,
  gapBeforeTwip = 0,
  gapAfterTwip = 0,
  indentTwip: { left: number; right: number } | null = null,
  trailingParagraphsXml = "",
  bandCrop: ZoneBandCrop | null = null,
  leadingParagraphsXml = "",
): string {
  const spacingAttrs: string[] = [];
  if (gapBeforeTwip > 0) spacingAttrs.push(`w:before="${gapBeforeTwip}"`);
  if (gapAfterTwip > 0) spacingAttrs.push(`w:after="${gapAfterTwip}"`);
  const spacingEl =
    spacingAttrs.length > 0
      ? `<w:spacing ${spacingAttrs.join(" ")} w:lineRule="auto"/>`
      : "";
  // Signed indents place the zone image at the zone's own left/right margins
  // relative to the section margins (see `zoneIndentTwip` in buildDocxBlob):
  // negative when the section margin is wider than the zone's, positive when
  // the zone's margin is wider.
  const indentEl = indentTwip
    ? `<w:ind w:left="${indentTwip.left}" w:right="${indentTwip.right}"/>`
    : "";

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<${tag}
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
  xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
  xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">
  ${leadingParagraphsXml}
  <w:p><w:pPr><w:jc w:val="center"/>${spacingEl}${indentEl}</w:pPr><w:r><w:drawing>
    <wp:inline distT="0" distB="0" distL="0" distR="0">
      <wp:extent cx="${widthEmu}" cy="${heightEmu}"/>
      <wp:docPr id="1" name="Letterhead"/>
      <a:graphic>
        <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">
          <pic:pic>
            <pic:nvPicPr><pic:cNvPr id="0" name="img"/><pic:cNvPicPr/></pic:nvPicPr>
            <pic:blipFill>
              <a:blip r:embed="${imgRelId}"/>
              ${zoneBandCropXml(bandCrop)}
              <a:stretch><a:fillRect/></a:stretch>
            </pic:blipFill>
            <pic:spPr>
              <a:xfrm><a:off x="0" y="0"/><a:ext cx="${widthEmu}" cy="${heightEmu}"/></a:xfrm>
              <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
            </pic:spPr>
          </pic:pic>
        </a:graphicData>
      </a:graphic>
    </wp:inline>
  </w:drawing></w:r></w:p>
  ${trailingParagraphsXml}
</${tag}>`;
}

/**
 * Build a header/footer part containing only paragraphs (no image) — the
 * page-number-without-letterhead case.
 */
function buildTextOnlyPartXml(
  tag: "w:hdr" | "w:ftr",
  paragraphsXml: string,
): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<${tag} xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  ${paragraphsXml}
</${tag}>`;
}

/**
 * Wrap body HTML in a full HTML document with default styles matching
 * the Word export conventions (font-family 11pt, line-height 1.6).
 * Side/top margins are handled by w:pgMar in the OOXML sectPr.
 *
 * The body-level `font-family` is what Word's altChunk import treats as
 * the document default — it's the reliable hook for honouring the org's
 * branding font (e.g. Open Sans). Without it Word coerces every run to
 * Arial regardless of inline `<span style="font-family:...">` markup.
 */
function wrapBodyHtml(
  bodyHtml: string,
  extraBodySidePx: number | { left: number; right: number } = 0,
  defaultFontFamily?: string | null,
  defaultLineHeight?: string | null,
  bodyTextColor?: string | null,
  defaultFontSize?: string | null,
  defaultParagraphSpacingBefore?: string | null,
  defaultParagraphSpacingAfter?: string | null,
): string {
  const extra =
    typeof extraBodySidePx === "number"
      ? { left: extraBodySidePx, right: extraBodySidePx }
      : extraBodySidePx;
  const wrappedBodyHtml = wrapBodyContentForDocx(
    bodyHtml,
    extra.left,
    extra.right,
  );
  const fontFamily = resolveFontFamily(defaultFontFamily);
  const fontSize = resolveFontSize(defaultFontSize);
  const fontSizePtValue = fontSizePt(defaultFontSize);
  // Word's altChunk import is unreliable with unitless line-height; convert
  // to absolute `pt` (assuming the resolved body font size) so per-paragraph
  // overrides emitted by `normalizeLineHeightForWord` and the body default
  // agree on units. Pair with `mso-line-height-rule:at-least` so Word uses
  // `lineRule="atLeast"` — loose values render literally (faithful to the
  // editor) while tight values fall back to natural single-line height
  // instead of clipping descenders. See `normalizeLineHeightForWord` for the
  // full rationale.
  const lineHeight = lineHeightToPt(
    resolveLineHeight(defaultLineHeight),
    fontSizePtValue,
  );
  const color = resolveBodyTextColor(bodyTextColor);
  const spacingBefore = resolveParagraphSpacing(defaultParagraphSpacingBefore);
  const spacingAfter = resolveParagraphSpacing(defaultParagraphSpacingAfter);
  const paragraphDefaultsCss = buildParagraphSpacingDefaultsCss(
    spacingBefore,
    spacingAfter,
  );
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<style>
  body { margin:0; padding:0; font-family:${fontFamily}; font-size:${fontSize}; line-height:${lineHeight}; mso-line-height-rule:at-least; color:${color}; }
  table { border-collapse:collapse; }
  img { max-width:100%; height:auto; }
  td { vertical-align:top; }
  ${paragraphDefaultsCss}
</style>
</head><body>${wrappedBodyHtml}</body></html>`;
}

/**
 * Emit CSS rules that apply org-default paragraph spacing to body paragraphs
 * and headings that don't already carry an inline `margin-top` / `margin-bottom`
 * override. Returns an empty string when neither default is set so the rule
 * block stays clean.
 */
function buildParagraphSpacingDefaultsCss(
  spacingBefore: string,
  spacingAfter: string,
): string {
  const rules: string[] = [];
  const selectors = [
    `p:not([style*="margin-top"])`,
    `h1:not([style*="margin-top"])`,
    `h2:not([style*="margin-top"])`,
    `h3:not([style*="margin-top"])`,
    `h4:not([style*="margin-top"])`,
    `h5:not([style*="margin-top"])`,
    `h6:not([style*="margin-top"])`,
  ];
  const bottomSelectors = selectors.map((s) =>
    s.replace("margin-top", "margin-bottom"),
  );
  if (spacingBefore) {
    rules.push(`${selectors.join(", ")} { margin-top: ${spacingBefore}; }`);
  }
  if (spacingAfter) {
    rules.push(
      `${bottomSelectors.join(", ")} { margin-bottom: ${spacingAfter}; }`,
    );
  }
  return rules.join("\n  ");
}

/**
 * An empty OOXML header/footer part (single blank paragraph).
 *
 * `minExtentTwip` (MOR-911): when > 0, the blank paragraph is given that
 * exact line height so the zone still RESERVES vertical space on the pages
 * that show it. Word computes the body's top/bottom boundary from
 * `max(pgMar, zone position + zone content height)`; with the no-zone
 * page margin set to the small side baseline (see `wordMarginsFromPadding`)
 * and a zero-height empty part, body text on header/footer-less pages
 * starts at the literal page edge when the branding's paddings are small
 * (Beacon's full-bleed letterhead uses 0). The browser PDF paginator
 * reserves ~1 inch on such pages (`NO_HEADER_FALLBACK_PX`); sizing the
 * empty part keeps Word consistent without touching zone-bearing pages.
 */
function buildEmptyPartXml(tag: "w:hdr" | "w:ftr", minExtentTwip = 0): string {
  const pPr =
    minExtentTwip > 0
      ? `<w:pPr><w:pStyle w:val="Normal"/><w:spacing w:before="0" w:after="0" w:line="${minExtentTwip}" w:lineRule="exact"/><w:rPr><w:sz w:val="2"/></w:rPr></w:pPr>`
      : `<w:pPr><w:pStyle w:val="Normal"/></w:pPr>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<${tag} xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:p>${pPr}</w:p>
</${tag}>`;
}

/**
 * Build a single OOXML run containing an anchored DrawingML image
 * positioned absolutely relative to the page. Used to place the
 * left/right side letterhead images on top/bottom edges of every page
 * that references the containing header part.
 *
 * The image is rendered `behindDoc="1"` so body text flows over it
 * cleanly when the side margins are not wide enough — but in practice
 * the section's `w:pgMar w:left` / `w:right` is widened to fully clear
 * the side image (see `wordMarginsFromPadding`).
 *
 * When `wrapSquare` is set (partial-height "topBlock" sides), the image is
 * instead anchored IN FRONT of the text stack with square wrapping, so
 * body text flows beside the block and then full-width below it — the
 * section margins are NOT widened for these. Wrap distances reuse the
 * same gutters as the HTML wrap spacer so Word output matches the PDF.
 */
function buildAnchoredSideImageRun(opts: {
  imgRelId: string;
  widthEmu: number;
  heightEmu: number;
  pageWidthEmu: number;
  position: "left" | "right";
  docPrId: number;
  name: string;
  wrapSquare?: boolean;
  /** Optional source crop, applied natively via <a:srcRect>. */
  crop?: SideCrop;
}): string {
  const offsetXEmu =
    opts.position === "left" ? 0 : opts.pageWidthEmu - opts.widthEmu;
  const gutterXEmu = pxToEmu(TOP_BLOCK_WRAP_GUTTER_X_PX);
  const gutterYEmu = pxToEmu(TOP_BLOCK_WRAP_GUTTER_Y_PX);
  // Square wrap: keep text off the block's inner edge and bottom edge.
  const distL = opts.wrapSquare && opts.position === "right" ? gutterXEmu : 0;
  const distR = opts.wrapSquare && opts.position === "left" ? gutterXEmu : 0;
  const distB = opts.wrapSquare ? gutterYEmu : 0;
  const wrapXml = opts.wrapSquare
    ? `<wp:wrapSquare wrapText="${opts.position === "right" ? "left" : "right"}"/>`
    : "<wp:wrapNone/>";
  // OOXML srcRect crops in thousandths of a percent per edge.
  const crop = opts.crop ?? EMPTY_SIDE_CROP;
  const pct1000 = (v: number) => Math.round(v * 100000);
  const srcRectXml = sideCropIsActive(crop)
    ? `<a:srcRect l="${pct1000(crop.left)}" t="${pct1000(crop.top)}" r="${pct1000(crop.right)}" b="${pct1000(crop.bottom)}"/>`
    : "";
  return `<w:r><w:drawing>
    <wp:anchor distT="0" distB="${distB}" distL="${distL}" distR="${distR}"
               relativeHeight="${opts.docPrId}" behindDoc="${opts.wrapSquare ? "0" : "1"}" locked="0"
               layoutInCell="1" allowOverlap="1" simplePos="0">
      <wp:simplePos x="0" y="0"/>
      <wp:positionH relativeFrom="page"><wp:posOffset>${offsetXEmu}</wp:posOffset></wp:positionH>
      <wp:positionV relativeFrom="page"><wp:posOffset>0</wp:posOffset></wp:positionV>
      <wp:extent cx="${opts.widthEmu}" cy="${opts.heightEmu}"/>
      <wp:effectExtent l="0" t="0" r="0" b="0"/>
      ${wrapXml}
      <wp:docPr id="${opts.docPrId}" name="${opts.name}"/>
      <wp:cNvGraphicFramePr/>
      <a:graphic>
        <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">
          <pic:pic>
            <pic:nvPicPr><pic:cNvPr id="0" name="${opts.name}"/><pic:cNvPicPr/></pic:nvPicPr>
            <pic:blipFill>
              <a:blip r:embed="${opts.imgRelId}"/>
              ${srcRectXml}
              <a:stretch><a:fillRect/></a:stretch>
            </pic:blipFill>
            <pic:spPr>
              <a:xfrm><a:off x="0" y="0"/><a:ext cx="${opts.widthEmu}" cy="${opts.heightEmu}"/></a:xfrm>
              <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
            </pic:spPr>
          </pic:pic>
        </a:graphicData>
      </a:graphic>
    </wp:anchor>
  </w:drawing></w:r>`;
}

/**
 * Build a full OOXML header part containing optional zones:
 *   - inline top-header image (centred)
 *   - anchored left-side full-height image
 *   - anchored right-side full-height image
 *
 * Any of the three zones may be absent. The header XML always emits
 * a single `<w:p>` paragraph so the part is structurally valid.
 */
function buildComposedHeaderPartXml(opts: {
  topHeader?: {
    imgRelId: string;
    widthEmu: number;
    heightEmu: number;
    gapAfterTwip: number;
    /**
     * Signed indent (twips per side) placing the inline image at the
     * header's own left/right margins relative to the section margins.
     */
    indentTwip?: { left: number; right: number } | null;
    /** Page-fit header beside side bands — see `ZoneBandCrop`. */
    bandCrop?: ZoneBandCrop | null;
  };
  leftSide?: {
    imgRelId: string;
    widthEmu: number;
    heightEmu: number;
    wrapSquare?: boolean;
    crop?: SideCrop;
  };
  rightSide?: {
    imgRelId: string;
    widthEmu: number;
    heightEmu: number;
    wrapSquare?: boolean;
    crop?: SideCrop;
  };
  pageWidthEmu: number;
  docPrSeed: number;
  /**
   * Exact line height (twips) for the part's paragraph when there is NO
   * inline top header — anchored side images contribute nothing to the
   * paragraph's line box, so without this a sides-only header part reserves
   * ~one blank line and body text starts at the page edge when the section
   * margins are small (MOR-911). Mirrors the PDF paginator's no-header
   * fallback reservation.
   */
  minLineTwipWhenNoTopHeader?: number;
}): string {
  const runs: string[] = [];
  const paragraphProps: string[] = [];

  if (
    !opts.topHeader &&
    opts.minLineTwipWhenNoTopHeader &&
    opts.minLineTwipWhenNoTopHeader > 0
  ) {
    paragraphProps.push(
      `<w:spacing w:before="0" w:after="0" w:line="${opts.minLineTwipWhenNoTopHeader}" w:lineRule="exact"/>`,
    );
  }

  if (opts.topHeader) {
    paragraphProps.push('<w:jc w:val="center"/>');
    if (opts.topHeader.gapAfterTwip > 0) {
      paragraphProps.push(
        `<w:spacing w:after="${opts.topHeader.gapAfterTwip}" w:lineRule="auto"/>`,
      );
    }
    if (opts.topHeader.indentTwip) {
      paragraphProps.push(
        `<w:ind w:left="${opts.topHeader.indentTwip.left}" w:right="${opts.topHeader.indentTwip.right}"/>`,
      );
    }
    runs.push(`<w:r><w:drawing>
      <wp:inline distT="0" distB="0" distL="0" distR="0">
        <wp:extent cx="${opts.topHeader.widthEmu}" cy="${opts.topHeader.heightEmu}"/>
        <wp:docPr id="${opts.docPrSeed + 1}" name="HeaderLetterhead"/>
        <a:graphic>
          <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">
            <pic:pic>
              <pic:nvPicPr><pic:cNvPr id="0" name="HeaderLetterhead"/><pic:cNvPicPr/></pic:nvPicPr>
              <pic:blipFill>
                <a:blip r:embed="${opts.topHeader.imgRelId}"/>
                ${zoneBandCropXml(opts.topHeader.bandCrop)}
                <a:stretch><a:fillRect/></a:stretch>
              </pic:blipFill>
              <pic:spPr>
                <a:xfrm><a:off x="0" y="0"/><a:ext cx="${opts.topHeader.widthEmu}" cy="${opts.topHeader.heightEmu}"/></a:xfrm>
                <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
              </pic:spPr>
            </pic:pic>
          </a:graphicData>
        </a:graphic>
      </wp:inline>
    </w:drawing></w:r>`);
  }

  if (opts.leftSide) {
    runs.push(
      buildAnchoredSideImageRun({
        imgRelId: opts.leftSide.imgRelId,
        widthEmu: opts.leftSide.widthEmu,
        heightEmu: opts.leftSide.heightEmu,
        pageWidthEmu: opts.pageWidthEmu,
        position: "left",
        docPrId: opts.docPrSeed + 2,
        name: "LeftSideLetterhead",
        wrapSquare: opts.leftSide.wrapSquare,
        crop: opts.leftSide.crop,
      }),
    );
  }

  if (opts.rightSide) {
    runs.push(
      buildAnchoredSideImageRun({
        imgRelId: opts.rightSide.imgRelId,
        widthEmu: opts.rightSide.widthEmu,
        heightEmu: opts.rightSide.heightEmu,
        pageWidthEmu: opts.pageWidthEmu,
        position: "right",
        docPrId: opts.docPrSeed + 3,
        name: "RightSideLetterhead",
        wrapSquare: opts.rightSide.wrapSquare,
        crop: opts.rightSide.crop,
      }),
    );
  }

  const pPr =
    paragraphProps.length > 0
      ? `<w:pPr>${paragraphProps.join("")}</w:pPr>`
      : "";
  const body =
    runs.length > 0
      ? `<w:p>${pPr}${runs.join("")}</w:p>`
      : '<w:p><w:pPr><w:pStyle w:val="Normal"/></w:pPr></w:p>';

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
  xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
  xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">
  ${body}
</w:hdr>`;
}

type PageClass = "first" | "default" | "even";

/** Does a given page rule apply to a given page class? */
function ruleAppliesToPageClass(rule: PageRule, pageClass: PageClass): boolean {
  switch (rule) {
    case "all":
      return true;
    case "firstOnly":
      return pageClass === "first";
    case "notFirst":
      return pageClass !== "first";
    case "oddOnly":
      // When evenAndOddHeaders is enabled, OOXML's "default" header is used
      // for odd pages and "even" for even pages — so oddOnly maps to default.
      return pageClass === "default";
    case "evenOnly":
      return pageClass === "even";
    default:
      return true;
  }
}

interface DocxPageRules {
  headerPageRule: PageRule;
  footerPageRule: PageRule;
  leftSidePageRule?: PageRule;
  rightSidePageRule?: PageRule;
  /**
   * Side fit — "topBlock" renders the side image at its natural aspect
   * anchored to the top corner of the FIRST page with square text
   * wrapping, instead of a full-height band with widened margins.
   */
  leftSideFit?: SideFit;
  rightSideFit?: SideFit;
  /** Page-height fraction a topBlock side spans (default 1/3). */
  leftSideTopBlockHeightPct?: number;
  rightSideTopBlockHeightPct?: number;
  /** Source-image crop for topBlock sides (normalized; optional). */
  leftSideCrop?: SideCrop;
  rightSideCrop?: SideCrop;
}

interface DocxOptions extends DocxPageRules {
  /**
   * Distinct header/footer image for every page AFTER the first (MOR-953).
   * Used when a "first page only" letterhead layer is paired with an
   * "all except first page" layer: the primary image renders on page 1
   * (with `headerPageRule: "firstOnly"`) and this one fills the
   * w:type="default" part instead of leaving it empty.
   */
  headerImgNotFirst?: { buf: ArrayBuffer; w: number; h: number } | null;
  footerImgNotFirst?: { buf: ArrayBuffer; w: number; h: number } | null;
  /**
   * Which of the header/footer images are PAGE-FIT (see
   * `utils/branding/pageFit.ts`). A page-fit image is sized at page scale
   * and cropped beside side bands rather than squeezed into its slot. The
   * caller passes `padding` / `paddingRest` with page fit already applied.
   */
  pageFitImages?: {
    header?: boolean;
    headerNotFirst?: boolean;
    footer?: boolean;
    footerNotFirst?: boolean;
  };
  padding?: DocumentPadding;
  /**
   * Margins for every page AFTER the first (Word's "default" / "even" page
   * classes). Header/footer parts for those classes bake this padding's
   * gaps and indents; page-1 sides drive `w:pgMar`. Defaults to `padding`.
   */
  paddingRest?: DocumentPadding;
  /**
   * Default font-family applied to the altChunk body. Sets Word's document
   * default so the org's branding font carries into the export.
   */
  defaultFontFamily?: string | null;
  /** Default body line-height (CSS value). Falls back to 1.6 when unset. */
  defaultLineHeight?: string | null;
  /** Default body text colour. Falls back to "#000" when unset. */
  bodyTextColor?: string | null;
  /** Default body font-size (CSS value). Falls back to "11pt" when unset. */
  defaultFontSize?: string | null;
  /** Default paragraph `margin-top` (CSS value). Empty → no default. */
  defaultParagraphSpacingBefore?: string | null;
  /** Default paragraph `margin-bottom` (CSS value). Empty → no default. */
  defaultParagraphSpacingAfter?: string | null;
  /**
   * Footer page numbering. Rendered as a Word `PAGE`/`NUMPAGES` field so the
   * number stays correct if the recipient edits the document.
   */
  pageNumbering?: PageNumberingConfig | null;
  debugInfo?: Pick<
    WordExportDebugInfo,
    "filename" | "paddingSources" | "headerAsset" | "footerAsset"
  >;
}

/**
 * Assemble a .docx ZIP blob using the altChunk approach.
 *
 * Page rules map to OOXML header/footer reference types:
 *   all      → single w:type="default"
 *   firstOnly  → w:type="first" (image) + w:type="default" (empty) + titlePg
 *   notFirst   → w:type="default" (image) + w:type="first" (empty) + titlePg
 *   oddOnly    → w:type="default" (image) + w:type="even" (empty) + evenAndOddHeaders
 *   evenOnly   → w:type="even" (image) + w:type="default" (empty) + evenAndOddHeaders
 */
async function buildDocxBlob(
  bodyHtml: string,
  headerImg: { buf: ArrayBuffer; w: number; h: number } | null,
  footerImg: { buf: ArrayBuffer; w: number; h: number } | null,
  options: DocxOptions = { headerPageRule: "all", footerPageRule: "all" },
  leftSideImg: { buf: ArrayBuffer; w: number; h: number } | null = null,
  rightSideImg: { buf: ArrayBuffer; w: number; h: number } | null = null,
): Promise<Blob> {
  const pageRules = options;
  const leftSidePageRule = options.leftSidePageRule ?? "all";
  const rightSidePageRule = options.rightSidePageRule ?? "all";
  const zip = new JSZip();

  const hasHeader = !!headerImg;
  const hasFooter = !!footerImg;
  // Numbering is an independent footer element: a firm can number pages with
  // no footer letterhead at all, so it must not be gated on `hasFooter`.
  const pageNumbering = options.pageNumbering?.enabled
    ? options.pageNumbering
    : null;
  const hasLeftSide = !!leftSideImg;
  const hasRightSide = !!rightSideImg;
  const headerImgNotFirst = options.headerImgNotFirst ?? null;
  const footerImgNotFirst = options.footerImgNotFirst ?? null;
  const resolvedPadding: DocumentPadding = options.padding ?? {
    ...DEFAULT_PADDING,
  };
  // Side image dimensions: a full-height side is rendered at the full page
  // height with its natural aspect ratio preserved (width = pageHeight ×
  // naturalW/naturalH). A topBlock side instead spans its configured
  // page-height fraction, width following the aspect ratio with the same
  // page-width cap as the HTML pipeline.
  const A4_HEIGHT_EMU = A4_H * 635;
  const A4_WIDTH_EMU = A4_W * 635;
  const leftIsTopBlock = hasLeftSide && options.leftSideFit === "topBlock";
  const rightIsTopBlock = hasRightSide && options.rightSideFit === "topBlock";
  const leftSideCrop = normalizeSideCrop(options.leftSideCrop);
  const rightSideCrop = normalizeSideCrop(options.rightSideCrop);

  const sideDimsEmu = (
    img: { w: number; h: number } | null,
    isTopBlock: boolean,
    topBlockHeightPct: number | undefined,
    crop: SideCrop,
  ): { wEmu: number; hEmu: number } => {
    if (!img || img.w <= 0 || img.h <= 0) return { wEmu: 0, hEmu: 0 };
    if (isTopBlock) {
      // Aspect follows the CROPPED region of the source image — the crop
      // itself is applied via <a:srcRect> in the blipFill.
      const effW = img.w * Math.max(0.1, 1 - crop.left - crop.right);
      const effH = img.h * Math.max(0.1, 1 - crop.top - crop.bottom);
      const initialHEmu = Math.round(
        clampTopBlockHeightPct(topBlockHeightPct) * A4_HEIGHT_EMU,
      );
      const initialWEmu = Math.round(initialHEmu * (effW / effH));
      const maxWEmu = Math.round(A4_WIDTH_EMU * TOP_BLOCK_MAX_WIDTH_FRACTION);
      const wEmu = initialWEmu > maxWEmu ? maxWEmu : initialWEmu;
      const hEmu =
        initialWEmu > maxWEmu ? Math.round(wEmu * (effH / effW)) : initialHEmu;
      return { wEmu, hEmu };
    }
    return {
      wEmu: Math.round(A4_HEIGHT_EMU * (img.w / img.h)),
      hEmu: A4_HEIGHT_EMU,
    };
  };

  const leftSideDims = sideDimsEmu(
    leftSideImg,
    leftIsTopBlock,
    options.leftSideTopBlockHeightPct,
    leftSideCrop,
  );
  const rightSideDims = sideDimsEmu(
    rightSideImg,
    rightIsTopBlock,
    options.rightSideTopBlockHeightPct,
    rightSideCrop,
  );
  const leftSideWidthEmu = hasLeftSide ? leftSideDims.wEmu : 0;
  const rightSideWidthEmu = hasRightSide ? rightSideDims.wEmu : 0;
  // topBlock sides do NOT widen the section margins — text wraps around
  // the anchored image instead (wrapSquare), then reclaims the full width.
  const leftSideWidthTwip = leftIsTopBlock
    ? 0
    : Math.round(leftSideWidthEmu / 635);
  const rightSideWidthTwip = rightIsTopBlock
    ? 0
    : Math.round(rightSideWidthEmu / 635);

  // Header/footer zone width. The preview (buildLetterHtml) lays the header,
  // body and footer out in the CENTRAL grid column, i.e. beside any
  // full-height side band — so the inline Word images must be sized to that
  // column, not the full page width. Sizing them to the page made Beacon's
  // full-bleed header (headerSide = 0) paint straight across the right-hand
  // sidebar in Word, even though the preview showed it tucked beside it.
  // topBlock sides don't join the column (text wraps around them instead),
  // so they contribute nothing here — same as the preview.
  const fullHeightSidesWidthPx = (leftSideWidthTwip + rightSideWidthTwip) / 15;
  const headerWidthIn =
    hasHeader || headerImgNotFirst
      ? printableWidthInFromSidePadding(
          {
            left: resolvedPadding.headerLeft,
            right: resolvedPadding.headerRight,
          },
          fullHeightSidesWidthPx,
        )
      : 0;
  const footerWidthIn =
    hasFooter || footerImgNotFirst
      ? printableWidthInFromSidePadding(
          {
            left: resolvedPadding.footerLeft,
            right: resolvedPadding.footerRight,
          },
          fullHeightSidesWidthPx,
        )
      : 0;
  // Page-fit images keep page scale: their height follows the FULL page
  // width even when the slot is narrowed by side bands, and the columns
  // under the bands are cropped away (`ZoneBandCrop`) instead of squeezed.
  const pageFitImages = options.pageFitImages ?? {};
  const isPageFitImg = (img: { buf: ArrayBuffer } | null): boolean =>
    !!img &&
    ((img === headerImg && !!pageFitImages.header) ||
      (img === headerImgNotFirst && !!pageFitImages.headerNotFirst) ||
      (img === footerImg && !!pageFitImages.footer) ||
      (img === footerImgNotFirst && !!pageFitImages.footerNotFirst));
  const hasPageFitZone =
    isPageFitImg(headerImg) ||
    isPageFitImg(headerImgNotFirst) ||
    isPageFitImg(footerImg) ||
    isPageFitImg(footerImgNotFirst);
  const fullPageWidthIn = WORD_PAGE_WIDTH_PT / 72;
  const zoneHeightWidthIn = (
    img: { buf: ArrayBuffer } | null,
    slotWidthIn: number,
  ): number => (isPageFitImg(img) ? fullPageWidthIn : slotWidthIn);
  const bandCropFor = (
    img: { buf: ArrayBuffer } | null,
  ): ZoneBandCrop | null =>
    isPageFitImg(img) && fullHeightSidesWidthPx > 0
      ? {
          left: leftSideWidthTwip / 15 / A4_PAGE_WIDTH_PX,
          right: rightSideWidthTwip / 15 / A4_PAGE_WIDTH_PX,
        }
      : null;
  const headerHeightTwip = hasHeader
    ? imageHeightTwipFromWidthIn(
        zoneHeightWidthIn(headerImg, headerWidthIn),
        headerImg.w,
        headerImg.h,
      )
    : 0;
  const footerHeightTwip = hasFooter
    ? imageHeightTwipFromWidthIn(
        zoneHeightWidthIn(footerImg, footerWidthIn),
        footerImg.w,
        footerImg.h,
      )
    : 0;

  // The section's side margins are the SMALLEST inset across the zones. A
  // page-fit zone has no side margins of its own (it reaches the page edge
  // through negative indents), so counting its 0 would collapse that
  // baseline to the page edge — and the baseline is also the top/bottom
  // margin wherever there is no every-page header/footer. For the baseline
  // only, a page-fit zone borrows the body's side margins.
  const headerIsPageFit =
    isPageFitImg(headerImg) || isPageFitImg(headerImgNotFirst);
  const footerIsPageFit =
    isPageFitImg(footerImg) || isPageFitImg(footerImgNotFirst);
  const marginBasisPadding: DocumentPadding = {
    ...resolvedPadding,
    ...(headerIsPageFit
      ? {
          headerLeft: resolvedPadding.bodyLeft,
          headerRight: resolvedPadding.bodyRight,
        }
      : {}),
    ...(footerIsPageFit
      ? {
          footerLeft: resolvedPadding.bodyLeft,
          footerRight: resolvedPadding.bodyRight,
        }
      : {}),
  };
  const baseLeftPaddingPx = Math.min(
    marginBasisPadding.headerLeft,
    marginBasisPadding.bodyLeft,
    marginBasisPadding.footerLeft,
  );
  const baseRightPaddingPx = Math.min(
    marginBasisPadding.headerRight,
    marginBasisPadding.bodyRight,
    marginBasisPadding.footerRight,
  );
  const baseSidePaddingPx = Math.min(baseLeftPaddingPx, baseRightPaddingPx);
  const bodyExtraLeftPaddingPx = Math.max(
    resolvedPadding.bodyLeft - baseLeftPaddingPx,
    0,
  );
  const bodyExtraRightPaddingPx = Math.max(
    resolvedPadding.bodyRight - baseRightPaddingPx,
    0,
  );
  const bodyExtraSidePaddingPx = Math.max(
    bodyExtraLeftPaddingPx,
    bodyExtraRightPaddingPx,
  );

  // MOR-791: Word cannot flow a TABLE beside an anchored square-wrapped
  // drawing — it pushes the whole table below the float. The historical
  // "table-spacer-columns" body wrapper is one full-width table around the
  // ENTIRE body, so with a topBlock side letterhead the first page's
  // content started below the block (Shaw Gidley: the date sat ~8cm down
  // the page instead of beside the letterhead). When a topBlock side is
  // present, model the body's extra side padding via the section margins
  // instead and compensate the header/footer parts with negative indents
  // so their inline letterhead images keep the full configured width.
  const hasTopBlockSide = leftIsTopBlock || rightIsTopBlock;

  // MOR-911: Word will not start a new page from a CSS page break that sits
  // inside a TABLE CELL, and the body wrapper below puts the entire body in
  // one. A precedent whose body side padding differs from the smallest zone
  // padding therefore lost every manual page break in the .docx — the markup
  // was present and correct, Word just ignored it. Beacon hits this because
  // its header padding is 0 while the body's is 19px; letterheads whose body
  // padding already equals the minimum were never wrapped, which is why the
  // bug looked letterhead-specific.
  //
  // `bodyHtml` has already been through `prepareForWord`, so the marker div
  // has become the `page-break-before` declaration we look for here.
  const bodyHasManualPageBreak = /page-break-before\s*:\s*always/i.test(
    bodyHtml,
  );

  // A page-fit zone has no side margins of its own, so without folding the
  // section margins would collapse to the page edge and EVERY page-fit
  // document would get its body wrapped in a spacer table. Fold instead;
  // the zone parts reach back to the page edge with negative indents.
  const foldBodySidePaddingIntoMargins =
    (hasTopBlockSide || bodyHasManualPageBreak || hasPageFitZone) &&
    bodyExtraSidePaddingPx > 0;
  const bodyWrapperExtraSidePaddingPx = foldBodySidePaddingIntoMargins
    ? { left: 0, right: 0 }
    : { left: bodyExtraLeftPaddingPx, right: bodyExtraRightPaddingPx };

  // topBlock sides force a distinct first-page header part: the block must
  // appear ONLY on page 1 (its wrap geometry is first-page-anchored),
  // regardless of the configured page rule.
  const needsTitlePg =
    (hasHeader &&
      (pageRules.headerPageRule === "firstOnly" ||
        pageRules.headerPageRule === "notFirst")) ||
    (hasFooter &&
      (pageRules.footerPageRule === "firstOnly" ||
        pageRules.footerPageRule === "notFirst")) ||
    (hasLeftSide &&
      (leftSidePageRule === "firstOnly" || leftSidePageRule === "notFirst")) ||
    (hasRightSide &&
      (rightSidePageRule === "firstOnly" ||
        rightSidePageRule === "notFirst")) ||
    (!!pageNumbering &&
      (pageNumbering.pageRule === "firstOnly" ||
        pageNumbering.pageRule === "notFirst")) ||
    !!headerImgNotFirst ||
    !!footerImgNotFirst ||
    leftIsTopBlock ||
    rightIsTopBlock;

  const needsEvenOdd =
    (hasHeader &&
      (pageRules.headerPageRule === "oddOnly" ||
        pageRules.headerPageRule === "evenOnly")) ||
    (hasFooter &&
      (pageRules.footerPageRule === "oddOnly" ||
        pageRules.footerPageRule === "evenOnly")) ||
    (hasLeftSide &&
      (leftSidePageRule === "oddOnly" || leftSidePageRule === "evenOnly")) ||
    (hasRightSide &&
      (rightSidePageRule === "oddOnly" || rightSidePageRule === "evenOnly")) ||
    (!!pageNumbering &&
      (pageNumbering.pageRule === "oddOnly" ||
        pageNumbering.pageRule === "evenOnly"));

  // Section margins are needed BEFORE the header/footer parts are built so
  // the parts can compensate for margins wider than their own zone padding
  // (see the negative-indent notes below).
  const wordMargins = wordMarginsFromPadding(
    marginBasisPadding,
    hasHeader,
    hasFooter,
    headerHeightTwip,
    footerHeightTwip,
    pageRules.headerPageRule,
    pageRules.footerPageRule,
    leftSideWidthTwip,
    rightSideWidthTwip,
  );
  if (pageNumbering) {
    // A Word footer block grows UPWARD from the `w:footer` anchor, so the
    // number paragraph pushes the letterhead up and the body must stop that
    // much higher. Where `w:bottom` is still the no-zone baseline (a footer
    // restricted to some pages) we leave it alone and let Word push the body
    // itself, as it already does for the letterhead.
    const numberedZoneTwip =
      pxToTwip(resolvedPadding.footerBottom) +
      footerHeightTwip +
      PAGE_NUMBER_BAND_TWIP;
    if (wordMargins.bottom > wordMargins.side || !hasFooter) {
      wordMargins.bottom = Math.max(wordMargins.bottom, numberedZoneTwip);
    }
  }
  if (foldBodySidePaddingIntoMargins) {
    wordMargins.left = Math.max(
      wordMargins.left,
      pxToTwip(resolvedPadding.bodyLeft),
    );
    wordMargins.right = Math.max(
      wordMargins.right,
      pxToTwip(resolvedPadding.bodyRight),
    );
    // The wrapper table previously carried no top inset either way, but the
    // PDF/preview body starts at the configured bodyTop — mirror it so the
    // first line (the date) sits where the preview puts it. Only the
    // topBlock layout needs this; applying it to the page-break case would
    // shift page-1 content that is already positioned by the header zone.
    if (hasTopBlockSide) {
      wordMargins.top = Math.max(
        wordMargins.top,
        pxToTwip(resolvedPadding.bodyTop),
      );
    }
  }

  /**
   * Paragraph indents (twips) that place a zone's inline letterhead image at
   * the zone's OWN left/right margins, measured from the page edge (plus any
   * full-height side band the zone sits beside). The section margins are the
   * minimum across all zones on each edge, so a zone whose margin is wider
   * gets a positive indent and a zone whose margin is narrower than the
   * folded body margin (topBlock / page-break "page-margins" strategy) gets
   * a negative one. Independent edges are what let the header sit flush
   * left while the body keeps a wider left margin, or vice versa.
   */
  const zoneIndentTwip = (
    zoneLeftPx: number,
    zoneRightPx: number,
  ): { left: number; right: number } | null => {
    const bandLeftTwip = leftIsTopBlock ? 0 : leftSideWidthTwip;
    const bandRightTwip = rightIsTopBlock ? 0 : rightSideWidthTwip;
    const left = bandLeftTwip + pxToTwip(zoneLeftPx) - wordMargins.left;
    const right = bandRightTwip + pxToTwip(zoneRightPx) - wordMargins.right;
    return left !== 0 || right !== 0 ? { left, right } : null;
  };
  const headerZoneIndent = zoneIndentTwip(
    resolvedPadding.headerLeft,
    resolvedPadding.headerRight,
  );
  const footerZoneIndent = zoneIndentTwip(
    resolvedPadding.footerLeft,
    resolvedPadding.footerRight,
  );
  // Per page class: page 1 ("first", or "default" when there is no title
  // page) uses `padding`; every later class uses `paddingRest`.
  const restPadding = options.paddingRest ?? resolvedPadding;
  const paddingForClass = (pageClass: PageClass): DocumentPadding =>
    pageClass === "first" || !needsTitlePg ? resolvedPadding : restPadding;
  const headerZoneIndentFor = (pageClass: PageClass) => {
    const pad = paddingForClass(pageClass);
    return pad === resolvedPadding
      ? headerZoneIndent
      : zoneIndentTwip(pad.headerLeft, pad.headerRight);
  };
  const footerZoneIndentFor = (pageClass: PageClass) => {
    const pad = paddingForClass(pageClass);
    return pad === resolvedPadding
      ? footerZoneIndent
      : zoneIndentTwip(pad.footerLeft, pad.footerRight);
  };
  const headerWidthInFor = (pageClass: PageClass) => {
    const pad = paddingForClass(pageClass);
    return pad === resolvedPadding
      ? headerWidthIn
      : printableWidthInFromSidePadding(
          { left: pad.headerLeft, right: pad.headerRight },
          fullHeightSidesWidthPx,
        );
  };
  const footerWidthInFor = (pageClass: PageClass) => {
    const pad = paddingForClass(pageClass);
    return pad === resolvedPadding
      ? footerWidthIn
      : printableWidthInFromSidePadding(
          { left: pad.footerLeft, right: pad.footerRight },
          fullHeightSidesWidthPx,
        );
  };

  // MOR-911: minimum vertical reservation for pages whose header/footer
  // zone is EMPTY (non-"all" page rules, or side-image-only header parts).
  // The section margin on those pages is the small side baseline — 0 for
  // full-bleed letterheads like Beacon's — so without a reservation body
  // text runs to the literal page edge and prints cut off. The browser PDF
  // paginator reserves NO_HEADER_FALLBACK_PX / NO_FOOTER_FALLBACK_PX
  // (~1 inch) on such pages; mirror it here. The empty part's extent is
  // measured from the zone's anchor (w:header / w:footer offset), so
  // subtract that to land the body boundary at the fallback distance from
  // the page edge. Pages whose margins already exceed the fallback are
  // unaffected — Word takes max(pgMar, zone extent).
  const emptyHeaderExtentTwip = Math.max(
    0,
    pxToTwip(NO_HEADER_FALLBACK_PX) - wordMargins.header,
  );
  const emptyFooterExtentTwip = Math.max(
    0,
    pxToTwip(NO_FOOTER_FALLBACK_PX) - wordMargins.footer,
  );

  // Track all OOXML parts for content types, rels, and sectPr references.
  // Each zone (header/footer) may produce up to 2 XML parts.
  let partCounter = 0;
  const overrides: string[] = [
    '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>',
    '<Override PartName="/word/afchunk.html" ContentType="text/html"/>',
  ];
  const docRels: string[] = [
    '<Relationship Id="rChunk" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/aFChunk" Target="afchunk.html"/>',
  ];
  const sectRefs: string[] = [];

  /**
   * Register a header or footer XML part in the ZIP. Returns the relationship
   * ID so it can be referenced from w:sectPr.
   */
  const addZonePart = (
    zone: "header" | "footer",
    xml: string,
    imgBuf?: ArrayBuffer,
    imgRelId?: string,
  ): string => {
    partCounter++;
    const partName = `${zone}${partCounter}`;
    const relId = `r${zone.charAt(0).toUpperCase()}${partCounter}`;
    const ctType =
      zone === "header"
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml";
    const relType =
      zone === "header"
        ? "http://schemas.openxmlformats.org/officeDocument/2006/relationships/header"
        : "http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer";

    overrides.push(
      `<Override PartName="/word/${partName}.xml" ContentType="${ctType}"/>`,
    );
    docRels.push(
      `<Relationship Id="${relId}" Type="${relType}" Target="${partName}.xml"/>`,
    );
    zip.file(`word/${partName}.xml`, xml);

    if (imgBuf && imgRelId) {
      const mediaPath = `media/${partName}.png`;
      zip.file(`word/media/${partName}.png`, imgBuf);
      zip.file(
        `word/_rels/${partName}.xml.rels`,
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="${imgRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="${mediaPath}"/>
</Relationships>`,
      );
    }

    return relId;
  };

  // For non-"all" rules the section-level w:pgMar uses the small no-zone
  // baseline (see wordMarginsFromPadding). To preserve the configured
  // body gap on the pages that DO render the zone, we bake the gap into
  // the zone's image paragraph: w:after below the header (between header
  // and body), w:before above the footer (between body and footer).
  const headerBakedGapTwipFor = (pageClass: PageClass) =>
    hasHeader && pageRules.headerPageRule !== "all"
      ? pxToTwip(paddingForClass(pageClass).bodyTop)
      : 0;
  const footerBakedGapTwipFor = (pageClass: PageClass) =>
    hasFooter && pageRules.footerPageRule !== "all"
      ? pxToTwip(
          paddingForClass(pageClass).footerBottom +
            paddingForClass(pageClass).bodyBottom,
        )
      : 0;

  const buildZoneImageXml = (
    zone: "header" | "footer",
    img: { buf: ArrayBuffer; w: number; h: number },
    trailingParagraphsXml = "",
    pageClass: PageClass = "default",
  ): { xml: string; imgRelId: string } => {
    const tag: "w:hdr" | "w:ftr" = zone === "header" ? "w:hdr" : "w:ftr";
    const imgRelId = `r${zone.charAt(0).toUpperCase()}Img${partCounter + 1}`;
    const aspect = img.h / img.w;
    const widthIn =
      zone === "header"
        ? headerWidthInFor(pageClass)
        : footerWidthInFor(pageClass);
    const wEmu = Math.round(widthIn * EMU_PER_INCH);
    const hEmu = Math.round(
      zoneHeightWidthIn(img, widthIn) * EMU_PER_INCH * aspect,
    );
    const gapAfter = zone === "header" ? headerBakedGapTwipFor(pageClass) : 0;
    const gapBefore = zone === "footer" ? footerBakedGapTwipFor(pageClass) : 0;
    const indent =
      zone === "header"
        ? headerZoneIndentFor(pageClass)
        : footerZoneIndentFor(pageClass);
    return {
      xml: buildImagePartXml(
        tag,
        imgRelId,
        wEmu,
        hEmu,
        gapBefore,
        gapAfter,
        indent,
        // The page-number line sits under a slot-fit letterhead, but ABOVE
        // a page-fit one: a Word footer grows upward from `w:footer`, so
        // anything under the artwork would lift it off its true position
        // (and print over a full-bleed footer in the PDF path).
        isPageFitImg(img) ? "" : trailingParagraphsXml,
        bandCropFor(img),
        isPageFitImg(img) ? trailingParagraphsXml : "",
      ),
      imgRelId,
    };
  };

  // -- Build header parts ---------------------------------------------------
  // The page-class-based approach unifies header + side rendering. For each
  // page class (first / default / even) needed by the union of all zone
  // rules, we generate a single header part containing the inline top
  // header image (if applicable) plus anchored side images (if applicable).
  // Anchored DrawingML images are how Word represents "full-height side
  // letterhead" — there's no native side-header primitive in OOXML.
  if (hasHeader || hasLeftSide || hasRightSide || headerImgNotFirst) {
    const classesNeeded: PageClass[] = ["default"];
    if (needsTitlePg) classesNeeded.push("first");
    if (needsEvenOdd) classesNeeded.push("even");

    // Inline top-header dimensions in EMU for a given image (the first-page
    // and not-first images can have different aspect ratios).
    const topHeaderEmuFor = (
      img: { buf: ArrayBuffer; w: number; h: number },
      pageClass: PageClass,
    ) => {
      const widthIn = headerWidthInFor(pageClass);
      const wEmu = Math.round(widthIn * EMU_PER_INCH);
      const hEmu = Math.round(
        zoneHeightWidthIn(img, widthIn) * EMU_PER_INCH * (img.h / img.w),
      );
      return { wEmu, hEmu };
    };

    let docPrCounter = 100;

    for (const pageClass of classesNeeded) {
      const includeTopHeader =
        hasHeader &&
        ruleAppliesToPageClass(pageRules.headerPageRule, pageClass);
      // Image for this page class: the primary header when its rule applies,
      // otherwise the paired not-first header on non-first classes (MOR-953).
      const classTopImg =
        includeTopHeader && headerImg
          ? headerImg
          : headerImgNotFirst && pageClass !== "first"
            ? headerImgNotFirst
            : null;
      // topBlock sides render on the first page ONLY (intersected with the
      // configured rule); full-height sides follow the rule as before.
      const includeLeft =
        hasLeftSide &&
        (leftIsTopBlock
          ? pageClass === "first" &&
            ruleAppliesToPageClass(leftSidePageRule, "first")
          : ruleAppliesToPageClass(leftSidePageRule, pageClass));
      const includeRight =
        hasRightSide &&
        (rightIsTopBlock
          ? pageClass === "first" &&
            ruleAppliesToPageClass(rightSidePageRule, "first")
          : ruleAppliesToPageClass(rightSidePageRule, pageClass));

      // Reserve unique relIds for every image referenced from this part —
      // even empty parts get a relId-counter bump for consistency.
      const partRelIds: string[] = [];
      const imageBuffers: Array<{ relId: string; buf: ArrayBuffer }> = [];
      const nextImgRelId = (): string => {
        const id = `rImg${partCounter + 1}_${partRelIds.length + 1}`;
        partRelIds.push(id);
        return id;
      };

      const topHeaderSpec:
        | {
            imgRelId: string;
            widthEmu: number;
            heightEmu: number;
            gapAfterTwip: number;
            indentTwip?: { left: number; right: number } | null;
            bandCrop?: ZoneBandCrop | null;
          }
        | undefined = classTopImg
        ? (() => {
            const topHeaderEmu = topHeaderEmuFor(classTopImg, pageClass);
            const relId = nextImgRelId();
            imageBuffers.push({ relId, buf: classTopImg.buf });
            return {
              imgRelId: relId,
              widthEmu: topHeaderEmu.wEmu,
              heightEmu: topHeaderEmu.hEmu,
              gapAfterTwip: headerBakedGapTwipFor(pageClass),
              indentTwip: headerZoneIndentFor(pageClass),
              bandCrop: bandCropFor(classTopImg),
            };
          })()
        : undefined;

      const leftSideSpec:
        | {
            imgRelId: string;
            widthEmu: number;
            heightEmu: number;
            wrapSquare?: boolean;
            crop?: SideCrop;
          }
        | undefined =
        includeLeft && leftSideImg
          ? (() => {
              const relId = nextImgRelId();
              imageBuffers.push({ relId, buf: leftSideImg.buf });
              return {
                imgRelId: relId,
                widthEmu: leftSideWidthEmu,
                heightEmu: leftSideDims.hEmu,
                wrapSquare: leftIsTopBlock,
                crop: leftIsTopBlock ? leftSideCrop : undefined,
              };
            })()
          : undefined;

      const rightSideSpec:
        | {
            imgRelId: string;
            widthEmu: number;
            heightEmu: number;
            wrapSquare?: boolean;
            crop?: SideCrop;
          }
        | undefined =
        includeRight && rightSideImg
          ? (() => {
              const relId = nextImgRelId();
              imageBuffers.push({ relId, buf: rightSideImg.buf });
              return {
                imgRelId: relId,
                widthEmu: rightSideWidthEmu,
                heightEmu: rightSideDims.hEmu,
                wrapSquare: rightIsTopBlock,
                crop: rightIsTopBlock ? rightSideCrop : undefined,
              };
            })()
          : undefined;

      const noZoneSpecs = !topHeaderSpec && !leftSideSpec && !rightSideSpec;
      const partXml: string = noZoneSpecs
        ? buildEmptyPartXml("w:hdr", emptyHeaderExtentTwip)
        : buildComposedHeaderPartXml({
            topHeader: topHeaderSpec,
            leftSide: leftSideSpec,
            rightSide: rightSideSpec,
            pageWidthEmu: A4_WIDTH_EMU,
            docPrSeed: docPrCounter,
            minLineTwipWhenNoTopHeader: emptyHeaderExtentTwip,
          });
      if (!noZoneSpecs) {
        docPrCounter += 10;
      }

      // Emit the part and all its image rels. We extend addZonePart's
      // single-image contract by writing additional images directly.
      const refType: "first" | "default" | "even" = pageClass;
      // Inline a multi-image version of addZonePart so we can attach
      // multiple image rels to a single header part.
      partCounter++;
      const partName = `header${partCounter}`;
      const relId = `rH${partCounter}`;
      overrides.push(
        `<Override PartName="/word/${partName}.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>`,
      );
      docRels.push(
        `<Relationship Id="${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="${partName}.xml"/>`,
      );
      zip.file(`word/${partName}.xml`, partXml);

      if (imageBuffers.length > 0) {
        const partRels = imageBuffers
          .map((entry, idx) => {
            const mediaPath = `media/${partName}_${idx + 1}.png`;
            zip.file(`word/media/${partName}_${idx + 1}.png`, entry.buf);
            return `<Relationship Id="${entry.relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="${mediaPath}"/>`;
          })
          .join("\n  ");
        zip.file(
          `word/_rels/${partName}.xml.rels`,
          `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${partRels}
</Relationships>`,
        );
      }

      sectRefs.push(`<w:headerReference w:type="${refType}" r:id="${relId}"/>`);
    }
  }

  // -- Build footer parts ---------------------------------------------------
  // Page-class based, like the header path above: the letterhead image and the
  // page-number line have independent page rules, so each class gets a part
  // carrying whichever of the two applies to it (or an empty part).
  if (hasFooter || pageNumbering || footerImgNotFirst) {
    const classesNeeded: PageClass[] = ["default"];
    if (needsTitlePg) classesNeeded.push("first");
    if (needsEvenOdd) classesNeeded.push("even");

    for (const pageClass of classesNeeded) {
      const includeImage =
        hasFooter &&
        ruleAppliesToPageClass(pageRules.footerPageRule, pageClass);
      // Image for this page class: the primary footer when its rule applies,
      // otherwise the paired not-first footer on non-first classes (MOR-953).
      const classImg =
        includeImage && footerImg
          ? footerImg
          : footerImgNotFirst && pageClass !== "first"
            ? footerImgNotFirst
            : null;
      const includeNumber =
        !!pageNumbering &&
        ruleAppliesToPageClass(pageNumbering.pageRule, pageClass);

      // No `spacingBeforeTwip`: clearance from the body comes from `w:bottom`
      // (or Word's own push), and any letterhead above already carries the
      // configured gap on its own paragraph.
      const numberXml = includeNumber
        ? buildWordPageNumberParagraphXml({
            config: pageNumbering,
            fontFamily: options.defaultFontFamily,
            colour: options.bodyTextColor,
          })
        : "";

      const relId: string = classImg
        ? (() => {
            const { xml, imgRelId } = buildZoneImageXml(
              "footer",
              classImg,
              numberXml,
              pageClass,
            );
            return addZonePart("footer", xml, classImg.buf, imgRelId);
          })()
        : includeNumber
          ? addZonePart("footer", buildTextOnlyPartXml("w:ftr", numberXml))
          : addZonePart(
              "footer",
              buildEmptyPartXml("w:ftr", emptyFooterExtentTwip),
            );

      sectRefs.push(
        `<w:footerReference w:type="${pageClass}" r:id="${relId}"/>`,
      );
    }
  }

  // -- word/settings.xml (only when odd/even differentiation needed) --------
  if (needsEvenOdd) {
    overrides.push(
      '<Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>',
    );
    zip.file(
      "word/settings.xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:evenAndOddHeaders/>
</w:settings>`,
    );
  }

  // -- [Content_Types].xml (written after all overrides are collected) ------
  zip.file(
    "[Content_Types].xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Default Extension="png" ContentType="image/png"/>
  <Default Extension="jpeg" ContentType="image/jpeg"/>
  <Default Extension="jpg" ContentType="image/jpeg"/>
  ${overrides.join("\n  ")}
</Types>`,
  );

  // -- _rels/.rels -----------------------------------------------------------
  zip.file(
    "_rels/.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rDoc" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
  );

  // -- word/_rels/document.xml.rels -----------------------------------------
  if (needsEvenOdd) {
    docRels.push(
      '<Relationship Id="rSettings" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>',
    );
  }
  zip.file(
    "word/_rels/document.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${docRels.join("\n  ")}
</Relationships>`,
  );

  // -- word/document.xml ----------------------------------------------------
  const extraSectPr: string[] = [];
  if (needsTitlePg) extraSectPr.push("<w:titlePg/>");

  const debugPayload: WordExportDebugInfo = {
    filename: options.debugInfo?.filename,
    paddingSources: options.debugInfo?.paddingSources,
    headerAsset: options.debugInfo?.headerAsset ?? {
      fetched: false,
      error: null,
    },
    footerAsset: options.debugInfo?.footerAsset ?? {
      fetched: false,
      error: null,
    },
    bodyWrapperStrategy: foldBodySidePaddingIntoMargins
      ? "page-margins"
      : bodyExtraSidePaddingPx > 0
        ? "table-spacer-columns"
        : "none",
    resolvedPadding,
    baseSidePaddingPx,
    bodyExtraSidePaddingPx,
    headerWidthIn,
    footerWidthIn,
    headerHeightTwip,
    footerHeightTwip,
    wordMargins,
  };

  if (typeof window !== "undefined") {
    window.__lastWordExportLayout = debugPayload;
  }

  zip.file(
    "word/document.xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    <w:altChunk r:id="rChunk"/>
    <w:sectPr>
      ${sectRefs.join("\n      ")}
      ${extraSectPr.join("\n      ")}
      <w:pgSz w:w="${A4_W}" w:h="${A4_H}"/>
      <w:pgMar w:top="${wordMargins.top}" w:right="${wordMargins.right}" w:bottom="${wordMargins.bottom}" w:left="${wordMargins.left}" w:header="${wordMargins.header}" w:footer="${wordMargins.footer}"/>
    </w:sectPr>
  </w:body>
</w:document>`,
  );

  // -- word/afchunk.html (the body) -----------------------------------------
  zip.file(
    "word/afchunk.html",
    wrapBodyHtml(
      bodyHtml,
      bodyWrapperExtraSidePaddingPx,
      options.defaultFontFamily,
      options.defaultLineHeight,
      options.bodyTextColor,
      options.defaultFontSize,
      options.defaultParagraphSpacingBefore,
      options.defaultParagraphSpacingAfter,
    ),
  );

  return zip.generateAsync({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

/**
 * Prepare body HTML for the altChunk: embed external images as base64,
 * normalize image dimensions for Word, and strip browser-only wrapper
 * styles.
 */
async function prepareBodyForDocx(
  bodyHtml: string,
  defaultFontSize?: string | null,
  spacingOpts?: {
    defaultLineHeight?: string | null;
    paragraphSpacingBefore?: string | null;
    paragraphSpacingAfter?: string | null;
  },
): Promise<string> {
  const embedded = await embedExternalImages(bodyHtml);
  const normalized = await normalizeBodyImageDimensions(embedded);
  const html = prepareForWord(normalized, defaultFontSize, spacingOpts);
  return html;
}

/**
 * Word's altChunk HTML import does NOT honor `max-width` / `max-height`
 * on `<img>` tags — it falls back to the image's native pixel dimensions.
 * That means a 1920×640 signature scribble that the browser renders at
 * 240×60 lands in Word at full size and overflows the page.
 *
 * To work around this, find any image whose only sizing constraint is
 * `max-*` CSS, load it to read its natural dimensions, and rewrite the
 * tag with explicit `width=` / `height=` HTML attributes (which Word
 * does respect) plus matching `width:` / `height:` style declarations.
 *
 * Images that already have an explicit width or height (either as an
 * HTML attribute or a non-`max-*` style) are left untouched.
 */
async function normalizeBodyImageDimensions(html: string): Promise<string> {
  const imgPattern = /<img\b([^>]*)>/gi;
  const matches: Array<{ full: string; attrs: string }> = [];
  let match: RegExpExecArray | null;
  while ((match = imgPattern.exec(html)) !== null) {
    matches.push({ full: match[0], attrs: match[1] });
  }
  if (matches.length === 0) return html;

  const replacements = await Promise.all(
    matches.map(async ({ full, attrs }) => {
      const styleMatch = /style\s*=\s*"([^"]*)"/i.exec(attrs);
      const srcMatch = /src\s*=\s*"([^"]*)"/i.exec(attrs);
      if (!styleMatch || !srcMatch) return { full, replacement: full };

      const style = styleMatch[1];
      const maxWMatch = /max-width\s*:\s*(\d+(?:\.\d+)?)px/i.exec(style);
      const maxHMatch = /max-height\s*:\s*(\d+(?:\.\d+)?)px/i.exec(style);
      if (!maxWMatch && !maxHMatch) return { full, replacement: full };

      const hasExplicitAttr = /\s(?:width|height)\s*=\s*"\d/i.test(attrs);
      const hasExplicitStyle = /(?:^|;)\s*(?:width|height)\s*:\s*\d/i.test(
        style,
      );
      if (hasExplicitAttr || hasExplicitStyle)
        return { full, replacement: full };

      const dims = await measureImage(srcMatch[1]);
      if (!dims || dims.w <= 0 || dims.h <= 0)
        return { full, replacement: full };

      const maxW = maxWMatch ? parseFloat(maxWMatch[1]) : Infinity;
      const maxH = maxHMatch ? parseFloat(maxHMatch[1]) : Infinity;
      const scale = Math.min(maxW / dims.w, maxH / dims.h, 1);
      const targetW = Math.max(1, Math.round(dims.w * scale));
      const targetH = Math.max(1, Math.round(dims.h * scale));

      const cleanedStyle = style
        .replace(/max-width\s*:[^;]+;?/gi, "")
        .replace(/max-height\s*:[^;]+;?/gi, "")
        .replace(/object-fit\s*:[^;]+;?/gi, "")
        .replace(/;\s*;/g, ";")
        .replace(/^\s*;\s*/, "")
        .replace(/\s*;\s*$/, "")
        .trim();
      const finalStyle = [
        `width:${targetW}px`,
        `height:${targetH}px`,
        cleanedStyle,
      ]
        .filter(Boolean)
        .join(";");

      const attrsWithoutSize = attrs
        .replace(/\swidth\s*=\s*"[^"]*"/gi, "")
        .replace(/\sheight\s*=\s*"[^"]*"/gi, "")
        .replace(/\sstyle\s*=\s*"[^"]*"/i, "")
        .replace(/\s*\/\s*$/, "")
        .trim();
      const replacement = `<img ${attrsWithoutSize} width="${targetW}" height="${targetH}" style="${finalStyle}" />`;
      return { full, replacement };
    }),
  );

  const result = replacements.reduce(
    (acc, { full, replacement }) =>
      full !== replacement ? acc.split(full).join(replacement) : acc,
    html,
  );
  return result;
}

function measureImage(src: string): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    if (typeof Image === "undefined") {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function buildWordBlob(
  input: string | LetterParts,
): Promise<Blob> {
  if (typeof input === "string") {
    const body = await prepareBodyForDocx(input);
    return await buildDocxBlob(body, null, null);
  }

  const body = await prepareBodyForDocx(input.bodyHtml, input.defaultFontSize, {
    defaultLineHeight: input.defaultLineHeight,
    paragraphSpacingBefore: input.defaultParagraphSpacingBefore,
    paragraphSpacingAfter: input.defaultParagraphSpacingAfter,
  });

  const noImageDebug = (label: string): WordExportAssetDebugInfo => ({
    requestedUrl: undefined,
    fetched: false,
    error: `No ${label} image URL provided`,
  });

  // Word OOXML supports only a single header/footer pair (with optional
  // first-page variant). The new resolver emits one VariantFragments per
  // (zone, layer), so picking `wordVariants[0]` and reading all four zone
  // URLs off it loses three of the four (the first fragment only carries
  // the header URL, etc.). Resolve each zone independently for page 1 so
  // footer/left/right stay tied to the same override the user picked.
  const wordVariants = getVariantsForParts(input);
  // Resolve each zone for page 1 AND page 2 (MOR-953): a "first page only"
  // layer paired with an "all except first page" layer resolves to two
  // different fragments, which OOXML expresses as w:type="first" +
  // w:type="default" header/footer parts. When both pages resolve to the
  // same fragment this collapses to the old single-image behaviour.
  const headerFirstSource = pickVariantForPage(wordVariants, "header", 1);
  const headerRestSource = pickVariantForPage(wordVariants, "header", 2);
  const footerFirstSource = pickVariantForPage(wordVariants, "footer", 1);
  const footerRestSource = pickVariantForPage(wordVariants, "footer", 2);
  const headerSource = headerFirstSource ?? headerRestSource;
  const footerSource = footerFirstSource ?? footerRestSource;
  const leftSource = pickVariantForPage(wordVariants, "leftSide", 1);
  const rightSource = pickVariantForPage(wordVariants, "rightSide", 1);
  const headerUrl =
    headerSource?.headerImageUrl ?? input.headerImageUrl ?? null;
  const footerUrl =
    footerSource?.footerImageUrl ?? input.footerImageUrl ?? null;
  const headerNotFirstUrl =
    headerFirstSource &&
    headerRestSource &&
    headerRestSource !== headerFirstSource
      ? (headerRestSource.headerImageUrl ?? null)
      : null;
  const footerNotFirstUrl =
    footerFirstSource &&
    footerRestSource &&
    footerRestSource !== footerFirstSource
      ? (footerRestSource.footerImageUrl ?? null)
      : null;
  const leftUrl =
    leftSource?.leftSideImageUrl ?? input.leftSideImageUrl ?? null;
  const rightUrl =
    rightSource?.rightSideImageUrl ?? input.rightSideImageUrl ?? null;

  const [
    headerFetch,
    footerFetch,
    leftFetch,
    rightFetch,
    headerNotFirstFetch,
    footerNotFirstFetch,
  ] = await Promise.all([
    headerUrl
      ? fetchImageBuffer(headerUrl)
      : Promise.resolve({ image: null, debug: noImageDebug("header") }),
    footerUrl
      ? fetchImageBuffer(footerUrl)
      : Promise.resolve({ image: null, debug: noImageDebug("footer") }),
    leftUrl
      ? fetchImageBuffer(leftUrl)
      : Promise.resolve({ image: null, debug: noImageDebug("left-side") }),
    rightUrl
      ? fetchImageBuffer(rightUrl)
      : Promise.resolve({ image: null, debug: noImageDebug("right-side") }),
    headerNotFirstUrl
      ? fetchImageBuffer(headerNotFirstUrl)
      : Promise.resolve({
          image: null,
          debug: noImageDebug("not-first header"),
        }),
    footerNotFirstUrl
      ? fetchImageBuffer(footerNotFirstUrl)
      : Promise.resolve({
          image: null,
          debug: noImageDebug("not-first footer"),
        }),
  ]);
  const headerImg = headerFetch.image;
  const footerImg = footerFetch.image;
  const leftSideImg = leftFetch.image;
  const rightSideImg = rightFetch.image;
  const headerImgNotFirst = headerNotFirstFetch.image;
  const footerImgNotFirst = footerNotFirstFetch.image;

  // Page fit is applied to the zone paddings here, so everything the builder
  // derives from them (image width, indents, `w:header`/`w:footer` distance,
  // section margins) follows without a second code path.
  const pageInputs = pagePaddingInputs(input);
  const padding = applyPageFitToPadding(
    resolvePagePadding(pageInputs, 1),
    zonePageFit(headerFirstSource, footerFirstSource),
  );
  const paddingRest = applyPageFitToPadding(
    resolvePagePadding(pageInputs, 2),
    zonePageFit(headerRestSource, footerRestSource),
  );
  const isFit = (offset: number | null | undefined) =>
    offset !== null && offset !== undefined;
  return await buildDocxBlob(
    body,
    headerImg,
    footerImg,
    {
      // A distinct not-first image forces the first/default pair; otherwise
      // the winning fragment's own rule carries through unchanged.
      headerPageRule: headerImgNotFirst
        ? "firstOnly"
        : (headerSource?.headerPageRule ?? input.headerPageRule ?? "all"),
      footerPageRule: footerImgNotFirst
        ? "firstOnly"
        : (footerSource?.footerPageRule ?? input.footerPageRule ?? "all"),
      headerImgNotFirst,
      footerImgNotFirst,
      pageFitImages: {
        header: isFit(headerSource?.headerPageFitTop),
        headerNotFirst: isFit(headerRestSource?.headerPageFitTop),
        footer: isFit(footerSource?.footerPageFitBottom),
        footerNotFirst: isFit(footerRestSource?.footerPageFitBottom),
      },
      leftSidePageRule:
        leftSource?.leftSidePageRule ?? input.leftSidePageRule ?? "all",
      rightSidePageRule:
        rightSource?.rightSidePageRule ?? input.rightSidePageRule ?? "all",
      leftSideFit: leftSource?.leftSideFit ?? input.leftSideFit ?? "fullHeight",
      rightSideFit:
        rightSource?.rightSideFit ?? input.rightSideFit ?? "fullHeight",
      leftSideTopBlockHeightPct:
        leftSource?.leftSideTopBlockHeightPct ??
        input.leftSideTopBlockHeightPct,
      rightSideTopBlockHeightPct:
        rightSource?.rightSideTopBlockHeightPct ??
        input.rightSideTopBlockHeightPct,
      leftSideCrop:
        leftSource?.leftSideCrop ?? normalizeSideCrop(input.leftSideCrop),
      rightSideCrop:
        rightSource?.rightSideCrop ?? normalizeSideCrop(input.rightSideCrop),
      padding,
      paddingRest,
      defaultFontFamily: input.defaultFontFamily,
      defaultLineHeight: input.defaultLineHeight,
      bodyTextColor: input.bodyTextColor,
      defaultFontSize: input.defaultFontSize,
      defaultParagraphSpacingBefore: input.defaultParagraphSpacingBefore,
      defaultParagraphSpacingAfter: input.defaultParagraphSpacingAfter,
      pageNumbering: input.pageNumbering,
      debugInfo: {
        paddingSources: {
          branding: input.brandingPadding,
          template: input.templatePadding,
          document: input.documentPadding,
        },
        headerAsset: headerFetch.debug,
        footerAsset: footerFetch.debug,
      },
    },
    leftSideImg,
    rightSideImg,
  );
}

export async function downloadAsWord(
  input: string | LetterParts,
  filename: string,
): Promise<void> {
  const safeName = filename.replace(/\.doc$/i, "").replace(/\.docx$/i, "");
  const blob =
    typeof input === "string"
      ? await buildWordBlob(input)
      : await buildWordBlob(input);
  downloadBlob(blob, `${safeName}.docx`);
}

// ---------------------------------------------------------------------------
// PDF export
//
// Strategy: render N explicit A4 page divs (one per logical page) using
// JavaScript pagination. After the print window's content loads, we measure
// the rendered heights of header/body/footer at the actual A4 width, then
// rebuild the document as a sequence of fixed-size `.print-page` divs. Each
// page's body is a clipped window over the full body content, translated
// vertically by the cumulative offset so subsequent pages reveal the next
// slice of content.
//
// Why this layout (vs. the old table+thead+tfoot approach):
//   - Chrome's `<tfoot>` does not anchor to the page bottom on the last page;
//     it floats up to hug the body content (MOR-237).
//   - `@page { margin: 0 }` plus a single inline padding on the body div
//     means pages 2+ have no top margin when the body paginates and there's
//     no repeating header (MOR-236).
//
// The flex layout per `.print-page` (header / clipped body / footer) anchors
// the footer to the page bottom on every page including the last, and the
// per-page reservation handles the missing-header case by leaving a default
// 1-inch top space matching Word's default page margin.
// ---------------------------------------------------------------------------

/** Default reservation when a page has no header (≈ 1 inch at 96 DPI). */
const NO_HEADER_FALLBACK_PX = 96;

/** Default reservation when a page has no footer (≈ 1 inch at 96 DPI). */
const NO_FOOTER_FALLBACK_PX = 96;

/** Hard upper bound to guarantee pagination terminates for any input. */
const MAX_PAGES = 200;

/** DOM ID applied to the off-screen measurement container we inject before paginating. */
const MEASUREMENT_ROOT_ID = "morgan-pdf-measurement";

// ---------------------------------------------------------------------------
// Partial-height ("topBlock") side letterhead
//
// A topBlock side renders at its natural aspect ratio anchored to the TOP
// corner of the FIRST page, reserves no uniform side band, and instead
// injects a floated spacer at the top of the body flow so text wraps:
// narrow beside the block, full width below it. The spacer is part of the
// body layout, so it must be present (with identical dimensions) in the
// measurement DOM, the per-page render, and the React preview.
// ---------------------------------------------------------------------------

/**
 * Max fraction of the page width a topBlock may occupy. A very wide source
 * image is scaled down (aspect preserved) so the wrapped text column can't
 * collapse to nothing.
 */
const TOP_BLOCK_MAX_WIDTH_FRACTION = 0.65;

/** Horizontal gap between wrapped body text and a topBlock's inner edge. */
const TOP_BLOCK_WRAP_GUTTER_X_PX = 18;

/** Vertical gap between a topBlock's bottom edge and the text below it. */
const TOP_BLOCK_WRAP_GUTTER_Y_PX = 14;

/** Pixel dimensions of a rendered topBlock (page coordinates). */
export interface TopBlockPx {
  widthPx: number;
  heightPx: number;
}

/**
 * Compute the on-page dimensions of a topBlock side image from its natural
 * dimensions and the configured height fraction. Height is `pct × A4
 * height`; width follows the image's aspect ratio, capped at
 * `TOP_BLOCK_MAX_WIDTH_FRACTION` of the page width (cap preserves aspect
 * by shrinking the height too).
 */
export function computeTopBlockDims(
  naturalW: number,
  naturalH: number,
  heightPct: number | null | undefined,
): TopBlockPx | null {
  if (!naturalW || !naturalH || naturalW <= 0 || naturalH <= 0) return null;
  const pct = clampTopBlockHeightPct(heightPct);
  const initialHeightPx = Math.round(pct * A4_PAGE_HEIGHT_PX);
  const initialWidthPx = Math.round(initialHeightPx * (naturalW / naturalH));
  const maxWidthPx = Math.round(
    A4_PAGE_WIDTH_PX * TOP_BLOCK_MAX_WIDTH_FRACTION,
  );
  const widthPx = initialWidthPx > maxWidthPx ? maxWidthPx : initialWidthPx;
  const heightPx =
    initialWidthPx > maxWidthPx
      ? Math.round(widthPx * (naturalH / naturalW))
      : initialHeightPx;
  if (widthPx <= 0 || heightPx <= 0) return null;
  return { widthPx, heightPx };
}

/** Which side zones (with content) use topBlock fit across the variant list. */
function topBlockPresence(variants: VariantFragments[]): {
  left: boolean;
  right: boolean;
} {
  return {
    left: variants.some(
      (v) => !!v.leftSideHtml && v.leftSideFit === "topBlock",
    ),
    right: variants.some(
      (v) => !!v.rightSideHtml && v.rightSideFit === "topBlock",
    ),
  };
}

/**
 * The two bands at the top of one page: the header slot and the space above
 * the body clip. They are separate numbers because a page's margins can
 * differ from the uniform values the header slot and body flow were measured
 * at (per-page / per-layout margins):
 *
 *   - header top: the slot's own top padding grew/shrank, and so does the
 *     slot. The body follows it.
 *   - body top: the flow's top padding was measured at the uniform value,
 *     so the difference moves the body clip instead of re-padding the
 *     wrapper (which would shift every break offset). A smaller body top
 *     lifts the clip over the header slot's bottom edge — but only by as
 *     much of the flow's blank top padding as this page's slice still
 *     shows, so real content never paints over the letterhead. The header
 *     slot keeps its full height regardless (MOR-1534: sizing it with
 *     `topReservedPx` cropped the letterhead image).
 */
function computeTopBands(args: {
  showHeader: boolean;
  headerHeight: number;
  uniformPadding: DocumentPadding;
  pagePadding: DocumentPadding;
  yOffsetPx: number;
}): { headerSlotHeightPx: number; topReservedPx: number } {
  const { showHeader, headerHeight, uniformPadding, pagePadding, yOffsetPx } =
    args;
  const headerSlotHeightPx = showHeader
    ? Math.max(
        0,
        headerHeight + pagePadding.headerTop - uniformPadding.headerTop,
      )
    : 0;
  const blankFlowTopPx = Math.max(0, uniformPadding.bodyTop - yOffsetPx);
  const bodyTopDeltaPx = Math.max(
    -blankFlowTopPx,
    pagePadding.bodyTop - uniformPadding.bodyTop,
  );
  return {
    headerSlotHeightPx,
    topReservedPx: Math.max(
      0,
      (showHeader ? headerSlotHeightPx : NO_HEADER_FALLBACK_PX) +
        bodyTopDeltaPx,
    ),
  };
}

/**
 * Compute the floated wrap-spacer dimensions for each topBlock side.
 *
 * The spacer models the block's intrusion into the body's CONTENT box on
 * page 1: horizontally past the body side padding, vertically past the
 * page-1 top reservation (header height or the no-header fallback) plus
 * the body top padding. Wrap gutters keep text from touching the art.
 * Returns null per side when the block doesn't actually intrude (e.g. it
 * fits entirely within the header band or the side padding).
 *
 * Shared by the export measurement pass, the export render pass, and the
 * React preview so all three lay the body out identically.
 */
export function computeWrapSpacers(
  parts: LetterParts,
  measured: Pick<
    PaginationMeasured,
    "headerHeight" | "leftTopBlock" | "rightTopBlock"
  >,
  padding: DocumentPadding,
): { left: TopBlockPx | null; right: TopBlockPx | null } {
  const variants = getVariantsForParts(parts);
  const headerOnFirstPage = !!pickVariantForPage(variants, "header", 1);
  // Where page 1's body clip actually starts — including page 1's own
  // header-top / body-top margins, which move the text relative to the
  // page-anchored block.
  const pageInputs = pagePaddingInputs(parts);
  const { topReservedPx } = computeTopBands({
    showHeader: headerOnFirstPage,
    headerHeight: measured.headerHeight,
    uniformPadding: resolveDocumentPadding(
      parts.brandingPadding,
      parts.templatePadding,
      parts.documentPadding,
      variantPaddingSources(variants),
    ),
    pagePadding: resolvePagePadding(pageInputs, 1),
    yOffsetPx: 0,
  });

  const spacerFor = (
    block: TopBlockPx | null | undefined,
    bodySidePx: number,
  ): TopBlockPx | null => {
    if (!block) return null;
    const intrusionPx = block.widthPx - bodySidePx;
    const overlapPx = block.heightPx - topReservedPx - padding.bodyTop;
    if (intrusionPx <= 0 || overlapPx <= 0) return null;
    return {
      widthPx: intrusionPx + TOP_BLOCK_WRAP_GUTTER_X_PX,
      heightPx: overlapPx + TOP_BLOCK_WRAP_GUTTER_Y_PX,
    };
  };

  return {
    left: spacerFor(measured.leftTopBlock, padding.bodyLeft),
    right: spacerFor(measured.rightTopBlock, padding.bodyRight),
  };
}

/**
 * Build the floated spacer HTML for one side. A `null` spacer emits a
 * zero-size placeholder (when `emitPlaceholder`) so `measurePrintLayout`
 * can size it in-DOM once header heights are known; otherwise nothing.
 * The `<div>` deliberately doesn't match `BREAK_CANDIDATE_SELECTOR`, so
 * it never becomes a page-break candidate.
 */
export function buildWrapSpacerHtml(
  side: "left" | "right",
  spacer: TopBlockPx | null,
  emitPlaceholder = false,
): string {
  if (!spacer && !emitPlaceholder) return "";
  const w = spacer ? spacer.widthPx : 0;
  const h = spacer ? spacer.heightPx : 0;
  return `<div data-wrap-spacer="${side}" style="float:${side};width:${w}px;height:${h}px"></div>`;
}

/**
 * Resolve the empty / present header fragment HTML used for both measurement
 * and per-page rendering.
 *
 * @param pageFitTop      Page-fit offset of this header (`null` = slot fit).
 *   A page-fit header ignores the header margins and lays out at page width —
 *   see `utils/branding/pageFit.ts`.
 * @param slotLeftInsetPx How far the slot starts from the page's left edge
 *   (side band / topBlock). Only a page-fit header needs it.
 */
function buildHeaderSlotHtml(
  headerHtml: string | undefined,
  padding: DocumentPadding,
  pageFitTop: number | null = null,
  slotLeftInsetPx = 0,
): string {
  if (!headerHtml) return "";
  const p = applyPageFitToPadding(padding, {
    headerTop: pageFitTop,
    footerBottom: null,
  });
  return `<div style="padding:${p.headerTop}px ${p.headerRight}px 0 ${p.headerLeft}px">${fitZoneHtml(headerHtml, pageFitTop, slotLeftInsetPx)}</div>`;
}

/**
 * @param showsPageNumber Whether this page also shows a page number. The
 *   number line is anchored below the letterhead, so the slot's bottom
 *   padding grows to keep the two from overlapping.
 */
function buildFooterSlotHtml(
  footerHtml: string | undefined,
  padding: DocumentPadding,
  showsPageNumber = false,
  pageFitBottom: number | null = null,
  slotLeftInsetPx = 0,
): string {
  if (!footerHtml) return "";
  const p = applyPageFitToPadding(padding, {
    headerTop: null,
    footerBottom: pageFitBottom,
  });
  const insets = footerSlotInsets(p, pageFitBottom, showsPageNumber);
  // A bare `0` when there is no top inset keeps slot-fit markup
  // byte-identical to what it has always been.
  const top = insets.topPx > 0 ? `${insets.topPx}px` : "0";
  return `<div style="padding:${top} ${p.footerRight}px ${insets.bottomPx}px ${p.footerLeft}px">${fitZoneHtml(footerHtml, pageFitBottom, slotLeftInsetPx)}</div>`;
}

/**
 * Wrap the body HTML in the padded `.letter-body` container.
 *
 * When topBlock sides are in play the wrapper additionally carries floated
 * wrap spacers ahead of the content (see `buildWrapSpacerHtml`) — either
 * fully sized (render pass) or as zero-size placeholders that
 * `measurePrintLayout` sizes in-DOM (measurement pass). Without any
 * topBlock side the output is byte-identical to the historical wrapper.
 */
function buildBodyWrapperHtml(
  bodyHtml: string,
  padding: DocumentPadding,
  wrapSpacers?: { left: TopBlockPx | null; right: TopBlockPx | null },
  emitPlaceholders?: { left: boolean; right: boolean },
): string {
  const leftSpacer = buildWrapSpacerHtml(
    "left",
    wrapSpacers?.left ?? null,
    emitPlaceholders?.left ?? false,
  );
  const rightSpacer = buildWrapSpacerHtml(
    "right",
    wrapSpacers?.right ?? null,
    emitPlaceholders?.right ?? false,
  );
  return `<div class="letter-body prose-letter" style="padding:${padding.bodyTop}px ${padding.bodyRight}px ${padding.bodyTop}px ${padding.bodyLeft}px">${leftSpacer}${rightSpacer}${normalizeTableWidthsForOutput(bodyHtml)}</div>`;
}

/**
 * Build the empty / present side fragment HTML used for both measurement
 * and per-page rendering. Side bands span the full page height (top to
 * bottom) — width is intrinsic to the image and read via measurement.
 *
 * For measurement we render the image at `height:${A4_PAGE_HEIGHT_PX}px`
 * with `width:auto` so the browser preserves aspect ratio. The returned
 * div's `offsetWidth` then equals the side band's reserved page width.
 */
function buildSideMeasurementHtml(
  sideHtml: string | undefined,
  attr: "data-measure-left" | "data-measure-right",
  fit: SideFit = "fullHeight",
  topBlockHeightPct?: number,
  crop?: SideCrop,
): string {
  if (!sideHtml) {
    return `<div ${attr} data-measure-empty="1" style="display:inline-block;height:0;width:0"></div>`;
  }
  // topBlock sides are measured at their configured BLOCK height (a page
  // fraction) instead of the full page height — offsetWidth then reflects
  // the image's natural aspect at that height. Tagged so measurePrintLayout
  // treats the result as block geometry, not a reserved band width. The
  // FULL image is measured; the crop's visible fractions ride along as
  // data attributes so measurePrintLayout can derive the cropped aspect.
  if (fit === "topBlock") {
    const blockHeightPx = Math.round(
      clampTopBlockHeightPct(topBlockHeightPct) * A4_PAGE_HEIGHT_PX,
    );
    const c = normalizeSideCrop(crop);
    const visibleX = Math.max(0.1, 1 - c.left - c.right);
    const visibleY = Math.max(0.1, 1 - c.top - c.bottom);
    return `<div ${attr} data-measure-fit="topBlock" data-block-height="${blockHeightPx}" data-visible-x="${visibleX.toFixed(4)}" data-visible-y="${visibleY.toFixed(4)}" style="display:inline-block;height:${blockHeightPx}px;line-height:0;font-size:0;">
    <span style="display:inline-block;height:${blockHeightPx}px;vertical-align:top;">
      ${sideHtml.replace(
        /style="([^"]*)"/i,
        'style="display:block;height:100%;width:auto"',
      )}
    </span>
  </div>`;
  }
  // Wrap the side image so it renders at the page height — width auto so the
  // browser preserves the natural aspect ratio. The inline `img` style is
  // overridden by `height:100%;width:auto` from the inline wrapper.
  return `<div ${attr} style="display:inline-block;height:${A4_PAGE_HEIGHT_PX}px;line-height:0;font-size:0;">
    <div style="height:${A4_PAGE_HEIGHT_PX}px;display:inline-block;line-height:0;">
      <span style="display:inline-block;height:${A4_PAGE_HEIGHT_PX}px;vertical-align:top;">
        ${sideHtml.replace(
          /style="([^"]*)"/i,
          'style="display:block;height:100%;width:auto"',
        )}
      </span>
    </div>
  </div>`;
}

/**
 * Build the per-page side-band HTML used by `buildPaginatedDocumentHtml`.
 * The band is absolutely positioned and spans the full page height; the
 * image fills the band with `object-fit:fill` so it matches whatever
 * width the paginator reserved.
 */
function buildSidePageHtml(
  sideHtml: string | undefined,
  position: "left" | "right",
  widthPx: number,
): string {
  if (!sideHtml || widthPx <= 0) return "";
  const positionCss = position === "left" ? "left:0" : "right:0";
  // Override the inline img style so the image stretches to fill the
  // reserved band. Aspect ratio is preserved through the measurement step:
  // widthPx already equals the image's natural width scaled to page height.
  const sized = sideHtml.replace(
    /style="([^"]*)"/i,
    'style="display:block;width:100%;height:100%;object-fit:fill"',
  );
  return `<div class="print-side-${position}" style="position:absolute;top:0;bottom:0;${positionCss};width:${widthPx}px;overflow:hidden;pointer-events:none">${sized}</div>`;
}

/**
 * Build the per-page HTML for a partial-height ("topBlock") side. Unlike
 * the full-height band the block is anchored to the TOP corner with an
 * explicit width AND height (both derived from the CROPPED image region's
 * aspect ratio by the measurement pass, so `object-fit:fill` doesn't
 * distort). When a source crop is set, the image is oversized inside the
 * overflow-hidden box and shifted so only the cropped region shows.
 */
function buildTopBlockPageHtml(
  sideHtml: string | undefined,
  position: "left" | "right",
  block: TopBlockPx | null,
  crop?: SideCrop,
): string {
  if (!sideHtml || !block) return "";
  const positionCss = position === "left" ? "left:0" : "right:0";
  const c = crop ?? EMPTY_SIDE_CROP;
  const geo = sideCropIsActive(c) ? sideCropGeometry(c) : null;
  const sized = geo
    ? sideHtml.replace(
        /style="([^"]*)"/i,
        `style="position:absolute;display:block;width:${geo.imgWidthPct.toFixed(3)}%;height:${geo.imgHeightPct.toFixed(3)}%;left:-${geo.offsetLeftPct.toFixed(3)}%;top:-${geo.offsetTopPct.toFixed(3)}%"`,
      )
    : sideHtml.replace(
        /style="([^"]*)"/i,
        'style="display:block;width:100%;height:100%;object-fit:fill"',
      );
  return `<div class="print-side-${position}" style="position:absolute;top:0;${positionCss};width:${block.widthPx}px;height:${block.heightPx}px;overflow:hidden;pointer-events:none">${sized}</div>`;
}

/**
 * Build the off-screen measurement HTML for a single LetterParts input.
 *
 * Renders the full header / body / footer at the exact A4 page width so the
 * browser's reflow gives us pixel-accurate heights. The container is
 * positioned far off-screen and made invisible — never seen, never printed.
 *
 * Each measurable element is tagged with a `data-measure-*` attribute so
 * `paginatePrintWindow` can find them deterministically by query selector.
 */
function buildPrintMeasurementHtml(
  parts: LetterParts,
  indexAttr?: number,
): string {
  const variants = getVariantsForParts(parts);
  const padding = resolveDocumentPadding(
    parts.brandingPadding,
    parts.templatePadding,
    parts.documentPadding,
    variantPaddingSources(variants),
  );

  // Render every variant's header/footer/sides into the measurement tree so
  // measurePrintLayout() can take the MAX across all variants. Page heights
  // stay consistent regardless of which variant wins on a given page.
  const headerSlots = variants
    .map(
      (v, idx) =>
        `<div data-measure-header data-measure-variant="${idx}">${buildHeaderSlotHtml(v.headerHtml, padding, v.headerPageFitTop ?? null)}</div>`,
    )
    .join("");
  const footerSlots = variants
    .map(
      (v, idx) =>
        `<div data-measure-footer data-measure-variant="${idx}">${buildFooterSlotHtml(v.footerHtml, padding, false, v.footerPageFitBottom ?? null)}</div>`,
    )
    .join("");
  const leftSlots = variants
    .map(
      (v, idx) =>
        `<div data-measure-variant="${idx}">${buildSideMeasurementHtml(v.leftSideHtml, "data-measure-left", v.leftSideFit, v.leftSideTopBlockHeightPct, v.leftSideCrop)}</div>`,
    )
    .join("");
  const rightSlots = variants
    .map(
      (v, idx) =>
        `<div data-measure-variant="${idx}">${buildSideMeasurementHtml(v.rightSideHtml, "data-measure-right", v.rightSideFit, v.rightSideTopBlockHeightPct, v.rightSideCrop)}</div>`,
    )
    .join("");

  // topBlock sides need zero-size wrap-spacer placeholders inside the body
  // wrapper; measurePrintLayout sizes them once header + block dimensions
  // are known, then measures the wrapped body.
  const bodyWrapper = buildBodyWrapperHtml(
    parts.bodyHtml,
    padding,
    undefined,
    topBlockPresence(variants),
  );
  const indexAttrStr =
    indexAttr === undefined ? "" : ` data-measure-index="${indexAttr}"`;

  const lineHeight = resolveLineHeight(parts.defaultLineHeight);
  const color = resolveBodyTextColor(parts.bodyTextColor);
  const fontSize = resolveFontSize(parts.defaultFontSize);
  return `<div class="print-measure" data-measure-root${indexAttrStr} style="position:absolute;left:-99999px;top:0;visibility:hidden;width:${A4_PAGE_WIDTH_PX}px;font-size:${fontSize};line-height:${lineHeight};color:${color};">
    <div data-measure-headers>${headerSlots}</div>
    <div data-measure-body>${bodyWrapper}</div>
    <div data-measure-footers>${footerSlots}</div>
    <div data-measure-sides style="position:absolute;left:0;top:0;visibility:hidden;height:0;overflow:hidden">${leftSlots}${rightSlots}</div>
  </div>`;
}

export interface PaginationMeasured {
  /** Rendered offsetHeight of the header slot (0 if there's no header at all). */
  headerHeight: number;
  /** Rendered offsetHeight of the footer slot (0 if there's no footer at all). */
  footerHeight: number;
  /** Rendered offsetHeight of the full body wrapper (including its padding). */
  bodyHeight: number;
  /**
   * Rendered offsetWidth of the left side zone when present (0 otherwise).
   * Equals the natural width of the side image scaled to the A4 page height,
   * preserving the cropped band's aspect ratio. Only FULL-HEIGHT sides
   * contribute — topBlock sides reserve no band width.
   */
  leftWidth?: number;
  /** Rendered offsetWidth of the right side zone when present (0 otherwise). */
  rightWidth?: number;
  /**
   * Rendered dimensions of a partial-height ("topBlock") side, when one is
   * configured. Width/height reflect the image's natural aspect at the
   * configured page-height fraction (capped — see `computeTopBlockDims`).
   */
  leftTopBlock?: TopBlockPx | null;
  rightTopBlock?: TopBlockPx | null;
  /**
   * Optional sorted (ascending) list of "safe" break points within the body,
   * measured in pixels from the top of the body wrapper. Each value
   * corresponds to the bottom edge of a paginatable block element
   * (paragraph, list item, table row, heading, etc.).
   *
   * When provided, the paginator picks the largest candidate that fits on
   * each page so we never slice mid-line of body text. When omitted (or
   * empty), pages cut at exact pixel boundaries — useful only for fallback
   * scenarios where DOM measurement isn't available.
   */
  breakCandidates?: number[];
  /**
   * Like `breakCandidates`, but excluding points that fall inside a table
   * row. The paginator tries these first to avoid slicing through rows,
   * then falls back to `breakCandidates` when the gap would be too large.
   */
  tableSafeBreakCandidates?: number[];
  /**
   * Optional sorted (ascending) list of MANDATORY page-break offsets within
   * the body, measured in pixels from the top of the body wrapper. Each
   * value corresponds to the bottom edge of a `[data-page-break]` marker
   * inserted by the user via the editor toolbar.
   *
   * The paginator snaps the page boundary to the next mandatory break that
   * falls inside the page, regardless of remaining height — manual breaks
   * always win over automatic ones.
   */
  mandatoryBreaks?: number[];
  /**
   * Geometry of every *bordered* table in the body. When a page boundary slices
   * one of these, the render draws a closing line at the cut in the table's own
   * border colour, so a table split across pages doesn't read as "open"
   * (MOR-623).
   */
  tableSpans?: TableSpan[];
  /**
   * The body measured once per distinct column geometry (width + body side
   * padding) the document's pages need. Present only when pages differ
   * (per-layout margins or page overrides); absent means the single flow
   * above is used for every page, exactly as before.
   */
  bodyByGeometry?: BodyMeasurement[];
}

/** One body flow, measured at a specific column geometry. */
export interface BodyMeasurement {
  /** Wrapper width the flow was measured at (A4 minus reserved side bands). */
  columnWidthPx: number;
  /** Body side padding the wrapper carried during measurement. */
  bodyLeftPaddingPx: number;
  bodyRightPaddingPx: number;
  bodyHeight: number;
  breakCandidates: number[];
  tableSafeBreakCandidates: number[];
  mandatoryBreaks: number[];
  tableSpans: TableSpan[];
  /**
   * Top offset of every paginatable block (BREAK_CANDIDATE_SELECTOR) in
   * document order. Block order is width-invariant, so a page break chosen
   * in one flow maps to the same block in another.
   */
  blockTops: number[];
}

/** Geometry of a bordered table in the body (px, relative to the body top-left). */
export interface TableSpan {
  top: number;
  bottom: number;
  left: number;
  right: number;
  /** Cell border colour, reused for the page-break closing line. */
  color: string;
}

/** A horizontal closing line drawn at a table's page-break cut. */
export interface TableEdgeRule {
  left: number;
  right: number;
  color: string;
}

export interface PaginatedPage {
  pageIndex: number;
  /** Either the rendered header slot HTML or "" when the page has no header. */
  headerHtml: string;
  /** Either the rendered footer slot HTML or "" when the page has no footer. */
  footerHtml: string;
  /**
   * The page-number line's HTML, or "" when this page carries no number.
   * Positioned against the page (not nested in the footer slot) so it lands
   * at a fixed distance from the page edge whether or not a footer
   * letterhead is present. Filled in after pagination completes, since the
   * "of Y" total isn't known until then.
   */
  pageNumberHtml: string;
  /** Whether this page shows a page number (see `pageNumberHtml`). */
  showsPageNumber: boolean;
  /**
   * Either the rendered left side band HTML or "" when the page has no
   * left side image (or the rule excludes this page).
   */
  leftSideHtml: string;
  /** Same as `leftSideHtml` but for the right side. */
  rightSideHtml: string;
  /**
   * Pixel height reserved at the top of the page: the header height (or the
   * no-header fallback), adjusted by how far this page's header-top and
   * body-top margins differ from the uniform values the slots and body flow
   * were measured at. The body clip starts here. NOT the header slot's
   * height — see `headerSlotHeightPx`.
   */
  topReservedPx: number;
  /**
   * Height of the header slot (0 when the page shows no header): the
   * measured header adjusted by this page's header-top margin only. Can
   * exceed `topReservedPx` when the page's body top is below the uniform
   * value; the overlap is the body flow's blank top padding.
   */
  headerSlotHeightPx: number;
  /**
   * Pixel height reserved at the bottom of the page: the footer height (or
   * the no-footer fallback) plus this page's body-bottom margin, adjusted by
   * the page's footer-bottom margin the same way as `topReservedPx`. The
   * body clip ends here. NOT the footer slot's height — see
   * `footerSlotHeightPx`.
   */
  bottomReservedPx: number;
  /**
   * Height of the bottom-anchored footer slot (0 when the page shows no
   * footer): `bottomReservedPx` without the body-bottom margin, which is a
   * gap between the body and the footer rather than part of the slot.
   */
  footerSlotHeightPx: number;
  /**
   * Pixel width reserved at the LEFT edge of the page for the side band.
   * `0` when the left side image is absent or the rule excludes this page.
   *
   * This governs where the side ARTWORK paints only. It deliberately does
   * NOT govern the body column — see `bodyLeftPx` / `bodyWidthPx`.
   */
  leftReservedPx: number;
  /** Pixel width reserved at the RIGHT edge of the page. See `leftReservedPx`. */
  rightReservedPx: number;
  /**
   * Left offset and width of the body column — IDENTICAL on every page
   * (MOR-911).
   *
   * The body is one continuous flow that gets measured exactly once, at
   * `A4 - leftWidth - rightWidth`. Every page then shows a vertical slice of
   * that single measured flow, so each slice must be rendered at the width it
   * was measured at. Deriving the body width from the per-page
   * `leftReservedPx`/`rightReservedPx` instead — which drop to 0 on pages a
   * side-band rule excludes — re-flowed the text ~1 band wider from page 2
   * onward while the paginator's slice offsets still described the narrow
   * layout. Lines then got sliced in half at the page boundary and manual
   * page breaks landed on the wrong content (Beacon: a full-height right
   * band with a `firstOnly` rule).
   *
   * Keeping the column fixed also matches the Word export, where the band
   * width lives in the section-level `w:pgMar` and therefore applies to
   * every page in the section.
   */
  bodyLeftPx: number;
  /** Width of the body column. Constant across pages — see `bodyLeftPx`. */
  bodyWidthPx: number;
  /**
   * Partial-height ("topBlock") side geometry for this page, or null/absent.
   * Only ever set on page 1 — topBlock sides are first-page-anchored because
   * the body wrap spacer lives at the top of the (single) body flow. The
   * block reserves NO uniform side width; `leftReservedPx`/`rightReservedPx`
   * stay 0 and the body wraps via the floated spacer instead.
   */
  leftTopBlock?: TopBlockPx | null;
  rightTopBlock?: TopBlockPx | null;
  /** Pixel offset (positive) into the full body that this page begins at. */
  yOffsetPx: number;
  /** Pixel height of the visible body slice on this page. */
  contentHeightPx: number;
  /**
   * Closing lines to draw at the BOTTOM edge of this page's body slice — one
   * per bordered table sliced by that edge (MOR-623). Empty/undefined when no
   * table crosses the boundary.
   */
  tableBottomRules?: TableEdgeRule[];
  /** Same as `tableBottomRules` but at the TOP edge (a table continued onto this page). */
  tableTopRules?: TableEdgeRule[];
  /**
   * Margins resolved for THIS page (per-layout / per-page tiers applied).
   * Renderers pad the header/footer slots with these. The body wrapper is
   * NOT padded with `bodyTop`: the flow was measured at the uniform value
   * and the page's difference is already folded into `topReservedPx`.
   */
  padding: DocumentPadding;
  /** Index into `measured.bodyByGeometry`, or -1 when the single legacy flow was used. */
  bodyMeasurementIndex: number;
}

/**
 * Find the largest "safe" break offset that fits within `(pageStart, idealEnd]`.
 *
 * Returns `null` if no candidate fits — caller should fall back to slicing at
 * the page boundary, accepting that one element will be split mid-content.
 *
 * Tolerance: candidates within `BREAK_OVERFLOW_TOLERANCE_PX` past `idealEnd`
 * are still acceptable. Browser sub-pixel rounding can put the bottom of a
 * paragraph fractionally below the page boundary even when it visually fits;
 * a small tolerance avoids unnecessary page breaks in those cases.
 */
const BREAK_OVERFLOW_TOLERANCE_PX = 2;

/**
 * Max fraction of the page content area that may be left empty when
 * preferring a table-row-safe break over an intra-row break. If the
 * table-safe break would leave a gap larger than this ratio, we fall back
 * to splitting inside the row to keep pages visually full.
 *
 * 0.25 = up to 25% of the content area (~230px on a typical page) can be
 * empty, which keeps short rows clean without creating huge blank bands.
 */
const TABLE_GAP_THRESHOLD_RATIO = 0.25;

function findBestBreak(
  candidates: number[],
  pageStart: number,
  idealEnd: number,
): number | null {
  const search = (i: number): number | null => {
    if (i < 0) return null;
    const candidate = candidates[i];
    if (candidate <= pageStart) return null;
    if (candidate <= idealEnd + BREAK_OVERFLOW_TOLERANCE_PX) return candidate;
    return search(i - 1);
  };
  return search(candidates.length - 1);
}

/**
 * Pure pagination math: given a body height and per-zone heights, decide how
 * many physical pages the document occupies and what each page should show.
 *
 * Page heights are non-uniform: a page that doesn't show the header reserves
 * `NO_HEADER_FALLBACK_PX` instead of `headerHeight`, so the body slice grows
 * to fill the space. This keeps content density consistent across pages
 * without leaving suspicious empty bands above/below the body.
 *
 * When `measured.breakCandidates` is provided we pick the largest candidate
 * that fits within each page so we never slice through the middle of a
 * paragraph, list item, or table row. The page's content slot then has a
 * variable height — `.print-footer` uses `margin-top: auto` to stay anchored
 * to the page bottom regardless.
 *
 * Always returns at least one page, even when the body is empty — otherwise
 * a header-only or footer-only page would render nothing.
 */
export function computePaginationLayout(
  parts: LetterParts,
  measured: PaginationMeasured,
): PaginatedPage[] {
  const variants = getVariantsForParts(parts);
  const padding = resolveDocumentPadding(
    parts.brandingPadding,
    parts.templatePadding,
    parts.documentPadding,
    variantPaddingSources(variants),
  );

  const leftWidth = Math.max(0, measured.leftWidth ?? 0);
  const rightWidth = Math.max(0, measured.rightWidth ?? 0);

  // The body column is measured ONCE at this width, so every page must
  // render its slice at the same width — even pages whose side-band rule
  // paints no artwork (MOR-911). See `PaginatedPage.bodyLeftPx`.
  const bodyLeftPx = leftWidth;
  const bodyWidthPx = Math.max(1, A4_PAGE_WIDTH_PX - leftWidth - rightWidth);

  // Multi-flow mode: the body was measured once per distinct column
  // geometry (per-layout margins / page overrides). Each page picks the
  // flow matching its geometry and, when the flow changes between pages,
  // the running offset is mapped across flows by block index. Without
  // `bodyByGeometry` every page reads the single legacy flow above.
  const flows = measured.bodyByGeometry ?? null;
  let currentFlow = -1;

  const pages: PaginatedPage[] = [];
  let yOffset = 0;

  for (let i = 0; i < MAX_PAGES; i++) {
    const pageIndex = i + 1;

    const geometry = bodyGeometryForPage(parts, measured, pageIndex);
    // This page's resolved margins (per-page / per-layout tiers applied).
    // Always per-page: a document whose overrides only change vertical
    // values (header top, body top, footer bottom) has a single body
    // geometry and therefore no `bodyByGeometry` flows, but its pages still
    // get their own margins.
    const pagePadding = geometry.padding;
    // The header/footer slots and the body flow were measured once, at the
    // uniform `padding`. A page whose margins differ needs the extra (or
    // reclaimed) space reflected in what it reserves, so the letterhead
    // isn't clipped and the body slice starts where the margin says. The top
    // of the page is handled by `computeTopBands`; at the bottom, the footer
    // slot's own bottom padding grew/shrank by this delta.
    const footerBottomDeltaPx = pagePadding.footerBottom - padding.footerBottom;
    if (flows) {
      const foundFlowIdx = flows.findIndex((f) =>
        bodyGeometriesEqual(f, geometry),
      );
      const flowIdx = foundFlowIdx < 0 ? 0 : foundFlowIdx;
      if (pages.length > 0 && flowIdx !== currentFlow) {
        const from = flows[currentFlow];
        const to = flows[flowIdx];
        // First block of the remainder in the old flow → same block's top
        // in the new flow. A break inside a block (no candidate at/after
        // the offset) maps proportionally instead.
        const blockIdx = from.blockTops.findIndex(
          (t) => t >= yOffset - BREAK_OVERFLOW_TOLERANCE_PX,
        );
        if (blockIdx >= 0 && to.blockTops[blockIdx] !== undefined) {
          yOffset = to.blockTops[blockIdx];
        } else {
          yOffset = Math.round(
            yOffset * (to.bodyHeight / Math.max(1, from.bodyHeight)),
          );
        }
      }
      currentFlow = flowIdx;
    }
    const flow = flows ? flows[currentFlow] : null;
    const bodyHeight = flow ? flow.bodyHeight : measured.bodyHeight;
    const breakCandidates = flow
      ? flow.breakCandidates
      : (measured.breakCandidates ?? []);
    const tableSafeCandidates = flow
      ? flow.tableSafeBreakCandidates
      : (measured.tableSafeBreakCandidates ?? []);
    const useBreaks = breakCandidates.length > 0;
    const mandatoryBreaks = flow
      ? flow.mandatoryBreaks
      : (measured.mandatoryBreaks ?? []);

    // Pick winning variant per zone — first variant in priority order whose
    // zone rule includes this page and which actually has content for that
    // zone. Returns null when no variant supplies the zone on this page.
    const headerVariant = pickVariantForPage(variants, "header", pageIndex);
    const footerVariant = pickVariantForPage(variants, "footer", pageIndex);
    const leftVariant = pickVariantForPage(variants, "leftSide", pageIndex);
    const rightVariant = pickVariantForPage(variants, "rightSide", pageIndex);

    // topBlock sides are first-page-anchored: the body wrap spacer lives at
    // the top of the single body flow, so the block can only render where
    // that spacer actually is — page 1. On later pages a topBlock winner
    // renders nothing (and reserves nothing).
    const leftIsTopBlock = leftVariant?.leftSideFit === "topBlock";
    const rightIsTopBlock = rightVariant?.rightSideFit === "topBlock";
    const leftTopBlock =
      leftIsTopBlock && pageIndex === 1
        ? (measured.leftTopBlock ?? null)
        : null;
    const rightTopBlock =
      rightIsTopBlock && pageIndex === 1
        ? (measured.rightTopBlock ?? null)
        : null;

    const showHeader = !!headerVariant;
    const showFooter = !!footerVariant;
    const showLeftSide = !!leftVariant && !leftIsTopBlock;
    const showRightSide = !!rightVariant && !rightIsTopBlock;
    const showsPageNumber = shouldShowPageNumber(
      parts.pageNumbering,
      pageIndex,
    );

    // A page-fit header/footer lays out at page width, so it needs to know
    // how far its slot starts from the page edge (the same insets the
    // renderers apply: side band, and topBlock art for the header).
    const pageFit = zonePageFit(headerVariant, footerVariant);
    const slotLeftBandPx = showLeftSide ? leftWidth : 0;
    const headerHtml = headerVariant
      ? buildHeaderSlotHtml(
          headerVariant.headerHtml,
          pagePadding,
          pageFit.headerTop,
          Math.max(slotLeftBandPx, leftTopBlock?.widthPx ?? 0),
        )
      : "";
    const footerHtml = footerVariant
      ? buildFooterSlotHtml(
          footerVariant.footerHtml,
          pagePadding,
          showsPageNumber,
          pageFit.footerBottom,
          slotLeftBandPx,
        )
      : "";

    const { headerSlotHeightPx, topReservedPx } = computeTopBands({
      showHeader,
      headerHeight: measured.headerHeight,
      uniformPadding: padding,
      pagePadding,
      yOffsetPx: yOffset,
    });
    // A numbered page needs room for the extra line under the letterhead. With
    // no footer letterhead the fallback band is already ~1 inch of margin, so
    // the number sits inside it and the body keeps its full height.
    const footerSlotHeightPx = showFooter
      ? Math.max(
          0,
          measured.footerHeight +
            footerBottomDeltaPx +
            (showsPageNumber ? PAGE_NUMBER_BAND_PX : 0),
        )
      : 0;
    const bottomReservedPx = Math.max(
      0,
      (showFooter ? footerSlotHeightPx : NO_FOOTER_FALLBACK_PX) +
        pagePadding.bodyBottom,
    );
    const leftReservedPx = showLeftSide ? leftWidth : 0;
    const rightReservedPx = showRightSide ? rightWidth : 0;
    const maxPageContentPx = Math.max(
      0,
      A4_PAGE_HEIGHT_PX - topReservedPx - bottomReservedPx,
    );

    const idealEnd = yOffset + maxPageContentPx;

    // Manual page break inside this page's slice? Snap the page boundary to
    // it regardless of the auto-pagination heuristic — user-inserted breaks
    // are always mandatory.
    const nextManualBreak = mandatoryBreaks.find(
      (offset) => offset > yOffset && offset <= idealEnd,
    );

    // Pick the actual end of this page's body slice. Prefer a safe break
    // point (line/paragraph boundary). Fall back to the page boundary when
    // no candidate fits — this happens when a single element is taller than
    // a page; we accept slicing mid-element rather than looping forever.
    //
    // Final-page short-circuit: if everything left in the body already fits
    // on this page (`bodyHeight <= idealEnd + tolerance`), end the document
    // here regardless of break candidates. Otherwise the break-candidate
    // search may pick the bottom of the last paragraph (e.g. 1000px) when
    // `bodyHeight` is slightly larger because of trailing body padding (e.g.
    // 1100px) and force a second page that contains only the empty padding —
    // visually a blank trailing page (MOR-pdf-trailing-blank).
    //
    // The manual-break check runs FIRST: even if the rest of the body fits,
    // the user explicitly said "split here" so we honour that.
    //
    // Table-row preservation: we try table-safe candidates first (which only
    // break at row boundaries, never inside a row). If that would leave more
    // than 25% of the page content area empty, we fall back to all candidates
    // (including intra-row ones) so the gap stays reasonable.
    const rawChosenEnd =
      nextManualBreak !== undefined
        ? nextManualBreak
        : bodyHeight <= idealEnd + BREAK_OVERFLOW_TOLERANCE_PX
          ? bodyHeight
          : useBreaks
            ? (() => {
                const safeBreak = findBestBreak(
                  tableSafeCandidates,
                  yOffset,
                  idealEnd,
                );
                const gapRatio =
                  safeBreak !== null
                    ? (idealEnd - safeBreak) / maxPageContentPx
                    : 1;
                if (
                  safeBreak !== null &&
                  gapRatio <= TABLE_GAP_THRESHOLD_RATIO
                ) {
                  return safeBreak;
                }
                const anyBreak = findBestBreak(
                  breakCandidates,
                  yOffset,
                  idealEnd,
                );
                return anyBreak ?? safeBreak ?? idealEnd;
              })()
            : idealEnd;

    // Don't slice past the end of the body — clamp to the actual body height.
    const chosenEnd = rawChosenEnd > bodyHeight ? bodyHeight : rawChosenEnd;

    const contentHeightPx = Math.max(0, chosenEnd - yOffset);

    // MOR-623: when a bordered table is sliced by this page's top or bottom
    // edge, emit a closing line (table's own colour + x-extent) so the cut
    // reads as a closed border instead of an open-ended one. A boundary
    // strictly inside a table span means that table continues across it.
    const tableSpans = flow ? flow.tableSpans : (measured.tableSpans ?? []);
    const tableBottomRules: TableEdgeRule[] = [];
    const tableTopRules: TableEdgeRule[] = [];
    for (const s of tableSpans) {
      if (chosenEnd < bodyHeight && chosenEnd > s.top && chosenEnd < s.bottom) {
        tableBottomRules.push({ left: s.left, right: s.right, color: s.color });
      }
      if (yOffset > 0 && yOffset > s.top && yOffset < s.bottom) {
        tableTopRules.push({ left: s.left, right: s.right, color: s.color });
      }
    }

    pages.push({
      pageIndex,
      headerHtml,
      footerHtml,
      // Resolved below, once the page count is known.
      pageNumberHtml: "",
      showsPageNumber,
      leftSideHtml:
        leftVariant && !leftIsTopBlock
          ? buildSidePageHtml(leftVariant.leftSideHtml, "left", leftReservedPx)
          : buildTopBlockPageHtml(
              leftVariant?.leftSideHtml,
              "left",
              leftTopBlock,
              leftVariant?.leftSideCrop,
            ),
      rightSideHtml:
        rightVariant && !rightIsTopBlock
          ? buildSidePageHtml(
              rightVariant.rightSideHtml,
              "right",
              rightReservedPx,
            )
          : buildTopBlockPageHtml(
              rightVariant?.rightSideHtml,
              "right",
              rightTopBlock,
              rightVariant?.rightSideCrop,
            ),
      topReservedPx,
      headerSlotHeightPx,
      bottomReservedPx,
      footerSlotHeightPx,
      leftReservedPx,
      rightReservedPx,
      bodyLeftPx: flow ? leftReservedPx : bodyLeftPx,
      bodyWidthPx: flow ? flow.columnWidthPx : bodyWidthPx,
      leftTopBlock,
      rightTopBlock,
      yOffsetPx: yOffset,
      contentHeightPx,
      tableBottomRules,
      tableTopRules,
      padding: pagePadding,
      bodyMeasurementIndex: currentFlow,
    });

    yOffset = chosenEnd;

    // Stop once we've covered the full body. We always emit page 1 even if
    // the body is empty so a header-only or footer-only document still prints.
    if (yOffset >= bodyHeight) break;
    // Safety: if the page can't fit any body content (e.g. header + footer
    // exceed the page height) AND we made no progress, bail out instead of
    // looping forever.
    if (contentHeightPx <= 0) break;
  }

  // Second pass for the number line: "Page 2 of 5" needs the total, which
  // only exists once every page has been laid out. Insets clear any side
  // letterhead band so the number lines up with the footer above it.
  for (const page of pages) {
    if (!page.showsPageNumber) continue;
    page.pageNumberHtml = buildPageNumberHtml({
      config: parts.pageNumbering,
      pageIndex: page.pageIndex,
      totalPages: pages.length,
      leftPx: page.leftReservedPx + page.padding.footerLeft,
      rightPx: page.rightReservedPx + page.padding.footerRight,
      bottomPx: pageNumberBottomPx(
        page.padding,
        zonePageFit(
          null,
          pickVariantForPage(variants, "footer", page.pageIndex),
        ).footerBottom,
        measured.footerHeight,
      ),
      fontFamily: parts.defaultFontFamily,
      colour: parts.bodyTextColor,
    });
  }

  return pages;
}

/**
 * Render the per-page paginated HTML for one document, given measured heights.
 *
 * Each `.print-page` is a fixed-size positioning context (`position: relative`,
 * `width: A4`, `height: A4`). Header, body-clip, and footer are absolutely
 * positioned within it:
 *
 *   - `.print-header` anchored to `top: 0` with height `headerSlotHeightPx`.
 *   - `.print-body-clip` anchored at `top: topReservedPx` with height
 *     `contentHeightPx` (the *visible* slice — may be smaller than the max
 *     page content area when smart breaks shorten the slice).
 *   - `.print-footer` anchored to `bottom: 0` with height `footerSlotHeightPx`.
 *
 * Inside `.print-body-clip`, `.print-body` is absolutely positioned at
 * `top: -yOffsetPx` so the visible slice corresponds to body content from
 * `yOffsetPx` to `yOffsetPx + contentHeightPx`.
 *
 * Why absolute positioning instead of flexbox + transforms (the previous
 * implementation):
 *   - Chrome's print engine has known clipping bugs with `transform` inside
 *     `overflow: hidden` containers — the transformed body content escapes
 *     the clip and visually overlaps the next slot (header/footer), which
 *     looks like "the header is rendering in the middle of the page."
 *   - `margin-top: auto` on a flex item to push it to the page bottom is
 *     fragile in print mode; `bottom: 0` on an absolutely-positioned footer
 *     is unambiguous.
 *
 * The whitespace between the body-clip and the footer (when the visible
 * slice is shorter than the max content area) is intentional — it keeps the
 * footer at the page bottom on the final page.
 */
export function buildPaginatedDocumentHtml(
  parts: LetterParts,
  measured: PaginationMeasured,
): string {
  const variants = getVariantsForParts(parts);
  const padding = resolveDocumentPadding(
    parts.brandingPadding,
    parts.templatePadding,
    parts.documentPadding,
    variantPaddingSources(variants),
  );
  // The rendered body carries the SAME wrap spacers the measurement pass
  // sized in-DOM, so the wrapped text flow (and therefore every break
  // offset) matches the measurement exactly.
  const wrapSpacers = computeWrapSpacers(parts, measured, padding);
  const bodyWrapper = buildBodyWrapperHtml(
    parts.bodyHtml,
    padding,
    wrapSpacers,
    topBlockPresence(variants),
  );
  // Multi-flow documents render each page's body with the padding its flow
  // was measured at, so the page's slice offsets line up with the flow.
  const flowWrappers = (measured.bodyByGeometry ?? []).map((flow) =>
    buildBodyWrapperHtml(
      parts.bodyHtml,
      {
        ...padding,
        bodyLeft: flow.bodyLeftPaddingPx,
        bodyRight: flow.bodyRightPaddingPx,
      },
      wrapSpacers,
      topBlockPresence(variants),
    ),
  );
  const pages = computePaginationLayout(parts, measured);

  return pages
    .map((page) => {
      // Body geometry is fixed across pages (MOR-911) — the flow was
      // measured once at this width. Only the side ARTWORK follows the
      // per-page reservations below.
      const bodyAvailableWidth = page.bodyWidthPx;
      const bodyLeft = page.bodyLeftPx;
      // Header/footer/body all inset by side reservations so the side
      // bands paint their own strip. Sides are emitted FIRST so the
      // header/footer (later in DOM order) sit on top at the corners.
      // The header additionally insets around topBlock art so a full-width
      // header image never paints underneath the block.
      const headerLeftInsetPx = Math.max(
        page.leftReservedPx,
        page.leftTopBlock?.widthPx ?? 0,
      );
      const headerRightInsetPx = Math.max(
        page.rightReservedPx,
        page.rightTopBlock?.widthPx ?? 0,
      );
      const headerStyle = `position:absolute;top:0;left:${headerLeftInsetPx}px;right:${headerRightInsetPx}px;height:${page.headerSlotHeightPx}px;overflow:hidden;`;
      const bodyClipStyle = `position:absolute;top:${page.topReservedPx}px;left:${bodyLeft}px;width:${bodyAvailableWidth}px;height:${page.contentHeightPx}px;overflow:hidden;`;
      const bodyStyle = `position:absolute;top:-${page.yOffsetPx}px;left:0;width:${bodyAvailableWidth}px;`;
      const footerStyle = `position:absolute;bottom:0;left:${page.leftReservedPx}px;right:${page.rightReservedPx}px;height:${page.footerSlotHeightPx}px;overflow:hidden;`;

      // MOR-623: closing lines for bordered tables sliced by a page edge. The
      // body-clip cuts the collapsed border at the boundary, leaving the
      // continuation fragment "open"; this redraws the border at the table's
      // own x-extent and colour so each fragment reads as a closed box.
      const tableRuleDiv = (topPx: number, rule: TableEdgeRule) => {
        const left = bodyLeft + Math.max(0, rule.left);
        const width = Math.max(
          1,
          Math.min(rule.right, bodyAvailableWidth) - Math.max(0, rule.left),
        );
        return `<div style="position:absolute;left:${left}px;top:${topPx}px;width:${width}px;height:1px;background:${rule.color};"></div>`;
      };
      const bottomRulesHtml = (page.tableBottomRules ?? [])
        .map((r) =>
          tableRuleDiv(page.topReservedPx + page.contentHeightPx - 1, r),
        )
        .join("");
      const topRulesHtml = (page.tableTopRules ?? [])
        .map((r) => tableRuleDiv(page.topReservedPx, r))
        .join("");

      return `<div class="print-page" data-page-index="${page.pageIndex}">
  ${page.leftSideHtml}
  ${page.rightSideHtml}
  <div class="print-header" style="${headerStyle}">${page.headerHtml}</div>
  <div class="print-body-clip" style="${bodyClipStyle}">
    <div class="print-body" style="${bodyStyle}">${flowWrappers[page.bodyMeasurementIndex] ?? bodyWrapper}</div>
  </div>
  ${topRulesHtml}
  ${bottomRulesHtml}
  <div class="print-footer" style="${footerStyle}">${page.footerHtml}</div>
  ${page.pageNumberHtml}
</div>`;
    })
    .join("");
}

/** Resolve once promise that fires when all images in the window have loaded or errored. */
function waitForImages(printWindow: Window): Promise<void> {
  return new Promise((resolve) => {
    const images = printWindow.document.images;
    if (images.length === 0) {
      resolve();
      return;
    }
    let remaining = images.length;
    const tick = () => {
      remaining--;
      if (remaining <= 0) resolve();
    };
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      if (img.complete) {
        tick();
      } else {
        img.addEventListener("load", tick, { once: true });
        img.addEventListener("error", tick, { once: true });
      }
    }
  });
}

/**
 * Wait for every `<img>` under `root` to settle, up to `timeoutMs`. Resolves
 * `true` once all of them have loaded, `false` when one failed or the cap
 * ran out first. A freshly inserted image lays out at 0px until its bytes
 * arrive (letterhead and signature images carry no width/height
 * attributes), so a tree must settle before anything in it is measured,
 * and a tree with an image still missing is not a measurement of the
 * document. The cap keeps a request that never settles from stalling an
 * export.
 */
function waitForImagesWithin(
  root: ParentNode,
  timeoutMs: number,
): Promise<boolean> {
  const images = Array.from(root.querySelectorAll<HTMLImageElement>("img"));
  // `complete` is also true for an image whose request already failed; it
  // fires no further events, and it has no natural size.
  if (images.some((img) => img.complete && img.naturalWidth === 0)) {
    return Promise.resolve(false);
  }
  const pending = images.filter((img) => !img.complete);
  if (pending.length === 0) return Promise.resolve(true);
  return new Promise((resolve) => {
    let remaining = pending.length;
    const timer = setTimeout(() => resolve(false), timeoutMs);
    const settle = (loaded: boolean) => {
      if (!loaded) {
        clearTimeout(timer);
        resolve(false);
        return;
      }
      remaining--;
      if (remaining <= 0) {
        clearTimeout(timer);
        resolve(true);
      }
    };
    for (const img of pending) {
      img.addEventListener("load", () => settle(true), { once: true });
      img.addEventListener("error", () => settle(false), { once: true });
    }
  });
}

/**
 * Wait for the print window to be visually ready: images decoded and webfonts
 * loaded. Header heights depend on the rendered font metrics, so measuring
 * before fonts.ready often produces incorrect (too-tall or too-short)
 * pagination. Browsers without `document.fonts` simply skip that step.
 */
async function waitForPrintWindowReady(printWindow: Window): Promise<void> {
  await waitForImages(printWindow);
  await waitForStylesheets(printWindow);
  try {
    const doc = printWindow.document as Document & {
      fonts?: FontFaceSet;
    };
    const fonts = doc.fonts;
    if (fonts) {
      // Explicitly request the body's resolved font (incl. bold + signature
      // Caveat) so the linked @font-face actually downloads before we measure
      // or rasterise — `fonts.ready` alone can resolve before a font that no
      // glyph has triggered yet is fetched (MOR-623 Open Sans -> Arial).
      if (typeof fonts.load === "function") {
        const bodyFont =
          printWindow.getComputedStyle(doc.body).fontFamily ||
          FALLBACK_FONT_FAMILY;
        await Promise.all(
          [
            fonts.load(`400 16px ${bodyFont}`),
            fonts.load(`700 16px ${bodyFont}`),
            fonts.load(`400 22px 'Caveat'`),
          ].map((p) => p.catch(() => undefined)),
        );
      }
      if (
        fonts.ready &&
        typeof (fonts.ready as Promise<unknown>).then === "function"
      ) {
        await fonts.ready;
      }
    }
  } catch {
    // Ignore: font loading is best-effort.
  }
}

/**
 * Resolve once all `<link rel="stylesheet">` elements in the window have
 * finished loading (or errored). The Google Fonts stylesheet defines the
 * `@font-face` rules; if we measure before it loads, the font faces aren't
 * registered yet and `fonts.load` has nothing to fetch.
 */
function waitForStylesheets(printWindow: Window): Promise<void> {
  return new Promise((resolve) => {
    const links = Array.from(
      printWindow.document.querySelectorAll<HTMLLinkElement>(
        'link[rel="stylesheet"]',
      ),
    );
    if (links.length === 0) {
      resolve();
      return;
    }
    let remaining = links.length;
    const tick = () => {
      remaining--;
      if (remaining <= 0) resolve();
    };
    // Safety valve: never block the export longer than 3s on a slow/blocked
    // font CDN — fall back to whatever has loaded.
    const timeout = printWindow.setTimeout(resolve, 3000);
    const done = () => {
      tick();
      if (remaining <= 0) printWindow.clearTimeout(timeout);
    };
    for (const link of links) {
      if ((link.sheet as CSSStyleSheet | null) !== null) {
        done();
      } else {
        link.addEventListener("load", done, { once: true });
        link.addEventListener("error", done, { once: true });
      }
    }
  });
}

/**
 * CSS selector matching block-level elements we consider safe to break
 * between. A page boundary at the bottom edge of any matching element will
 * never bisect a line of body text.
 *
 * Notable exclusions:
 *   - `<div>` — too generic; many wrappers would create false break points
 *     extremely close together which doesn't help readability.
 *   - `<table>` itself — we break between `<tr>` rows instead so multi-row
 *     tables still paginate.
 */
const BREAK_CANDIDATE_SELECTOR =
  "p, h1, h2, h3, h4, h5, h6, li, tr, blockquote, hr, pre, figure";

/**
 * Effective scale factor an ancestor CSS transform is applying to `el`.
 *
 * The paginator mixes two coordinate systems: zone heights come from
 * `offsetHeight` (untransformed layout px) while break offsets come from
 * `getBoundingClientRect()` (transformed, on-screen px). Under an ancestor
 * `scale()` the offsets shrink while the heights don't, so `chosenEnd` no
 * longer lines up with the element boundary it was derived from and the page
 * clip slices through a line of text.
 *
 * This only bites the in-app preview: Radix's dialog opens with a 200ms
 * `zoom-in-95` (scale 0.95 → 1) animation, the measurement effect runs while
 * the subtree is still scaled, and nothing re-measures once the animation
 * finishes — a CSS animation ending is not a React render, and ResizeObserver
 * reports untransformed sizes so it never fires. The PDF path measures in a
 * print window with no transform, which is why exports paginated correctly
 * while the viewer cut lines in half.
 *
 * Dividing every `getBoundingClientRect()` delta by this factor puts the
 * collectors back in untransformed layout px, matching the `offsetHeight`
 * measurements. Returns 1 when the rect is within a pixel of the layout box
 * (i.e. no transform, just integer rounding on `offsetWidth`/`offsetHeight`),
 * so the export path is untouched.
 */
function getMeasurementScale(el: HTMLElement): number {
  const rect = el.getBoundingClientRect();
  const layoutWidth = el.offsetWidth;
  const layoutHeight = el.offsetHeight;
  const scale =
    layoutWidth > 0 && Math.abs(rect.width - layoutWidth) >= 1
      ? rect.width / layoutWidth
      : layoutHeight > 0 && Math.abs(rect.height - layoutHeight) >= 1
        ? rect.height / layoutHeight
        : 1;
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

/** Matches the `alt` text of an inserted signature image (`alt="Signature"`). */
const SIGNATURE_ALT_PATTERN = /signature/i;

/**
 * A trailing sign-off block (name, title, rule) taller than this is treated as
 * ordinary body content rather than part of the signature, and is NOT glued to
 * the signature. Keeps us from accidentally dragging a real paragraph — or an
 * over-tall block that would never fit a page — onto the next page with the
 * signature.
 */
const SIGNATURE_TRAILING_BLOCK_MAX_PX = 60;

/** Max number of trailing blocks (name / title / rule) glued after the image. */
const SIGNATURE_TRAILING_BLOCK_LIMIT = 3;

/**
 * Hard cap on how many sibling blocks the trailing walk inspects in total,
 * INCLUDING blank spacer paragraphs. Blank spacers don't count toward
 * `SIGNATURE_TRAILING_BLOCK_LIMIT` (MOR-769 — editors author sign-offs as
 * "signature / blank / title / blank / date / rule", and counting the blanks
 * used to leave the date + rule outside the band, letting the paginator
 * strand them on the next page), but an unbounded walk could glue content
 * across a long run of empty paragraphs that visually reads as a section
 * gap — so cap the total scan.
 */
const SIGNATURE_TRAILING_SCAN_LIMIT = 8;

/**
 * A block that renders no visible content — an empty paragraph the editor
 * inflates to `<p>&nbsp;</p>` for manual blank lines (MOR-382). These are
 * vertical spacing inside the sign-off, not sign-off content, so the band
 * walk spans them without spending the trailing-block budget on them.
 */
function isBlankSpacerBlock(el: Element): boolean {
  if (el.querySelector("img, table, hr")) return false;
  return (el.textContent ?? "").replace(/\u00a0/g, " ").trim() === "";
}

/**
 * Compute vertical "keep-together" bands — `[topPx, bottomPx]` ranges, relative
 * to the body top — that the paginator must never place a page boundary INSIDE.
 *
 * Today this protects the signature block (MOR-595): a precedent's sign-off is
 * authored as a closing line ("Yours faithfully"), the signature `<img>`, and
 * the signatory name/title (often followed by a horizontal rule). With no
 * keep-together rule the paginator greedily fills the page up to the bottom of
 * the closing line and orphans the signature — and everything below it — onto
 * the next page, so the signature appears "cut off".
 *
 * Each band spans from the TOP of the block immediately preceding the signature
 * image through the BOTTOM of the trailing name/title/rule. Break candidates
 * strictly inside a band are dropped (see `dropCandidatesInsideBands`), while
 * the band's own top edge survives — so the paginator can still break cleanly
 * BEFORE the whole block and push the entire sign-off to the next page intact.
 */
function collectSignatureKeepTogetherBands(
  bodyEl: HTMLElement,
): Array<[number, number]> {
  const imgs = Array.from(
    bodyEl.querySelectorAll<HTMLImageElement>("img"),
  ).filter((img) => SIGNATURE_ALT_PATTERN.test(img.getAttribute("alt") || ""));
  if (imgs.length === 0) return [];

  const bodyTop = bodyEl.getBoundingClientRect().top;
  const scale = getMeasurementScale(bodyEl);
  const bands: Array<[number, number]> = [];

  for (const img of imgs) {
    // The signature's own block: a wrapper whose only meaningful content is the
    // image (e.g. `<p><img></p>`), otherwise the bare image itself.
    const wrapper = img.closest<HTMLElement>("p, div, figure");
    const anchor =
      wrapper && wrapper !== img && (wrapper.textContent || "").trim() === ""
        ? wrapper
        : img;

    // Glue the single block immediately above (the closing line) to the image.
    const startEl = anchor.previousElementSibling ?? anchor;

    // Walk forward over the trailing name / title / rule blocks, stopping at a
    // horizontal rule (inclusive), at the block limit, or at the first block
    // tall enough to be ordinary body content. Blank spacer paragraphs are
    // spanned without counting toward the limit (MOR-769) so an authored
    // "signature / blank / title / blank / date / rule" sign-off stays one
    // band; a total scan cap keeps the walk from gluing across long empty
    // runs that read as a section gap.
    let endEl: Element = anchor;
    let node = anchor.nextElementSibling;
    let meaningful = 0;
    for (
      let scanned = 0;
      node &&
      scanned < SIGNATURE_TRAILING_SCAN_LIMIT &&
      meaningful < SIGNATURE_TRAILING_BLOCK_LIMIT;
      scanned++
    ) {
      if (
        node.getBoundingClientRect().height / scale >
        SIGNATURE_TRAILING_BLOCK_MAX_PX
      ) {
        break;
      }
      endEl = node;
      if (node.tagName === "HR" || node.querySelector("hr")) break;
      if (!isBlankSpacerBlock(node)) meaningful++;
      node = node.nextElementSibling;
    }

    const top = Math.round(
      (startEl.getBoundingClientRect().top - bodyTop) / scale,
    );
    const bottom = Math.round(
      (endEl.getBoundingClientRect().bottom - bodyTop) / scale,
    );
    if (bottom > top) bands.push([top, bottom]);
  }

  return bands;
}

/**
 * Drop break candidates that fall STRICTLY inside any keep-together band. The
 * band edges themselves survive, so the paginator can still break right before
 * the protected block (band top) or right after it (band bottom).
 */
export function dropCandidatesInsideBands(
  candidates: number[],
  bands: Array<[number, number]>,
): number[] {
  if (bands.length === 0) return candidates;
  return candidates.filter(
    (c) => !bands.some(([top, bottom]) => c > top && c < bottom),
  );
}

/**
 * Walk the body DOM and collect the y-offsets of every paginatable block
 * element's bottom edge, relative to the body wrapper. The returned list is
 * sorted ascending so the paginator can scan it in order.
 *
 * We use `getBoundingClientRect` rather than `offsetTop`/`offsetHeight` so
 * the math survives the `position: relative` / nested-flex edge cases that
 * arise inside rich body content.
 */
/**
 * Top offset of every paginatable block in DOCUMENT order (not sorted, not
 * filtered). Block order does not change with column width, so a page break
 * chosen in one body flow maps to the same block in another flow.
 */
export function collectBlockTops(bodyEl: HTMLElement): number[] {
  const elements = bodyEl.querySelectorAll<HTMLElement>(
    BREAK_CANDIDATE_SELECTOR,
  );
  const bodyTop = bodyEl.getBoundingClientRect().top;
  const scale = getMeasurementScale(bodyEl);
  return Array.from(elements, (el) =>
    Math.round((el.getBoundingClientRect().top - bodyTop) / scale),
  );
}

export function collectBreakCandidates(bodyEl: HTMLElement): number[] {
  const elements = bodyEl.querySelectorAll<HTMLElement>(
    BREAK_CANDIDATE_SELECTOR,
  );
  if (elements.length === 0) return [];

  const bodyTop = bodyEl.getBoundingClientRect().top;
  const scale = getMeasurementScale(bodyEl);
  const candidates = new Set<number>();
  for (const el of elements) {
    const rect = el.getBoundingClientRect();
    candidates.add(Math.round((rect.bottom - bodyTop) / scale));
  }
  // Also include the top of each element as a candidate. This lets us break
  // *before* a tall element (like a giant table) instead of being forced to
  // slice through it.
  for (const el of elements) {
    const rect = el.getBoundingClientRect();
    const topOffset = Math.round((rect.top - bodyTop) / scale);
    if (topOffset > 0) candidates.add(topOffset);
  }
  // Drop break points that would orphan a signature block (MOR-595).
  const bands = collectSignatureKeepTogetherBands(bodyEl);
  return dropCandidatesInsideBands(
    [...candidates].sort((a, b) => a - b),
    bands,
  );
}

/**
 * Subset of `collectBreakCandidates` that only includes positions where
 * the paginator can break without slicing through a table row. Elements
 * nested inside a `<tr>` (e.g. `<p>`, `<li>` inside a `<td>`) are excluded —
 * only `<tr>` boundaries and non-table elements contribute.
 *
 * Used as the preferred first-choice set by the paginator; falls back to
 * the full `breakCandidates` when the gap left by a table-safe break would
 * be too large (see `TABLE_GAP_THRESHOLD_RATIO`).
 */
export function collectTableSafeBreakCandidates(bodyEl: HTMLElement): number[] {
  const elements = bodyEl.querySelectorAll<HTMLElement>(
    BREAK_CANDIDATE_SELECTOR,
  );
  if (elements.length === 0) return [];

  const bodyTop = bodyEl.getBoundingClientRect().top;
  const scale = getMeasurementScale(bodyEl);
  const candidates = new Set<number>();
  for (const el of elements) {
    if (el.tagName !== "TR" && el.closest("tr")) continue;
    const rect = el.getBoundingClientRect();
    candidates.add(Math.round((rect.bottom - bodyTop) / scale));
  }
  for (const el of elements) {
    if (el.tagName !== "TR" && el.closest("tr")) continue;
    const rect = el.getBoundingClientRect();
    const topOffset = Math.round((rect.top - bodyTop) / scale);
    if (topOffset > 0) candidates.add(topOffset);
  }
  // Drop break points that would orphan a signature block (MOR-595).
  const bands = collectSignatureKeepTogetherBands(bodyEl);
  return dropCandidatesInsideBands(
    [...candidates].sort((a, b) => a - b),
    bands,
  );
}

/**
 * Walk the body DOM and collect the y-offsets of every manual page break
 * marker (`[data-page-break]`), relative to the body wrapper.
 *
 * Each marker is taken at its TOP edge — that's where the next page should
 * begin. The list is sorted ascending so the paginator can advance through
 * it linearly.
 */
export function collectMandatoryBreaks(bodyEl: HTMLElement): number[] {
  const markers = bodyEl.querySelectorAll<HTMLElement>("[data-page-break]");
  if (markers.length === 0) return [];

  const bodyTop = bodyEl.getBoundingClientRect().top;
  const scale = getMeasurementScale(bodyEl);
  const breaks: number[] = [];
  for (const el of markers) {
    const rect = el.getBoundingClientRect();
    const topOffset = Math.round((rect.top - bodyTop) / scale);
    if (topOffset > 0) breaks.push(topOffset);
  }
  return breaks.sort((a, b) => a - b);
}

/**
 * Measure the heights of the off-screen draft elements injected by
 * `buildPrintMeasurementHtml` and return them. Also collects break
 * candidates from the rendered body so the paginator can avoid slicing
 * mid-paragraph.
 *
 * When the document has left/right side bands, this function FIRST reads
 * the side image widths, THEN constrains the body wrapper width to
 * `A4 - leftWidth - rightWidth` and re-measures the body. This keeps the
 * break candidates accurate against the actual rendered width (otherwise
 * body content would paginate as if it had A4 width to spare, then
 * overflow at render time onto extra pages).
 *
 * topBlock sides go through a different path: they contribute NO band
 * width. Instead their measured block dimensions size the floated wrap
 * spacer placeholders inside the body wrapper (injected by
 * `buildPrintMeasurementHtml`), and the body is measured with the spacers
 * in place so text wraps exactly as it will render. Sizing the spacer
 * needs the page-1 top reservation, which is why `parts` is threaded in.
 */
function measurePrintLayout(
  printWindow: Window,
  measureRoot: HTMLElement,
  parts?: LetterParts,
): PaginationMeasured {
  // Multiple variants may live in the tree; take the MAX height/width so the
  // pagination reservations are large enough for any variant that might show
  // up on any page. Predictable trumps pixel-perfect here — a smaller
  // variant just gets some extra whitespace on its pages.
  const headerEls = Array.from(
    measureRoot.querySelectorAll<HTMLElement>("[data-measure-header]"),
  );
  const footerEls = Array.from(
    measureRoot.querySelectorAll<HTMLElement>("[data-measure-footer]"),
  );
  const leftEls = Array.from(
    measureRoot.querySelectorAll<HTMLElement>("[data-measure-left]"),
  );
  const rightEls = Array.from(
    measureRoot.querySelectorAll<HTMLElement>("[data-measure-right]"),
  );
  const bodyEl = measureRoot.querySelector<HTMLElement>("[data-measure-body]");

  void printWindow;

  const maxOffsetHeight = (els: HTMLElement[]) =>
    els.reduce((max, el) => Math.max(max, el.offsetHeight), 0);

  // Empty-side sentinels are tagged with data-measure-empty="1"; ignore them.
  // topBlock slots are tagged data-measure-fit="topBlock" — they contribute
  // block geometry, not a reserved band width.
  const isEmptySide = (el: HTMLElement): boolean =>
    el.getAttribute("data-measure-empty") === "1";
  const isTopBlockSide = (el: HTMLElement): boolean =>
    el.getAttribute("data-measure-fit") === "topBlock";
  const maxSideWidth = (els: HTMLElement[]) =>
    els.reduce((max, el) => {
      if (isEmptySide(el) || isTopBlockSide(el)) return max;
      return Math.max(max, el.offsetWidth);
    }, 0);

  // Largest topBlock geometry per side (mirrors the MAX philosophy above).
  // The slot renders the FULL image at the configured block height with
  // width:auto, so offsetWidth is the natural width at that height. The
  // crop's visible fractions (from the slot's data attributes) scale that
  // down to the CROPPED region's aspect before computeTopBlockDims()
  // applies the page-width cap.
  const maxTopBlock = (els: HTMLElement[]): TopBlockPx | null =>
    els
      .filter((el) => !isEmptySide(el) && isTopBlockSide(el))
      .reduce<TopBlockPx | null>((best, el) => {
        const blockHeightPx = parseInt(
          el.getAttribute("data-block-height") || "0",
          10,
        );
        if (blockHeightPx <= 0 || el.offsetWidth <= 0) return best;
        const visibleX =
          parseFloat(el.getAttribute("data-visible-x") || "1") || 1;
        const visibleY =
          parseFloat(el.getAttribute("data-visible-y") || "1") || 1;
        const dims = computeTopBlockDims(
          el.offsetWidth * visibleX,
          blockHeightPx * visibleY,
          blockHeightPx / A4_PAGE_HEIGHT_PX,
        );
        if (!dims) return best;
        return !best ||
          dims.widthPx * dims.heightPx > best.widthPx * best.heightPx
          ? dims
          : best;
      }, null);

  const leftWidth = maxSideWidth(leftEls);
  const rightWidth = maxSideWidth(rightEls);
  const leftTopBlock = maxTopBlock(leftEls);
  const rightTopBlock = maxTopBlock(rightEls);
  const headerHeight = maxOffsetHeight(headerEls);

  // If either full-height side is present, constrain the body wrapper's
  // width before measuring body height + break candidates so the wrap math
  // is accurate.
  if (bodyEl && (leftWidth > 0 || rightWidth > 0)) {
    const constrained = Math.max(1, A4_PAGE_WIDTH_PX - leftWidth - rightWidth);
    const wrapper = bodyEl.querySelector<HTMLElement>(".letter-body");
    if (wrapper) {
      wrapper.style.width = `${constrained}px`;
      wrapper.style.boxSizing = "border-box";
      // Force layout so subsequent offsetHeight reflects the new width.
      void wrapper.offsetHeight;
    }
  }

  // Size the topBlock wrap-spacer placeholders now that header + block
  // dimensions are known, so the body measurement below reflects the
  // wrapped text flow. Uses the same helper as the render pass.
  if (bodyEl && parts && (leftTopBlock || rightTopBlock)) {
    const spacerVariants = getVariantsForParts(parts);
    const padding = resolveDocumentPadding(
      parts.brandingPadding,
      parts.templatePadding,
      parts.documentPadding,
      variantPaddingSources(spacerVariants),
    );
    const spacers = computeWrapSpacers(
      parts,
      { headerHeight, leftTopBlock, rightTopBlock },
      padding,
    );
    for (const side of ["left", "right"] as const) {
      const spacerEl = bodyEl.querySelector<HTMLElement>(
        `[data-wrap-spacer="${side}"]`,
      );
      const spacer = spacers[side];
      if (spacerEl && spacer) {
        spacerEl.style.width = `${spacer.widthPx}px`;
        spacerEl.style.height = `${spacer.heightPx}px`;
      }
    }
    // Force layout so subsequent measurements reflect the wrapped flow.
    void bodyEl.offsetHeight;
  }

  // Per-layout margins / page overrides: pages can need different body
  // column geometries (a band on page 1 only, a wider left margin on page
  // 3). Measure the body once per distinct geometry by cloning the body
  // slot, so each flow's break offsets are real. Documents with a single
  // geometry skip this and keep the legacy single-flow result.
  const bodyByGeometry: BodyMeasurement[] | undefined =
    bodyEl && parts
      ? (() => {
          const geometries = distinctBodyGeometries(parts, {
            leftWidth,
            rightWidth,
          });
          if (geometries.length <= 1) return undefined;
          return geometries.map((g) => {
            const clone = bodyEl.cloneNode(true) as HTMLElement;
            clone.removeAttribute("data-measure-body");
            clone.setAttribute("data-measure-flow", "1");
            bodyEl.parentElement?.appendChild(clone);
            const wrapper =
              clone.querySelector<HTMLElement>(".letter-body") ?? clone;
            wrapper.style.width = `${g.columnWidthPx}px`;
            wrapper.style.boxSizing = "border-box";
            wrapper.style.paddingLeft = `${g.bodyLeftPaddingPx}px`;
            wrapper.style.paddingRight = `${g.bodyRightPaddingPx}px`;
            void clone.offsetHeight;
            const flow: BodyMeasurement = {
              ...g,
              bodyHeight: clone.offsetHeight,
              breakCandidates: collectBreakCandidates(clone),
              tableSafeBreakCandidates: collectTableSafeBreakCandidates(clone),
              mandatoryBreaks: collectMandatoryBreaks(clone),
              tableSpans: collectBorderedTableSpans(clone),
              blockTops: collectBlockTops(clone),
            };
            clone.remove();
            return flow;
          });
        })()
      : undefined;

  return {
    headerHeight,
    footerHeight: maxOffsetHeight(footerEls),
    bodyHeight: bodyEl?.offsetHeight ?? 0,
    leftWidth,
    rightWidth,
    leftTopBlock,
    rightTopBlock,
    breakCandidates: bodyEl ? collectBreakCandidates(bodyEl) : [],
    tableSafeBreakCandidates: bodyEl
      ? collectTableSafeBreakCandidates(bodyEl)
      : [],
    mandatoryBreaks: bodyEl ? collectMandatoryBreaks(bodyEl) : [],
    tableSpans: bodyEl ? collectBorderedTableSpans(bodyEl) : [],
    ...(bodyByGeometry ? { bodyByGeometry } : {}),
  };
}

/**
 * First visible (non-transparent) cell border colour, or null if the cell has
 * no drawn border. Used to decide whether a table is "bordered" and what colour
 * to reuse for the page-break closing line.
 */
function visibleBorderColor(style: CSSStyleDeclaration): string | null {
  for (const side of ["top", "right", "bottom", "left"] as const) {
    const width =
      parseFloat(style.getPropertyValue(`border-${side}-width`)) || 0;
    const lineStyle = style.getPropertyValue(`border-${side}-style`);
    const color = style.getPropertyValue(`border-${side}-color`);
    if (
      width > 0 &&
      lineStyle !== "none" &&
      lineStyle !== "hidden" &&
      color &&
      !/transparent|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(color)
    ) {
      return color;
    }
  }
  return null;
}

/**
 * Collect geometry of every *bordered* data table in the body. Borderless
 * layout tables (letterhead scaffolding etc.) are excluded — only tables whose
 * cells draw a visible border get a closing line when sliced across a page
 * boundary (MOR-623).
 */
export function collectBorderedTableSpans(bodyEl: HTMLElement): TableSpan[] {
  const tables = Array.from(bodyEl.querySelectorAll<HTMLTableElement>("table"));
  if (tables.length === 0) return [];
  const win = bodyEl.ownerDocument.defaultView;
  if (!win) return [];

  const bodyRect = bodyEl.getBoundingClientRect();
  const scale = getMeasurementScale(bodyEl);
  const spans: TableSpan[] = [];
  for (const table of tables) {
    const cell = table.querySelector<HTMLElement>("td, th");
    if (!cell) continue;
    const color =
      visibleBorderColor(win.getComputedStyle(cell)) ??
      visibleBorderColor(win.getComputedStyle(table));
    if (!color) continue;

    const r = table.getBoundingClientRect();
    const top = Math.round((r.top - bodyRect.top) / scale);
    const bottom = Math.round((r.bottom - bodyRect.top) / scale);
    const left = Math.round((r.left - bodyRect.left) / scale);
    const right = Math.round((r.right - bodyRect.left) / scale);
    if (bottom > top && right > left)
      spans.push({ top, bottom, left, right, color });
  }
  return spans;
}

/**
 * Print CSS shared between single and bulk PDF exports.
 *
 * Targets the absolute-positioned page layout produced by
 * `buildPaginatedDocumentHtml`. Each `.print-page` is its own positioning
 * context, with header/body/footer anchored via `top` / `bottom` rather than
 * via flexbox. This avoids Chrome print-engine bugs with `transform`,
 * `overflow: hidden`, and `margin-top: auto` interacting unreliably.
 *
 * Also defines the `.letter-body` typography rules used inside the body
 * wrapper (preserved from the previous implementation).
 */
/**
 * Body typography CSS for the `.letter-body` wrapper, shared between the
 * PDF print window (`buildPrintCss`) and the on-screen paginated preview
 * (`PaginatedLetterPreview`). Emitting the SAME rules in both places is
 * what keeps preview pagination identical to the printed output (MOR-521):
 * the same per-element heights yield the same page-break offsets.
 *
 * Every rule uses the 4-value margin shorthand with an explicit
 * `margin-top: 0` so it produces identical geometry whether or not
 * Tailwind's preflight ran — the print window has no Tailwind, the live
 * preview does. Without the explicit reset the print window would inherit
 * the user-agent `margin-top: 1em` on `<p>/<ul>/<ol>/<li>/<h*>` and drift
 * from the preview (see MOR-382 and the v5-side-headers follow-up).
 *
 * `scope` is prepended to every `.letter-body` selector so the preview can
 * inject these rules scoped to its own subtree (e.g. `[data-morgan-preview] `)
 * without leaking onto the rest of the app. The export path passes `""` for
 * bare, print-window-global rules.
 *
 * `@counter-style` at-rules are global by spec (unscopable) and already
 * declared in `src/index.css`, so they are emitted only on the unscoped
 * (export) path; the scoped preview path relies on those global
 * declarations.
 */
export function buildLetterBodyCss(opts: {
  defaultLineHeight?: string | null;
  defaultParagraphSpacingBefore?: string | null;
  defaultParagraphSpacingAfter?: string | null;
  scope?: string;
}): string {
  const lineHeight = resolveLineHeight(opts.defaultLineHeight);
  const s = opts.scope ?? "";
  const paragraphDefaultsCss = buildScopedParagraphSpacingDefaultsCss(
    `${s}.letter-body `,
    resolveParagraphSpacing(opts.defaultParagraphSpacingBefore),
    resolveParagraphSpacing(opts.defaultParagraphSpacingAfter),
  );
  // @counter-style rules are unscopable and global; emit them only on the
  // export (unscoped) path. The preview inherits them from src/index.css.
  const counterStyles =
    s === ""
      ? `
  /* Custom counter styles for "a) b) c)" / "i) ii) iii)" markers. Mirrored
   * from .prose-letter in src/index.css so PDF print output matches the
   * editor and preview. Browser-rendered PDF only — Word altChunk import
   * does not understand @counter-style and falls back to its native
   * numbering. */
  @counter-style lower-alpha-paren { system: extends lower-alpha; suffix: ") "; }
  @counter-style upper-alpha-paren { system: extends upper-alpha; suffix: ") "; }
  @counter-style lower-roman-paren { system: extends lower-roman; suffix: ") "; }
  @counter-style upper-roman-paren { system: extends upper-roman; suffix: ") "; }`
      : "";
  return `
  ${s}.letter-body { overflow: visible !important; }
  ${s}.letter-body > :first-child { margin-top: 0 !important; padding-top: 0 !important; }
  ${s}.letter-body p { margin: 0 0 0.75em 0; }
  ${s}.letter-body td p, ${s}.letter-body th p { margin: 0; }
  /* Blank lines (empty <p></p> from TipTap getHTML()) collapse to 0 height
   * without content. Inject a non-breaking space so author-added blank lines
   * survive the PDF. Mirrored from .prose-letter in src/index.css. */
  ${s}.letter-body p:empty::before { content: "\\00a0"; }
  /* Headings: pin a bold weight and inherit the document font so the print
   * window matches the editor. Without font-weight the print engine falls back
   * to the browser-default bold, rendering headers heavier than the editor's
   * weight-400 preview ("too bold", MOR-632); font-family:inherit makes headers
   * follow the document font (e.g. Open Sans) instead of a serif fallback,
   * mirroring .prose-letter in src/index.css. */
  ${s}.letter-body h1, ${s}.letter-body h2, ${s}.letter-body h3, ${s}.letter-body h4 { margin: 1em 0 0.5em 0; font-family: inherit; font-weight: 700; }
  ${s}.letter-body blockquote { margin: 0 0 0.75em 0; }
  /* Lists must always render at body line-height in the PDF. Without this
   * the browser print engine inherits a tight inline line-height (e.g.
   * 1.0) from a parent paragraph, which squashes list items together and
   * misaligns the markers vs. their wrapped text (MOR-382). */
  ${s}.letter-body ul { list-style-type: disc; margin: 0 0 0.75em 0; padding-left: 1.5em; line-height: ${lineHeight}; }
  ${s}.letter-body ol { list-style-type: decimal; margin: 0 0 0.75em 0; padding-left: 1.5em; line-height: ${lineHeight}; }
  ${s}.letter-body li { margin: 0 0 0.5em 0; line-height: ${lineHeight}; }
  /* Items that wrap their content in <p> (or any block element) get spacing
   * from the <li> itself; without margin 0 here the inner block's margin
   * double-stacks and its user-agent margin-top strands the marker on its
   * own line (MOR-388).
   *
   * MOR-565: the marker sits on the <li>'s first line box, but the text is
   * laid out in the inner <p>'s box. Any margin-TOP on that <p> pushes the
   * text down while the marker stays put, so the bullet/number floats ABOVE
   * the text. A plain "margin 0" is NOT enough: the paragraph-spacing-before
   * default rule (.letter-body p:not([style*=margin-top]), specificity 0,2,1)
   * and per-paragraph INLINE margin-top both outrank .letter-body li > p
   * (0,1,2) and re-add the top margin. !important forces the reset to win in
   * every case — list items always take spacing from the <li>, never from the
   * inner paragraph. line-height is pinned to the body value so wrapped lines
   * never inherit a tight value (MOR-382).
   *
   * The bottom margin is only forced to 0 on the item's LAST block, because
   * that is the one whose spacing the <li> already owns. A block followed by
   * a sibling inside the same <li> — typically a nested bullet list under a
   * numbered line, or a second paragraph — keeps its own bottom margin, so
   * the gap the author sees in the editor (.prose-letter p, or an explicit
   * "space after" they set) survives into the preview and the PDF. Marker
   * alignment is unaffected: only margin-TOP can strand a marker. */
  ${s}.letter-body li > p, ${s}.letter-body li > div { margin-top: 0 !important; margin-right: 0 !important; margin-left: 0 !important; line-height: ${lineHeight}; }
  ${s}.letter-body li > p:last-child, ${s}.letter-body li > div:last-child { margin-bottom: 0 !important; }${counterStyles}
  /* Hyperlinks: mirror .prose-letter a (src/index.css) so links render the
   * same in the editor, preview, and exported PDF. */
  ${s}.letter-body a { color: #1d4ed8; text-decoration: underline; }
  ${s}.letter-body table { border-collapse: collapse; table-layout: auto; }
  ${s}.letter-body td, ${s}.letter-body th { vertical-align: top; }
  /* Unresolved field chips must be inline-BLOCK inside a paginated body.
   * The chips carry vertical padding and a border, which an inline box
   * paints OUTSIDE its line box — at a tight line-height there is no
   * half-leading to absorb them, so the chip's top edge ends up above the
   * line it belongs to. Since each page is a fixed-height clip over one
   * continuous body, a page boundary on that line (a manual page break
   * included) then separates the chip from its own text: a sliver of
   * border is stranded at the bottom of one page and the chip opens the
   * next with its top shaved off. inline-block folds the padding and
   * border into the line box so the whole chip travels with its line.
   * Scoped to .letter-body — the live editor (.prose-letter) keeps inline
   * chips so toolbar marks apply across a text selection. */
  ${s}.letter-body .field-placeholder-chip, ${s}.letter-body .field-placeholder { display: inline-block; text-indent: 0; }
  ${paragraphDefaultsCss}
`;
}

function buildPrintCss(
  defaultFontFamily?: string | null,
  defaultLineHeight?: string | null,
  bodyTextColor?: string | null,
  defaultFontSize?: string | null,
  defaultParagraphSpacingBefore?: string | null,
  defaultParagraphSpacingAfter?: string | null,
): string {
  const fontFamily = resolveFontFamily(defaultFontFamily);
  const lineHeight = resolveLineHeight(defaultLineHeight);
  const color = resolveBodyTextColor(bodyTextColor);
  const fontSize = resolveFontSize(defaultFontSize);
  const letterBodyCss = buildLetterBodyCss({
    defaultLineHeight,
    defaultParagraphSpacingBefore,
    defaultParagraphSpacingAfter,
    scope: "",
  });
  return `
  @page { size: A4; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body { font-family: ${fontFamily}; font-size: ${fontSize}; line-height: ${lineHeight}; color: ${color}; }
  .print-page {
    position: relative;
    width: ${A4_PAGE_WIDTH_PX}px;
    height: ${A4_PAGE_HEIGHT_PX}px;
    box-sizing: border-box;
    page-break-after: always;
    break-after: page;
    overflow: hidden;
  }
  .print-page:last-child { page-break-after: avoid; break-after: avoid; }
  .print-header, .print-footer, .print-body-clip { box-sizing: border-box; }
  ${letterBodyCss}
  /* Manual page-break markers (div[data-page-break]) are empty block
   * elements with no styling in the print window, so they render as
   * 0-height invisible divs in flow — exactly what we want.
   *
   * Important: do NOT set "display: none" here. The measurement DOM uses
   * the same CSS as the final render, and display:none removes the marker
   * from the layout tree, which makes getBoundingClientRect() return all
   * zeros. That breaks collectMandatoryBreaks, which relies on the
   * marker's true vertical position to compute the page boundary offset
   * (MOR-271 PDF page-break regression). */
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;
}

/**
 * Variant of `buildParagraphSpacingDefaultsCss` that prepends a parent
 * selector (e.g. `".letter-body "`) so the rules can be embedded in a
 * print/export `<style>` block alongside other letter-body rules without
 * leaking onto unrelated nodes.
 */
function buildScopedParagraphSpacingDefaultsCss(
  scope: string,
  spacingBefore: string,
  spacingAfter: string,
): string {
  const blockSelectors = ["p", "h1", "h2", "h3", "h4", "h5", "h6"];
  const rules: string[] = [];
  if (spacingBefore) {
    const selectors = blockSelectors
      .map((tag) => `${scope}${tag}:not([style*="margin-top"])`)
      .join(", ");
    rules.push(`${selectors} { margin-top: ${spacingBefore}; }`);
  }
  if (spacingAfter) {
    const selectors = blockSelectors
      .map((tag) => `${scope}${tag}:not([style*="margin-bottom"])`)
      .join(", ");
    rules.push(`${selectors} { margin-bottom: ${spacingAfter}; }`);
  }
  return rules.join("\n  ");
}

/** Open a new browser window pre-sized for an A4 print preview. */
function openPrintWindow(): Window | null {
  return window.open("about:blank", "_blank", "width=800,height=1100");
}

/**
 * Trigger the print dialog for a window after a short delay so the browser
 * finishes laying out the freshly written DOM. Without the delay Chrome can
 * occasionally print a blank page when innerHTML was just replaced.
 */
function triggerPrint(printWindow: Window): void {
  setTimeout(() => {
    try {
      printWindow.focus();
      printWindow.print();
    } catch {
      // Window may have been closed by the user; nothing to do.
    }
  }, 50);
}

/**
 * Legacy single-page PDF export for raw HTML strings.
 *
 * Used when the caller doesn't have structured `LetterParts` (e.g. PDF-form
 * documents that fall through to a Convex-rendered HTML preview). Skips
 * pagination entirely — the browser flows the content using its default
 * print algorithm, which is fine for short single-page outputs.
 */
function printRawHtml(
  html: string,
  title: string,
  fontFamily?: string | null,
  lineHeight?: string | null,
  bodyTextColor?: string | null,
  defaultFontSize?: string | null,
  defaultParagraphSpacingBefore?: string | null,
  defaultParagraphSpacingAfter?: string | null,
): void {
  const printWindow = openPrintWindow();
  if (!printWindow) return;

  const paragraphDefaultsCss = buildScopedParagraphSpacingDefaultsCss(
    ".letter-body ",
    resolveParagraphSpacing(defaultParagraphSpacingBefore),
    resolveParagraphSpacing(defaultParagraphSpacingAfter),
  );
  const css = `
    @page { size: A4; margin: 0; }
    html, body { margin: 0; padding: 0; font-family: ${resolveFontFamily(fontFamily)}; font-size: ${resolveFontSize(defaultFontSize)}; line-height: ${resolveLineHeight(lineHeight)}; color: ${resolveBodyTextColor(bodyTextColor)}; }
    body > div { height: auto !important; min-height: ${A4_PAGE_HEIGHT_PX}px; }
    .letter-body { overflow: visible !important; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    ${paragraphDefaultsCss}
  `;

  printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${title}</title>
<style>${css}</style>
</head><body>${html}</body></html>`);
  printWindow.document.close();

  waitForPrintWindowReady(printWindow)
    .catch(() => undefined)
    .finally(() => triggerPrint(printWindow));
}

/**
 * Geometry drift (px) between the measurement pass and a re-measure after the
 * font set settles that forces a re-paginate. Anything larger than sub-pixel
 * rounding means line wraps moved and the page cuts would land mid-line.
 */
const PAGINATION_DRIFT_TOLERANCE_PX = 2;

/**
 * How long the post-font re-measure waits for the images in its fresh
 * measurement tree. They were fetched moments earlier for the first pass,
 * so this normally resolves at once from the browser cache; the cap only
 * matters when that cache has let them go and the network is slow.
 */
const REMEASURE_IMAGE_TIMEOUT_MS = 10_000;

/**
 * Measure every document's layout from the injected measurement root.
 * Containers are matched by `data-measure-index` when present; a single
 * unindexed root (the single-document export path) matches any container.
 * Returns null when the root or a container is missing.
 */
function measureAllFromRoot(
  printWindow: Window,
  docs: LetterParts[],
): PaginationMeasured[] | null {
  const root = printWindow.document.getElementById(MEASUREMENT_ROOT_ID);
  if (!root) return null;
  const out: PaginationMeasured[] = [];
  for (let index = 0; index < docs.length; index++) {
    const container =
      root.querySelector<HTMLElement>(
        `[data-measure-root][data-measure-index="${index}"]`,
      ) ??
      (docs.length === 1
        ? root.querySelector<HTMLElement>("[data-measure-root]")
        : null);
    if (!container) return null;
    out.push(measurePrintLayout(printWindow, container, docs[index]));
  }
  return out;
}

/** Replace the window body with the paginated pages for every document. */
function renderMeasured(
  printWindow: Window,
  docs: LetterParts[],
  measurements: PaginationMeasured[],
): void {
  printWindow.document.body.innerHTML = docs
    .map((parts, index) =>
      buildPaginatedDocumentHtml(parts, measurements[index]),
    )
    .join("");
}

/** Best-effort wait for the window's font set to finish loading. */
async function settleFonts(printWindow: Window): Promise<void> {
  try {
    const fonts = (printWindow.document as Document & { fonts?: FontFaceSet })
      .fonts;
    if (fonts?.ready) await fonts.ready;
  } catch {
    // Font settling is best-effort.
  }
}

/**
 * Guard against the measure/render font race that slices lines across page
 * boundaries (Shaw Gidley: half a text line at the bottom of one page, the
 * other half at the top of the next).
 *
 * `waitForPrintWindowReady` caps its stylesheet wait at 3s, so on a slow font
 * CDN the pagination is measured with FALLBACK font metrics. When the real
 * webfonts land moments later the rendered pages re-wrap — every break offset
 * shifts, and the fixed pixel cuts now land mid-line. After the font set
 * settles, re-measure against the settled layout and re-render the pages
 * whenever the geometry drifted.
 *
 * The fresh measurement tree holds brand-new `<img>` elements for the
 * letterhead and signature. The first pass measured only after
 * `waitForImages`; this pass must do the same, because a browser that no
 * longer has those images in its memory cache (a bulk download rasterising
 * other letters at 3x A4 is enough to evict them) loads them asynchronously,
 * and an image measured before it arrives is 0px tall. That reading used to
 * pass the drift check as a font reflow and re-paginate the letter with no
 * header or footer band and every break below the signature off by its
 * height (MOR-2756: one of three identical PPSR letters in a bulk download
 * came out with no letterhead and a clipped schedule page).
 */
async function verifyPaginationAfterFonts(
  printWindow: Window,
  docs: LetterParts[],
  used: PaginationMeasured[],
  imageTimeoutMs = REMEASURE_IMAGE_TIMEOUT_MS,
): Promise<void> {
  await settleFonts(printWindow);
  const doc = printWindow.document;

  // The first render replaced the measurement root, so re-inject one (its
  // content is absolutely positioned off-screen and removed right after
  // measuring, so it never shows up in the printed/rasterised output).
  const wrapper = doc.createElement("div");
  wrapper.id = MEASUREMENT_ROOT_ID;
  wrapper.innerHTML = docs
    .map((parts, index) => buildPrintMeasurementHtml(parts, index))
    .join("");
  doc.body.appendChild(wrapper);
  const imagesSettled = await waitForImagesWithin(wrapper, imageTimeoutMs);
  const fresh = imagesSettled ? measureAllFromRoot(printWindow, docs) : null;
  wrapper.remove();
  // An image that timed out or failed leaves its slot short — a padded
  // header slot still reads above zero, and a missing signature only
  // shortens the body — so no reading from that tree can be told apart
  // from a font reflow. The first layout, measured with every image in
  // place, stands.
  if (!fresh) return;

  const drifted = fresh.some((m, i) => {
    const prev = used[i];
    return (
      Math.abs(m.bodyHeight - prev.bodyHeight) >
        PAGINATION_DRIFT_TOLERANCE_PX ||
      Math.abs(m.headerHeight - prev.headerHeight) >
        PAGINATION_DRIFT_TOLERANCE_PX ||
      Math.abs(m.footerHeight - prev.footerHeight) >
        PAGINATION_DRIFT_TOLERANCE_PX
    );
  });
  if (drifted) renderMeasured(printWindow, docs, fresh);
}

/**
 * Replace the print window's body with the paginated final layout for a
 * single LetterParts input. Assumes the window already contains the
 * measurement root (from `buildPrintMeasurementHtml`) and that
 * `waitForPrintWindowReady` has resolved. Returns the measurements used so
 * the caller can run `verifyPaginationAfterFonts` before printing/rasterising.
 */
function paginateAndRender(
  printWindow: Window,
  parts: LetterParts,
): PaginationMeasured[] | null {
  const measured = measureAllFromRoot(printWindow, [parts]);
  if (!measured) {
    // Caller never injected a measurement root — print whatever's there.
    return null;
  }
  renderMeasured(printWindow, [parts], measured);
  return measured;
}

export function downloadAsPdf(
  input: string | LetterParts,
  title: string,
): void {
  if (typeof input === "string") {
    printRawHtml(input, title);
    return;
  }

  const parts = input;
  const printWindow = openPrintWindow();
  if (!printWindow) return;

  const measurementHtml = `<div id="${MEASUREMENT_ROOT_ID}">${buildPrintMeasurementHtml(parts)}</div>`;

  printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${title}</title>
${EXPORT_FONT_LINKS}
<style>${buildPrintCss(parts.defaultFontFamily, parts.defaultLineHeight, parts.bodyTextColor, parts.defaultFontSize, parts.defaultParagraphSpacingBefore, parts.defaultParagraphSpacingAfter)}</style>
</head><body>${measurementHtml}</body></html>`);
  printWindow.document.close();

  waitForPrintWindowReady(printWindow)
    .catch(() => undefined)
    .finally(() => {
      const used = paginateAndRender(printWindow, parts);
      if (!used) {
        triggerPrint(printWindow);
        return;
      }
      // Re-verify after the fonts settle so a late webfont can't reflow the
      // pages and slice lines across page boundaries.
      verifyPaginationAfterFonts(printWindow, [parts], used)
        .catch(() => undefined)
        .finally(() => triggerPrint(printWindow));
    });
}

/** Lower-case alphabetic marker: 1 -> a, 26 -> z, 27 -> aa (spreadsheet style). */
function toAlphaMarker(n: number): string {
  const build = (num: number, acc: string): string => {
    if (num <= 0) return acc;
    const rem = (num - 1) % 26;
    return build(
      Math.floor((num - 1) / 26),
      String.fromCharCode(97 + rem) + acc,
    );
  };
  return build(n, "") || "a";
}

/** Lower-case roman numeral for 1..3999 (clamped); falls back to the number. */
function toRomanMarker(n: number): string {
  if (n <= 0 || n >= 4000) return String(n);
  const table: Array<[number, string]> = [
    [1000, "m"],
    [900, "cm"],
    [500, "d"],
    [400, "cd"],
    [100, "c"],
    [90, "xc"],
    [50, "l"],
    [40, "xl"],
    [10, "x"],
    [9, "ix"],
    [5, "v"],
    [4, "iv"],
    [1, "i"],
  ];
  return table.reduce(
    (acc, [value, sym]) => {
      const count = Math.floor(acc.remaining / value);
      return {
        text: acc.text + sym.repeat(count),
        remaining: acc.remaining - count * value,
      };
    },
    { text: "", remaining: n },
  ).text;
}

/**
 * Format an ordered-list marker for a 1-based `index` given the CSS
 * `list-style-type`. Mirrors the markers the print CSS renders via the real
 * browser (decimal "1.", lower-alpha "a.", the "a)"/"i)" `@counter-style`
 * paren variants, etc.) so the rasterised output matches the print PDF.
 */
function formatOrderedMarker(index: number, styleType: string): string {
  switch (styleType) {
    case "lower-alpha":
    case "lower-latin":
      return `${toAlphaMarker(index)}.`;
    case "upper-alpha":
    case "upper-latin":
      return `${toAlphaMarker(index).toUpperCase()}.`;
    case "lower-roman":
      return `${toRomanMarker(index)}.`;
    case "upper-roman":
      return `${toRomanMarker(index).toUpperCase()}.`;
    case "lower-alpha-paren":
      return `${toAlphaMarker(index)})`;
    case "upper-alpha-paren":
      return `${toAlphaMarker(index).toUpperCase()})`;
    case "lower-roman-paren":
      return `${toRomanMarker(index)})`;
    case "upper-roman-paren":
      return `${toRomanMarker(index).toUpperCase()})`;
    case "decimal":
    default:
      return `${index}.`;
  }
}

/**
 * Replace native CSS list markers with INLINE text markers inside each list
 * item's first block, for the html2canvas rasterisation path ONLY (MOR-565).
 *
 * Why: with TipTap's `<li><p>…</p></li>` shape and `list-style-position:
 * outside`, html2canvas paints the disc/number in the list-item's OWN line
 * box — which sits ABOVE the inner `<p>` block — so the bullet floats above
 * the text in the bundled (attachment) PDF. The real browser (print-window
 * PDF) and the on-screen preview don't have this bug, so the divergence is
 * purely html2canvas's marker rendering.
 *
 * The fix moves the marker INTO the text's line box: we hide the native
 * marker (`list-style:none`) and prepend an inline-block span carrying the
 * marker glyph to the item's first block child. Pulled left by its own width
 * (`margin-left:-W`) it hangs into the list's left padding, baseline-aligned
 * with the first text line, and wrapped lines still align under the text.
 * html2canvas only ever sees ordinary inline text, which it rasterises
 * faithfully. The print-window and Word exports never call this, so those
 * (working) paths are unchanged.
 */
function inlineListMarkersForRaster(root: HTMLElement): void {
  const doc = root.ownerDocument;
  if (!doc) return;
  const lists = root.querySelectorAll<HTMLElement>("ul, ol");
  for (const list of Array.from(lists)) {
    const isOrdered = list.tagName === "OL";
    const styleType =
      list.getAttribute("data-list-style") ||
      list.style.listStyleType ||
      (isOrdered ? "decimal" : "disc");
    const startAttr = parseInt(list.getAttribute("start") || "", 10);
    const startIndex =
      Number.isFinite(startAttr) && startAttr > 0 ? startAttr : 1;
    list.style.listStyleType = "none";
    const liEls = Array.from(list.children).filter(
      (li): li is HTMLElement => li.tagName === "LI",
    );
    liEls.forEach((liEl, i) => {
      const index = startIndex + i;
      const markerText = isOrdered
        ? formatOrderedMarker(index, styleType)
        : "•";
      // Host the marker inside the item's first block child (the <p> that
      // lays out the text) so it shares the text's line box; fall back to the
      // <li> itself for plain-text items.
      const host =
        liEl.querySelector<HTMLElement>(":scope > p, :scope > div") ?? liEl;
      const span = doc.createElement("span");
      span.setAttribute("data-raster-marker", "1");
      span.textContent = markerText;
      // Inline-block pulled left by its own width: hangs into the gutter,
      // baseline-aligned with the first text line, wrapped lines unaffected.
      span.style.display = "inline-block";
      span.style.width = "1.2em";
      span.style.marginLeft = "-1.2em";
      host.insertBefore(span, host.firstChild);
    });
  }
}

/**
 * Replace every `<img>` with an http(s) src by an inlined base64 data URI,
 * in-place in a live document.
 *
 * html-to-image rasterises through an SVG `<foreignObject>`, which the browser
 * will NOT let load cross-origin resources during serialisation — so a
 * letterhead/side-band image left as a Convex storage URL renders blank in the
 * bundled PDF. Inlining the bytes first (Convex storage is CORS-fetchable, same
 * as `fetchAttachmentBlobs`) guarantees the imagery survives (MOR-623).
 */
async function inlineDomImagesForRaster(doc: Document): Promise<void> {
  const imgs = Array.from(doc.querySelectorAll<HTMLImageElement>("img")).filter(
    (img) => /^https?:/i.test(img.src),
  );
  if (imgs.length === 0) return;
  await Promise.all(
    imgs.map(async (img) => {
      const dataUri = await fetchImageAsDataUri(img.src);
      if (!dataUri) return;
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = dataUri;
      });
    }),
  );
}

/** Cached font-embed CSS shared by every raster export (see `letterPartsToPdfBlob`). */
let exportFontEmbedCssPromise: Promise<string | undefined> | null = null;

/**
 * Render `LetterParts` into a real PDF Blob without popping a print window.
 *
 * Reuses the same off-screen pagination pipeline as `downloadAsPdf`
 * (`buildPrintMeasurementHtml` -> `paginateAndRender` -> `.print-page`
 * divs), then rasterises each `.print-page` via `html-to-image` (browser-native
 * rendering, so 1px borders stay crisp) and stitches the canvases into a
 * multi-page PDF with `jspdf`. Trade-off: the resulting PDF is image-based (no
 * selectable text) but visually matches the print preview, which is what we
 * need to bundle the letter alongside its attachments (MOR-301 / MOR-623).
 *
 * Libraries are loaded via dynamic `import()` so they only ship in the bundle
 * when the user actually downloads a letter PDF with attachments.
 */
export async function letterPartsToPdfBlob(
  parts: LetterParts,
  title: string,
): Promise<Blob> {
  void title;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.left = "-99999px";
  iframe.style.top = "0";
  iframe.style.width = `${A4_PAGE_WIDTH_PX}px`;
  iframe.style.height = `${A4_PAGE_HEIGHT_PX}px`;
  iframe.style.border = "0";
  iframe.style.visibility = "hidden";
  document.body.appendChild(iframe);

  try {
    const printWindow = iframe.contentWindow;
    const printDoc = iframe.contentDocument;
    if (!printWindow || !printDoc) {
      throw new Error("letterPartsToPdfBlob: iframe document unavailable");
    }

    const measurementHtml = `<div id="${MEASUREMENT_ROOT_ID}">${buildPrintMeasurementHtml(parts)}</div>`;
    printDoc.open();
    printDoc.write(`<!DOCTYPE html>
<html><head>
${EXPORT_FONT_LINKS}
<style>${buildPrintCss(parts.defaultFontFamily, parts.defaultLineHeight, parts.bodyTextColor, parts.defaultFontSize, parts.defaultParagraphSpacingBefore, parts.defaultParagraphSpacingAfter)}</style>
</head><body>${measurementHtml}</body></html>`);
    printDoc.close();

    await waitForPrintWindowReady(printWindow).catch(() => undefined);
    const used = paginateAndRender(printWindow, parts);
    // Re-verify after the fonts settle: the rasteriser embeds the real
    // webfonts, so pages measured with fallback metrics would re-wrap and
    // slice lines across page boundaries.
    if (used) {
      await verifyPaginationAfterFonts(printWindow, [parts], used).catch(
        () => undefined,
      );
    }

    // MOR-565: html2canvas paints `list-style-position: outside` markers in
    // the list item's own line box (above the inner <p>), stranding bullets
    // above their text in the bundled PDF. Swap them for inline text markers
    // so html2canvas renders them on the text baseline. Print-window path is
    // unaffected (it never calls this).
    inlineListMarkersForRaster(printDoc.body);

    // Inline letterhead/side-band images as data URIs — html-to-image's
    // foreignObject rasteriser can't fetch cross-origin images (MOR-623).
    await inlineDomImagesForRaster(printDoc);

    const pageEls = Array.from(
      printDoc.querySelectorAll<HTMLElement>(".print-page"),
    );
    if (pageEls.length === 0) {
      throw new Error("letterPartsToPdfBlob: pagination produced no pages");
    }

    const [htmlToImage, jsPdfModule] = await Promise.all([
      import("html-to-image"),
      import("jspdf"),
    ]);
    const JsPDF =
      jsPdfModule.jsPDF ??
      (jsPdfModule as unknown as { default: typeof jsPdfModule.jsPDF }).default;

    const pdf = new JsPDF({
      unit: "px",
      format: [A4_PAGE_WIDTH_PX, A4_PAGE_HEIGHT_PX],
      orientation: "portrait",
      hotfixes: ["px_scaling"],
    });

    // Embed the branding webfonts once and reuse across every page AND
    // every document (module-level cache) — the export iframes all load the
    // same fixed EXPORT_FONT_LINKS set, and rebuilding the embedded font CSS
    // (fetch + base64 of every font file) per document was a large fixed
    // cost of bulk downloads (MOR-971). A failed build isn't cached so the
    // next document can retry.
    if (!exportFontEmbedCssPromise) {
      exportFontEmbedCssPromise = htmlToImage
        .getFontEmbedCSS(pageEls[0])
        .catch(() => undefined);
    }
    const fontEmbedCSS = await exportFontEmbedCssPromise;
    // An empty result means the font CSS hadn't loaded in this iframe yet;
    // caching it would render every later document with fallback fonts for
    // the rest of the session, so let the next document rebuild it.
    if (!fontEmbedCSS) exportFontEmbedCssPromise = null;

    for (let i = 0; i < pageEls.length; i++) {
      const pageEl = pageEls[i];
      // MOR-623: rasterise via html-to-image, which uses the browser's NATIVE
      // renderer (SVG foreignObject). 1px table borders stay a crisp ~1px
      // instead of being fattened to ~1.5-3px the way html2canvas (a JS
      // reimplementation of the renderer) does. pixelRatio 3 keeps text and
      // borders sharp for print; original template border colours are kept.
      const canvas = await htmlToImage.toCanvas(pageEl, {
        pixelRatio: 3,
        backgroundColor: "#ffffff",
        width: A4_PAGE_WIDTH_PX,
        height: A4_PAGE_HEIGHT_PX,
        fontEmbedCSS,
      });
      // Encode as high-quality JPEG rather than PNG: PNG-encoding a 3×-A4
      // canvas (~24MP) takes seconds per page and produced multi-megabyte
      // pages, which is the bulk of why day-one downloads with annexures
      // were so slow (MOR-971). At pixelRatio 3 the JPEG artefacts are not
      // visible in the A4-sized output, the background is already opaque
      // white (no transparency to lose), and pages come out ~10× smaller.
      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      if (i > 0) pdf.addPage([A4_PAGE_WIDTH_PX, A4_PAGE_HEIGHT_PX], "portrait");
      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        A4_PAGE_WIDTH_PX,
        A4_PAGE_HEIGHT_PX,
        undefined,
        "FAST",
      );
    }

    return pdf.output("blob");
  } finally {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}

// ---------------------------------------------------------------------------
// Bulk exports
// ---------------------------------------------------------------------------

export async function downloadBulkAsWord(
  pages: LetterParts[],
  filename: string,
): Promise<void> {
  const safeName = filename.replace(/\.doc$/i, "").replace(/\.docx$/i, "");
  const blob = await buildBulkWordBlob(pages, safeName);
  downloadBlob(blob, `${safeName}.docx`);
}

/**
 * Build the combined bulk-letters `.docx` as a blob (every selected letter
 * concatenated with page breaks, sharing the first document's letterhead).
 *
 * Extracted from `downloadBulkAsWord` so callers that need to bundle the
 * combined document alongside attachments (the Day-One bulk download) can zip
 * the blob rather than trigger an immediate download.
 */
export async function buildBulkWordBlob(
  pages: LetterParts[],
  filename = "document",
): Promise<Blob> {
  const safeName = filename.replace(/\.doc$/i, "").replace(/\.docx$/i, "");
  const first = pages[0];

  // Bulk Word export: use the FIRST document's per-zone resolved variants
  // (or its legacy flat fields) as the shared letterhead for the whole
  // bundle. Word can't mix variants across pages in OOXML. Resolve each
  // zone independently for page 1 — the new resolver emits one fragment
  // per zone, so picking firstVariants[0] would only carry the header URL.
  const firstVariants = first ? getVariantsForParts(first) : [];
  // Page 1 vs pages 2+ can resolve to different layers (first-page +
  // subsequent-page letterhead pair, MOR-953) — resolve both so the Word
  // bundle carries the continuation header/footer too.
  const headerFirstSource = pickVariantForPage(firstVariants, "header", 1);
  const headerRestSource = pickVariantForPage(firstVariants, "header", 2);
  const footerFirstSource = pickVariantForPage(firstVariants, "footer", 1);
  const footerRestSource = pickVariantForPage(firstVariants, "footer", 2);
  const headerSource = headerFirstSource ?? headerRestSource;
  const footerSource = footerFirstSource ?? footerRestSource;
  const headerUrl =
    headerSource?.headerImageUrl ?? first?.headerImageUrl ?? null;
  const footerUrl =
    footerSource?.footerImageUrl ?? first?.footerImageUrl ?? null;
  const headerNotFirstUrl =
    headerFirstSource &&
    headerRestSource &&
    headerRestSource !== headerFirstSource
      ? (headerRestSource.headerImageUrl ?? null)
      : null;
  const footerNotFirstUrl =
    footerFirstSource &&
    footerRestSource &&
    footerRestSource !== footerFirstSource
      ? (footerRestSource.footerImageUrl ?? null)
      : null;

  const [headerNotFirstFetch, footerNotFirstFetch] = await Promise.all([
    headerNotFirstUrl
      ? fetchImageBuffer(headerNotFirstUrl)
      : Promise.resolve({ image: null }),
    footerNotFirstUrl
      ? fetchImageBuffer(footerNotFirstUrl)
      : Promise.resolve({ image: null }),
  ]);
  const headerImgNotFirst = headerNotFirstFetch.image;
  const footerImgNotFirst = footerNotFirstFetch.image;

  const [headerFetch, footerFetch] = await Promise.all([
    headerUrl
      ? fetchImageBuffer(headerUrl)
      : Promise.resolve({
          image: null,
          debug: {
            requestedUrl: undefined,
            fetched: false,
            error: "No header image URL provided",
          } satisfies WordExportAssetDebugInfo,
        }),
    footerUrl
      ? fetchImageBuffer(footerUrl)
      : Promise.resolve({
          image: null,
          debug: {
            requestedUrl: undefined,
            fetched: false,
            error: "No footer image URL provided",
          } satisfies WordExportAssetDebugInfo,
        }),
  ]);
  const headerImg = headerFetch.image;
  const footerImg = footerFetch.image;

  const preparedBodies = await Promise.all(
    pages.map((page) =>
      prepareBodyForDocx(page.bodyHtml, page.defaultFontSize),
    ),
  );
  const combinedBody = preparedBodies.join(
    '<div style="page-break-after:always"></div>',
  );

  // Page fit applied to the zone paddings, as in `buildWordBlob`.
  const padding = applyPageFitToPadding(
    first
      ? resolvePagePadding(pagePaddingInputs(first), 1)
      : resolveDocumentPadding(
          undefined,
          undefined,
          undefined,
          variantPaddingSources(firstVariants),
        ),
    zonePageFit(headerFirstSource, footerFirstSource),
  );
  const paddingRest = first
    ? applyPageFitToPadding(
        resolvePagePadding(pagePaddingInputs(first), 2),
        zonePageFit(headerRestSource, footerRestSource),
      )
    : padding;
  const isFit = (offset: number | null | undefined) =>
    offset !== null && offset !== undefined;
  const blob = await buildDocxBlob(combinedBody, headerImg, footerImg, {
    headerPageRule: headerImgNotFirst
      ? "firstOnly"
      : (headerSource?.headerPageRule ?? first?.headerPageRule ?? "all"),
    footerPageRule: footerImgNotFirst
      ? "firstOnly"
      : (footerSource?.footerPageRule ?? first?.footerPageRule ?? "all"),
    headerImgNotFirst,
    footerImgNotFirst,
    pageFitImages: {
      header: isFit(headerSource?.headerPageFitTop),
      headerNotFirst: isFit(headerRestSource?.headerPageFitTop),
      footer: isFit(footerSource?.footerPageFitBottom),
      footerNotFirst: isFit(footerRestSource?.footerPageFitBottom),
    },
    padding,
    paddingRest,
    defaultFontFamily: first?.defaultFontFamily,
    defaultLineHeight: first?.defaultLineHeight,
    bodyTextColor: first?.bodyTextColor,
    defaultFontSize: first?.defaultFontSize,
    defaultParagraphSpacingBefore: first?.defaultParagraphSpacingBefore,
    defaultParagraphSpacingAfter: first?.defaultParagraphSpacingAfter,
    pageNumbering: first?.pageNumbering,
    debugInfo: {
      filename: safeName,
      paddingSources: {
        branding: first?.brandingPadding,
        template: first?.templatePadding,
        document: first?.documentPadding,
      },
      headerAsset: headerFetch.debug,
      footerAsset: footerFetch.debug,
    },
  });
  return blob;
}

/**
 * Bulk PDF export: paginate each `LetterParts` independently using the same
 * measurement-then-render flow as `downloadAsPdf`, then concatenate the
 * resulting `.print-page` divs into a single print job.
 */
export function downloadBulkAsPdf(pages: LetterParts[], title: string) {
  if (pages.length === 0) return;

  const printWindow = openPrintWindow();
  if (!printWindow) return;

  // Inject one measurement root per document, each tagged with its index so
  // we can pair measurements back to their `LetterParts` after layout.
  const measurementHtml = pages
    .map((parts, index) => buildPrintMeasurementHtml(parts, index))
    .join("");

  printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${title}</title>
<style>${buildPrintCss(pages[0]?.defaultFontFamily, pages[0]?.defaultLineHeight, pages[0]?.bodyTextColor, pages[0]?.defaultFontSize, pages[0]?.defaultParagraphSpacingBefore, pages[0]?.defaultParagraphSpacingAfter)}</style>
</head><body><div id="${MEASUREMENT_ROOT_ID}">${measurementHtml}</div></body></html>`);
  printWindow.document.close();

  waitForPrintWindowReady(printWindow)
    .catch(() => undefined)
    .finally(() => {
      const used = measureAllFromRoot(printWindow, pages);
      if (!used) {
        triggerPrint(printWindow);
        return;
      }
      renderMeasured(printWindow, pages, used);
      // Re-verify after the fonts settle so a late webfont can't reflow the
      // pages and slice lines across page boundaries.
      verifyPaginationAfterFonts(printWindow, pages, used)
        .catch(() => undefined)
        .finally(() => triggerPrint(printWindow));
    });
}
